import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGameContext } from '../../context/GameContext';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import VictoryOverlay from '../../components/VictoryOverlay/VictoryOverlay';
import { type GameNotification } from '../../components/GameNotification';
import AIEngineIndicator from '../../components/AIEngineIndicator';
import PlayerHand from '../../components/PlayerHand';
import PlayingCard from '../../components/PlayingCard';
import GameInterfaceEnhancement from '../../components/GameInterfaceEnhancement/GameInterfaceEnhancement';
import ErrorBoundary from '../../components/ErrorBoundary';
// import TurnIndicator from '../../components/TurnIndicator/TurnIndicator';
import { CleanPlayerCard } from '../../components/CleanPlayerCard/CleanPlayerCard';
import type { SuitType, Card, RankType } from '../../types/game';
import { dbg } from '../../utils/debugLogger';
import { roomScoreApi, type RoomScoreData } from '../../services/roomScoreApi';
import { getAIPlayerDisplayName } from '../../utils/aiPlayerDisplay';
import { getPartnerCount } from 'card-game-shared';
import '../Rooms/CreateRoom.css';

// Interface for partner cards with play order preference
interface SelectedPartnerCard {
  suit: SuitType;
  rank: RankType;
  playOrder: 1 | 2; // 1st or 2nd to play becomes partner
  id: string;
}

// Convert shared types to display format
const convertSuitToDisplay = (suit: SuitType): string => {
  const suitMap = {
    spades: '♠',
    hearts: '♥',
    diamonds: '♦',
    clubs: '♣'
  };
  return suitMap[suit] || suit;
};

const getSuitColor = (suit: SuitType): string => {
  return suit === 'hearts' || suit === 'diamonds' ? '#dc2626' : '#1f2937';
};

// Helper function to calculate actual play order for partner cards
const calculateActualPlayOrder = (partnerCard: Card, gameState: any): { timesPlayed: number; currentPlayOrder: number } => {
  if (!gameState?.currentRound?.playPhase) {
    return { timesPlayed: 0, currentPlayOrder: 1 };
  }

  const playPhase = gameState.currentRound.playPhase;
  let timesPlayedBefore = 0;

  // Count from completed tricks
  if (playPhase.completedTricks && playPhase.completedTricks.length > 0) {
    playPhase.completedTricks.forEach((trick: any) => {
      if (trick.cards && trick.cards.length > 0) {
        trick.cards.forEach((trickCard: any) => {
          if (trickCard.card && 
              trickCard.card.suit === partnerCard.suit && 
              trickCard.card.rank === partnerCard.rank) {
            timesPlayedBefore++;
          }
        });
      }
    });
  }

  // Count from current trick
  if (playPhase.currentTrick && playPhase.currentTrick.cards && playPhase.currentTrick.cards.length > 0) {
    playPhase.currentTrick.cards.forEach((trickCard: any) => {
      if (trickCard.card && 
          trickCard.card.suit === partnerCard.suit && 
          trickCard.card.rank === partnerCard.rank) {
        timesPlayedBefore++;
      }
    });
  }

  return {
    timesPlayed: timesPlayedBefore,
    currentPlayOrder: timesPlayedBefore + 1  // Next play would be this order
  };
};

interface MultiplayerGameProps {
  username?: string;
  roomId?: string;
  skipModal?: boolean;
  onReturnToLobby?: () => void;
  onNavigateToLobby?: () => void;
}

interface JoinRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJoinRoom: (roomId: string, username: string) => void;
  onCreateRoom: (username: string) => void;
}

const JoinRoomModal: React.FC<JoinRoomModalProps> = ({ isOpen, onClose, onJoinRoom, onCreateRoom }) => {
  const [roomId, setRoomId] = useState('');
  const [username, setUsername] = useState('Player1');

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        maxWidth: '400px',
        width: '90%'
      }}>
        <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Join Kadi ni Tidi Game</h2>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Your Name:
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your name"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Room ID (optional):
          </label>
          <input
            type="text"
            value={roomId}
            onChange={(e) => setRoomId(e.target.value.toUpperCase())}
            placeholder="Leave empty to create new room"
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              fontSize: '1rem',
              textTransform: 'uppercase'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
          {/* Primary action buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => {
                if (!username.trim()) return;
                onCreateRoom(username);
              }}
              disabled={!username.trim()}
              style={{
                flex: 1,
                background: !username.trim() ? '#9ca3af' : '#a78bfa',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                cursor: !username.trim() ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              Create New Room
            </button>
            <button
              onClick={() => {
                if (!username.trim() || !roomId.trim()) return;
                onJoinRoom(roomId.trim(), username);
              }}
              disabled={!username.trim() || !roomId.trim()}
              style={{
                flex: 1,
                background: (!username.trim() || !roomId.trim()) ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                cursor: (!username.trim() || !roomId.trim()) ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              Join Existing Room
            </button>
          </div>
          
          {/* Cancel button */}
          <button
            onClick={onClose}
            style={{
              background: '#6b7280',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const MultiplayerGameComponent: React.FC<MultiplayerGameProps> = ({ 
  username: propsUsername, 
  roomId: frontendRoomId, 
  skipModal = false,
  onReturnToLobby,
  onNavigateToLobby
}) => {
  const location = useLocation();
  const navigationState = location.state as { roomId?: string; username?: string } | null;
  
  dbg('multiplayer', '🎮 MultiplayerGame: Component loaded with skipModal:', skipModal, 'navigationState:', navigationState);
  
  const { user } = useAuth();
  
  const {
    isConnected,
    isConnecting,
    connectionError,
    gameState,
    partnershipConfig, // Get from room state instead of gameState.settings
    currentPlayer,
    players,
    currentPhase,
    playerCards,
    connectToGame,
    disconnect,
    makeBid,
    passMove,
    playCard,
    setReady,
    joinSpecificRoom,
    requestAIPlayers,
    addAIPlayer,
    removeAIPlayer,
    restartGame,
    toggleAutoPlay,
    startFreshSession,
    roomInfo,
    setGameErrorHandler,
  } = useGameContext();

  // Notification system
  const { 
    showGameNotification, 
    showSuccess, 
    showError, 
    showWarning, 
    showConnectionAlert 
  } = useNotifications();

  dbg('multiplayer', '🎮 MultiplayerGame: Hook state - isConnected:', isConnected, 'currentPhase:', currentPhase, 'gameState:', gameState?.status);

  // Dynamic bid amount based on player count and variant
  const getMinBidAmount = () => {
    // 6-2deck variant uses 2 decks, so min bid is 250
    if (partnershipConfig === '6-2deck') {
      return 250;
    }
    // Standard logic: 7+ players use 2 decks (250 min), otherwise 1 deck (125 min)
    return players.length >= 7 ? 250 : 125;
  };
  
  // Partnership config comes from room state (via context), no need to extract from gameState.settings
  
  // Helper function to get expected partner count with config (temporarily unused)
  // const getExpectedPartnerCount = () => {
  //   return getPartnerCount(players.length, partnershipConfig);
  // };
  
  const [bidAmount, setBidAmount] = useState(() => getMinBidAmount()); 
  const [selectedSuit, setSelectedSuit] = useState<SuitType>('spades');
  const [selectedPartnerCards, setSelectedPartnerCards] = useState<SelectedPartnerCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [showBidding, setShowBidding] = useState(false);
  const [showMobileInfo, setShowMobileInfo] = useState(false); // Mobile info panel toggle
  // Don't show modal if we're coming from GameRoom with existing connection
  const [showJoinModal, setShowJoinModal] = useState(!navigationState && !skipModal); 
  const [roomScores, setRoomScores] = useState<RoomScoreData | null>(null);
  // Mobile tab state removed as it's not used in the simplified mobile design
  
  // Save last bid info before round ends (for victory overlay)
  const [lastBidAmount, setLastBidAmount] = useState<number | undefined>(undefined);
  const [lastBidderId, setLastBidderId] = useState<string | undefined>(undefined);
  
  // Timer and Pressure Indicators State
  const [biddingTimeLeft, setBiddingTimeLeft] = useState<number>(30); // Deprecated - kept for backward compatibility
  const [timeLeft, setTimeLeft] = useState<number | null>(null); // Server-synced timer for both phases
  const [timerActive, setTimerActive] = useState(false);
  const [pressureLevel, setPressureLevel] = useState<'low' | 'medium' | 'high'>('low');
  const [serverTimerStart, setServerTimerStart] = useState<number | null>(null);
  const [serverTimerDuration, setServerTimerDuration] = useState<number>(30000);
  
    // AI Engine Status
    const [aiEngineStatus, setAiEngineStatus] = useState<'active' | 'fallback' | 'unknown'>('unknown');

  // Auto-play state
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);

  // Helper function to add notifications using new toast system
  const addNotification = (message: string, type: GameNotification['type'] = 'info', duration = 3000) => {
    // Map notification types to toast notifications
    switch (type) {
      case 'success':
        showSuccess('Success', message, { duration });
        break;
      case 'error':
        showError('Error', message, { duration });
        break;
      case 'warning':
        showWarning('Warning', message, { duration });
        break;
      case 'turn':
        showGameNotification('Your Turn', message, { duration, icon: '⏰' });
        break;
      case 'info':
      default:
        showGameNotification('Game Info', message, { duration, icon: '🎮' });
        break;
    }
  };


  // Toggle auto-play handler
  const handleToggleAutoPlay = () => {
    const newState = !autoPlayEnabled;
    setAutoPlayEnabled(newState);
    toggleAutoPlay(newState);
    
    addNotification(
      newState 
        ? '🤖 Auto-play enabled - AI will make moves for you' 
        : '👤 Auto-play disabled - manual control restored',
      newState ? 'success' : 'info'
    );
  };

  // Timer and Pressure Management
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (timerActive && biddingTimeLeft > 0) {
      timer = setInterval(() => {
        setBiddingTimeLeft(prev => {
          const newTime = prev - 1;
          
          // Update pressure level based on time remaining
          if (newTime <= 5) {
            setPressureLevel('high');
          } else if (newTime <= 15) {
            setPressureLevel('medium');
          } else {
            setPressureLevel('low');
          }
          
          // Auto-pass when timer reaches 0
          if (newTime <= 0) {
            setTimerActive(false);
            if (showBidding && gameState?.currentRound?.biddingPhase.currentBidder === currentPlayer?.id) {
              addNotification('Time up! Automatically passing turn', 'warning');
              try {
                passMove();
                setShowBidding(false);
              } catch (error) {
                console.error('Auto-pass failed:', error);
              }
            }
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timerActive, biddingTimeLeft, showBidding, currentPlayer?.id, gameState?.currentRound?.biddingPhase.currentBidder]);

    // Listen for AI engine status changes
    useEffect(() => {
      const handleAIStatusChange = (event: CustomEvent) => {
        setAiEngineStatus(event.detail);
      };
    
      window.addEventListener('aiEngineStatusChanged', handleAIStatusChange as EventListener);
    
      return () => {
        window.removeEventListener('aiEngineStatusChanged', handleAIStatusChange as EventListener);
      };
    }, []);

  // Listen for server turn timer events (new server-synced timer)
  useEffect(() => {
    const handleTimerStarted = (event: CustomEvent) => {
      const data = event.detail as { 
        playerId: string; 
        startTime: number; 
        durationMs: number;
        phase: string;
      };
      
      console.log('🕐 Server timer started:', data);
      setServerTimerStart(data.startTime);
      setServerTimerDuration(data.durationMs);
      
      // Calculate initial time left based on server time
      const now = Date.now();
      const elapsed = now - data.startTime;
      const calculatedTimeLeft = Math.max(0, Math.floor((data.durationMs - elapsed) / 1000));
      setTimeLeft(calculatedTimeLeft);
      setBiddingTimeLeft(calculatedTimeLeft); // Update old state for backward compatibility
      
      // Update pressure level based on phase duration
      if (data.phase === 'playing') {
        // 15 second timer - high pressure from start
        if (calculatedTimeLeft <= 5) {
          setPressureLevel('high');
        } else if (calculatedTimeLeft <= 10) {
          setPressureLevel('medium');
        } else {
          setPressureLevel('low');
        }
      } else {
        // 30 second timer - normal pressure curve
        setPressureLevel('low');
      }
    };
    
    window.addEventListener('turnTimerStarted', handleTimerStarted as EventListener);
    
    return () => {
      window.removeEventListener('turnTimerStarted', handleTimerStarted as EventListener);
    };
  }, []);

  // Update timer every second based on server timestamp
  useEffect(() => {
    if (serverTimerStart === null) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - serverTimerStart;
      const calculatedTimeLeft = Math.max(0, Math.floor((serverTimerDuration - elapsed) / 1000));
      setTimeLeft(calculatedTimeLeft);
      setBiddingTimeLeft(calculatedTimeLeft); // Update old state for backward compatibility
      
      // Update pressure level based on time remaining
      if (serverTimerDuration === 15000) {
        // Playing phase (15s)
        if (calculatedTimeLeft <= 3) {
          setPressureLevel('high');
        } else if (calculatedTimeLeft <= 8) {
          setPressureLevel('medium');
        } else {
          setPressureLevel('low');
        }
      } else {
        // Bidding phase (30s)
        if (calculatedTimeLeft <= 5) {
          setPressureLevel('high');
        } else if (calculatedTimeLeft <= 15) {
          setPressureLevel('medium');
        } else {
          setPressureLevel('low');
        }
      }
      
      if (calculatedTimeLeft === 0) {
        setServerTimerStart(null);
        setTimeLeft(null);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [serverTimerStart, serverTimerDuration]);

  // Start timer when it's player's turn to bid OR play (game-wide, not modal-dependent)
  useEffect(() => {
    const isBiddingTurn = currentPhase === 'bidding' && 
                          gameState?.currentRound?.biddingPhase.currentBidder === currentPlayer?.id;
    const isPlayingTurn = currentPhase === 'playing' &&
                          gameState?.currentRound?.playPhase?.currentPlayer === currentPlayer?.id;
    
    const isMyTurn = isBiddingTurn || isPlayingTurn;
    
    if (isMyTurn && !timerActive) {
      // Reset timer duration based on phase (15s for playing, 30s for bidding)
      const duration = currentPhase === 'playing' ? 15 : 30;
      setBiddingTimeLeft(duration);
      setTimerActive(true);
      setPressureLevel('low');
      console.log(`⏰ Frontend: Timer activated for ${currentPhase} phase, ${duration}s`);
    } else if (!isMyTurn && timerActive) {
      setTimerActive(false);
      console.log(`⏰ Frontend: Timer deactivated (not my turn)`);
    }
  }, [currentPhase, gameState?.currentRound?.biddingPhase.currentBidder, gameState?.currentRound?.playPhase?.currentPlayer, currentPlayer?.id, timerActive]);

  // Get playable cards based on current game state
  const getPlayableCards = (): Card[] => {
    dbg('multiplayer', '🎯 DEBUG getPlayableCards:', {
      currentPhase,
      hasPlayPhase: !!gameState?.currentRound?.playPhase,
      currentPlayer: currentPlayer?.id,
      backendCurrentPlayer: gameState?.currentRound?.playPhase?.currentPlayer,
      isMyTurn: gameState?.currentRound?.playPhase?.currentPlayer === currentPlayer?.id,
      playerCardsCount: playerCards.length
    });

    if (currentPhase !== 'playing' || !gameState?.currentRound?.playPhase) {
  dbg('multiplayer', '🎯 Not playing phase or no play phase');
      return [];
    }

    const currentTrick = gameState.currentRound.playPhase.currentTrick;
    const isMyTurn = gameState.currentRound.playPhase.currentPlayer === currentPlayer?.id;
    
    if (!isMyTurn) {
  dbg('multiplayer', '🎯 Not my turn');
      return [];
    }

    // If no current trick or no cards have been played in this trick, all cards are playable
    if (!currentTrick || !currentTrick.cards || currentTrick.cards.length === 0) {
      return playerCards;
    }

    // If cards have been played, we need to follow suit if possible
    const leadSuit = currentTrick.cards[0]?.card?.suit;
    if (!leadSuit) {
      return playerCards;
    }

    // Check if player has cards of the lead suit
    const cardsOfLeadSuit = playerCards.filter(card => card.suit === leadSuit);
    
    // If player has cards of lead suit, they must play them
    if (cardsOfLeadSuit.length > 0) {
      return cardsOfLeadSuit;
    }

    // If player doesn't have cards of lead suit, they can play any card
    return playerCards;
  };

  // Set up game error handler to show notifications for game action errors
  useEffect(() => {
    setGameErrorHandler((error: string) => {
      dbg('multiplayer', '🎯 Handling game action error as notification:', error);
      
      // Map specific errors to more user-friendly messages
      let message = error;
      let type: GameNotification['type'] = 'error';
      
      if (error.includes('Invalid bid')) {
        message = 'Invalid bid! Please check your bid amount and partner card selection.';
        type = 'warning';
      } else if (error.includes('Invalid card play')) {
        message = 'Invalid card play! Please check if you can play that card according to the rules.';
        type = 'warning';
      } else if (error.includes('Not your turn')) {
        message = "It's not your turn!";
        type = 'turn';
      } else if (error.includes('Cannot play')) {
        message = 'Cannot play that card right now.';
        type = 'warning';
      } else if (error.includes('Invalid move')) {
        message = 'Invalid move! Please try again.';
        type = 'warning';
      }
      
      addNotification(message, type, 4000); // Show for 4 seconds
    });
  }, [setGameErrorHandler]);

  // Auto-restart game when auto-play is enabled and all players are ready during round_end
  useEffect(() => {
    if (!autoPlayEnabled) return; // Only auto-restart when auto-play is enabled
    
    if (currentPhase === 'round_end' || currentPhase === 'waiting_for_players') {
      const humanPlayers = players.filter(p => !p.isAI);
      const readyHumans = humanPlayers.filter(p => p.isReady);
      const allHumansReady = humanPlayers.length > 0 && readyHumans.length === humanPlayers.length;
      
      // Also check if all players (including AI) are ready
      const allPlayersReady = players.length > 0 && players.every(p => p.isReady);
      
      if ((allHumansReady || allPlayersReady) && gameState?.status !== 'completed') {
        console.log('🤖 Auto-play mode: All players ready - auto-starting new game...');
        // Small delay to allow backend to complete auto-ready process
        setTimeout(() => {
          restartGame();
        }, 1500);
      }
    }
  }, [autoPlayEnabled, currentPhase, players, gameState?.status, restartGame]);

  // Don't auto-connect - always show the join modal first
  // useEffect(() => {
  //   if (roomId && !isConnected && !isConnecting) {
  //     setShowJoinModal(false);
  //     connectToGame(currentUsername, roomId);
  //   }
  // }, [connectToGame, currentUsername, roomId, isConnected, isConnecting]);

  // Auto-connect when skipModal is true and roomId is provided (for Dashboard join)
  useEffect(() => {
    const effectiveUsername = propsUsername || user?.displayName || user?.username || 'Anonymous Player';
    if (skipModal && frontendRoomId && !isConnected && !isConnecting && effectiveUsername) {
  dbg('multiplayer', '🎮 MultiplayerGame: Auto-connecting to room', frontendRoomId, 'with skipModal for user:', effectiveUsername);
      setShowJoinModal(false);
      joinSpecificRoom(frontendRoomId, effectiveUsername);
    }
  }, [skipModal, frontendRoomId, isConnected, isConnecting, propsUsername, user?.username, user?.displayName, joinSpecificRoom]);

  // Reset bidding state when new game starts (phase changes to waiting_for_players or round_end)
  useEffect(() => {
    if (currentPhase === 'waiting_for_players' || currentPhase === 'round_end') {
      dbg('multiplayer', '🔄 Resetting bidding state - phase changed to:', currentPhase);
      setBidAmount(getMinBidAmount());
      setSelectedSuit('spades');
      setSelectedPartnerCards([]);
      setShowBidding(false);
    }
  }, [currentPhase]);

  // Update bid amount when bidding modal opens - set to current bid + 5 if there's an existing bid
  useEffect(() => {
    if (showBidding && currentPhase === 'bidding') {
      const currentBidAmount = gameState?.currentRound?.biddingPhase.currentBid?.amount || 0;
      const minBid = getMinBidAmount();
      
      if (currentBidAmount > 0) {
        // Round up to nearest 5
        const nextBid = Math.ceil((currentBidAmount + 5) / 5) * 5;
        dbg('multiplayer', `🎯 Setting bid amount to current bid + 5: ${currentBidAmount} + 5 = ${nextBid}`);
        setBidAmount(nextBid);
      } else {
        dbg('multiplayer', `🎯 No current bid, using minimum: ${minBid}`);
        setBidAmount(minBid);
      }
    }
  }, [showBidding, currentPhase, gameState?.currentRound?.biddingPhase.currentBid?.amount]);

  // Capture bid info when phase changes (for victory overlay)
  useEffect(() => {
    const currentBid = gameState?.currentRound?.biddingPhase?.currentBid;
    if (currentBid?.amount && currentBid?.playerId) {
      console.log('💾 Saving bid info:', currentBid);
      setLastBidAmount(currentBid.amount);
      setLastBidderId(currentBid.playerId);
    }
  }, [gameState?.currentRound?.biddingPhase?.currentBid?.amount, gameState?.currentRound?.biddingPhase?.currentBid?.playerId]);

  // Handle partner card selection - improved system to prevent play order conflicts
  const handlePartnerCardClick = (card: Card) => {
    dbg('multiplayer', `🎯 PARTNER SELECTION: User clicked ${card.rank}${convertSuitToDisplay(card.suit)}`);
    
    const expectedCount = getPartnerCount(players.length, partnershipConfig);
    const maxPlayers = gameState?.maxPlayers || players.length;
    
    // Single deck: 4-6 players EXCEPT "6-2deck" variant which uses 2 decks
    const isSingleDeck = maxPlayers <= 6 && partnershipConfig !== '6-2deck';
    
    console.log('🔍 PARTNER CARD CLICK:', {
      card: `${card.rank}${convertSuitToDisplay(card.suit)}`,
      maxPlayers,
      partnershipConfig,
      isSingleDeck,
      calculation: `${maxPlayers} <= 6 && "${partnershipConfig}" !== "6-2deck" = ${isSingleDeck}`
    });
    
    dbg('multiplayer', `🎯 PARTNER SELECTION DEBUG: maxPlayers=${maxPlayers}, partnershipConfig="${partnershipConfig}", isSingleDeck=${isSingleDeck}, expectedCount=${expectedCount}`);
    
    // Check how many times this card (suit+rank) is already selected
    const existingCount = selectedPartnerCards.filter(pc => 
      pc.suit === card.suit && pc.rank === card.rank
    ).length;
    
    console.log('📊 EXISTING COUNT:', {
      card: `${card.rank}${convertSuitToDisplay(card.suit)}`,
      existingCount,
      selectedPartnerCards: selectedPartnerCards.map(pc => `${pc.rank}${convertSuitToDisplay(pc.suit)}-${pc.playOrder === 1 ? '1st' : '2nd'}`),
      totalSelected: selectedPartnerCards.length,
      expectedCount
    });
    
  dbg('multiplayer', `🎯 PARTNER SELECTION: ${card.rank}${convertSuitToDisplay(card.suit)} already selected ${existingCount} times, total partner cards: ${selectedPartnerCards.length}/${expectedCount}, isSingleDeck: ${isSingleDeck}`);
    
    if (existingCount === 0) {
      console.log('✅ FIRST SELECTION PATH: existingCount === 0');
      // First selection: check if we can add this card
      if (selectedPartnerCards.length >= expectedCount) {
        addNotification(`Maximum ${expectedCount} partner cards allowed!`, 'warning');
        return;
      }
      
      // For single deck games, automatically set to 1st play (only 1 copy exists)
      // For double deck games, default to 2nd play order (user can change via toggle)
      let playOrder: 1 | 2 = isSingleDeck ? 1 : 2;
      
      const newPartnerCard: SelectedPartnerCard = {
        suit: card.suit,
        rank: card.rank,
        playOrder,
        id: `${card.suit}-${card.rank}-${playOrder === 1 ? '1st' : '2nd'}`
      };
      
  setSelectedPartnerCards(prev => [...prev, newPartnerCard]);
  
      if (isSingleDeck) {
        dbg('multiplayer', `✅ PARTNER SELECTION: Added ${card.rank}${convertSuitToDisplay(card.suit)} - single deck, automatically 1st play (ID: ${newPartnerCard.id})`);
        addNotification(`${card.rank}${convertSuitToDisplay(card.suit)} selected - Single deck, no play order needed`, 'success');
      } else {
        dbg('multiplayer', `✅ PARTNER SELECTION: Added ${card.rank}${convertSuitToDisplay(card.suit)} - 2nd play becomes partner (ID: ${newPartnerCard.id})`);
      }
      
      // Show helpful feedback about play order distribution (only for double deck)
      if (!isSingleDeck) {
        const newFirstPlayCount = selectedPartnerCards.filter(pc => pc.playOrder === 1).length;
        const newSecondPlayCount = selectedPartnerCards.filter(pc => pc.playOrder === 2).length + 1;
      
        if (selectedPartnerCards.length + 1 === expectedCount) {
          addNotification(`Partner selection complete: ${newFirstPlayCount} cards set to 1st play, ${newSecondPlayCount} cards set to 2nd play`, 'success');
        }
      }
      
    } else if (existingCount === 1) {
      console.log('🔄 SECOND SELECTION PATH: existingCount === 1, isSingleDeck =', isSingleDeck);
      
      // Second selection: only allowed in double-deck games (7+ players)
      if (isSingleDeck) {
        console.log('❌ REJECTING: isSingleDeck is TRUE, cannot add second copy');
        addNotification(`Card already selected! Single deck games only have one copy of each card.`, 'warning');
        dbg('multiplayer', `⚠️ PARTNER SELECTION: Cannot select ${card.rank}${convertSuitToDisplay(card.suit)} again - single deck game`);
        return;
      }
      
      console.log('✅ ALLOWING: isSingleDeck is FALSE, proceeding to add opposite play order');
      
      // For double deck games, add the opposite play order for the same card
      if (selectedPartnerCards.length >= expectedCount) {
        console.log('❌ REJECTING: Already at maximum partner count');
        addNotification(`Maximum ${expectedCount} partner cards allowed!`, 'warning');
        return;
      }
      
      // Find the existing play order for this card and use the opposite
      const existingCard = selectedPartnerCards.find(pc => 
        pc.suit === card.suit && pc.rank === card.rank
      );
      const oppositePlayOrder: 1 | 2 = existingCard?.playOrder === 1 ? 2 : 1;
      
      console.log('✅ ADDING OPPOSITE PLAY ORDER:', {
        existingPlayOrder: existingCard?.playOrder === 1 ? '1st' : '2nd',
        oppositePlayOrder: oppositePlayOrder === 1 ? '1st' : '2nd'
      });
      
  dbg('multiplayer', `🎯 PARTNER SELECTION: ${card.rank}${convertSuitToDisplay(card.suit)} already has ${existingCard?.playOrder === 1 ? '1st' : '2nd'} play, adding ${oppositePlayOrder === 1 ? '1st' : '2nd'} play`);
      
      const newPartnerCard: SelectedPartnerCard = {
        suit: card.suit,
        rank: card.rank,
        playOrder: oppositePlayOrder,
        id: `${card.suit}-${card.rank}-${oppositePlayOrder === 1 ? '1st' : '2nd'}`
      };
      
  setSelectedPartnerCards(prev => [...prev, newPartnerCard]);
  dbg('multiplayer', `✅ PARTNER SELECTION: Added ${card.rank}${convertSuitToDisplay(card.suit)} - ${oppositePlayOrder === 1 ? '1st' : '2nd'} play becomes partner (ID: ${newPartnerCard.id})`);
      
      addNotification(`${card.rank}${convertSuitToDisplay(card.suit)} now selected for both 1st and 2nd play`, 'info');
      
    } else {
      // Already have 2 selections for this card, show info
  dbg('multiplayer', `🎯 PARTNER SELECTION: ${card.rank}${convertSuitToDisplay(card.suit)} already selected for both 1st and 2nd play (max reached)`);
      addNotification(`${card.rank}${convertSuitToDisplay(card.suit)} already selected for both 1st and 2nd play`, 'info');
    }
  };

  // Get how many times a card is selected
  const getCardSelectionCount = (suit: SuitType, rank: RankType): number => {
    return selectedPartnerCards.filter(pc => pc.suit === suit && pc.rank === rank).length;
  };

  // Function to remove a specific partner card
  const removePartnerCard = (cardToRemove: SelectedPartnerCard) => {
    setSelectedPartnerCards(prev => 
      prev.filter(pc => pc.id !== cardToRemove.id)
    );
    dbg('multiplayer', `🗑️ Removed ${cardToRemove.rank}${convertSuitToDisplay(cardToRemove.suit)} (${cardToRemove.playOrder === 1 ? '1st' : '2nd'} play)`);
  };

  // Function to toggle play order for a selected partner card
  const togglePlayOrder = (card: SelectedPartnerCard) => {
    const maxPlayers = gameState?.maxPlayers || players.length;
    // partnershipConfig available from useGameContext() hook
    const isSingleDeck = maxPlayers <= 6 && partnershipConfig !== '6-2deck'; // Single deck EXCEPT 6-2deck variant
    
    console.log('🔄 togglePlayOrder:', { maxPlayers, partnershipConfig, isSingleDeck, card: `${card.rank}${convertSuitToDisplay(card.suit)}` });
    
    // Prevent play order changes in single deck games
    if (isSingleDeck) {
      addNotification('Single deck game - no play order selection needed', 'info');
      return;
    }
    
    // Determine the target play order (the one we're toggling TO)
    const targetPlayOrder: 1 | 2 = card.playOrder === 1 ? 2 : 1;
    
    // CRITICAL: Prevent creating a duplicate (same suit+rank+playOrder).
    // If another selected card already has this suit+rank with the target play order,
    // toggling would create two cards with the same id (React key conflict) and
    // allow the same card to be selected for the same play order twice.
    const conflict = selectedPartnerCards.find(pc =>
      pc.id !== card.id &&
      pc.suit === card.suit &&
      pc.rank === card.rank &&
      pc.playOrder === targetPlayOrder
    );
    
    if (conflict) {
      console.log('❌ TOGGLE CONFLICT: Another card already has this play order:', {
        card: `${card.rank}${convertSuitToDisplay(card.suit)}`,
        targetPlayOrder,
        conflictId: conflict.id
      });
      addNotification(`${card.rank}${convertSuitToDisplay(card.suit)} is already set to ${targetPlayOrder === 1 ? '1st' : '2nd'} play. Remove the other selection first.`, 'warning');
      return;
    }
    
  dbg('multiplayer', `🔄 TOGGLE START: Toggling ${card.rank}${convertSuitToDisplay(card.suit)} from ${card.playOrder}${card.playOrder === 1 ? 'st' : 'nd'} play`);
  dbg('multiplayer', `🔄 TOGGLE START: Current selectedPartnerCards:`, selectedPartnerCards.map(pc => `${pc.rank}${convertSuitToDisplay(pc.suit)}:${pc.playOrder}`));
    
    setSelectedPartnerCards(prev => {
      const updated = prev.map(pc => {
        if (pc.id === card.id) {
          const newPlayOrder: 1 | 2 = pc.playOrder === 1 ? 2 : 1;
          const newId = `${pc.suit}-${pc.rank}-${newPlayOrder === 1 ? '1st' : '2nd'}`;
          dbg('multiplayer', `🔄 TOGGLE UPDATE: ${pc.rank}${convertSuitToDisplay(pc.suit)} changing from ${pc.playOrder}${pc.playOrder === 1 ? 'st' : 'nd'} to ${newPlayOrder}${newPlayOrder === 1 ? 'st' : 'nd'} play`);
          return { ...pc, playOrder: newPlayOrder, id: newId };
        }
        return pc;
      });
  dbg('multiplayer', `🔄 TOGGLE RESULT: Updated selectedPartnerCards:`, updated.map(pc => `${pc.rank}${convertSuitToDisplay(pc.suit)}:${pc.playOrder}`));
      return updated;
    });
  dbg('multiplayer', `✅ Toggle complete for ${card.rank}${convertSuitToDisplay(card.suit)}`);
  };

  const handleJoinRoom = (targetRoomId: string, playerUsername: string) => {
    setShowJoinModal(false);
    // Pass authenticated user ID for stats tracking
    joinSpecificRoom(targetRoomId, playerUsername, user?.id);
  };

  const handleCreateRoom = (playerUsername: string) => {
    setShowJoinModal(false);
    // Clear temp user ID when creating new room, then pass authenticated user ID
    startFreshSession();
    connectToGame(playerUsername, undefined, false, user?.id);
  };

  // Update bid amount when player count changes
  useEffect(() => {
    const newMinBid = getMinBidAmount();
    if (bidAmount < newMinBid) {
      setBidAmount(newMinBid);
    }
  }, [players.length]);

  // Connection status notifications
  useEffect(() => {
    if (isConnected) {
      showConnectionAlert('Connected', 'Successfully connected to game room!', { 
        duration: 3000, 
        icon: '✅' 
      });
    }
  }, [isConnected]);

  // Connection error notifications
  useEffect(() => {
    if (connectionError) {
      showError('Connection Error', connectionError, { 
        duration: 6000, 
        icon: '🔴' 
      });
    }
  }, [connectionError]);

  // Game phase transition notifications
  useEffect(() => {
    if (currentPhase) {
      switch (currentPhase) {
        case 'bidding':
          showGameNotification('Bidding Phase', 'The bidding phase has started!', { 
            duration: 3000, 
            icon: '🎯' 
          });
          break;
        case 'playing':
          showGameNotification('Playing Phase', 'Time to play cards!', { 
            duration: 3000, 
            icon: '🃏' 
          });
          break;
        case 'round_end':
          showSuccess('Round Complete', 'Round has ended. Calculating scores...', { 
            duration: 4000, 
            icon: '🏆' 
          });
          break;
      }
    }
  }, [currentPhase]);

  // Player join/leave notifications
  const [previousPlayerCount, setPreviousPlayerCount] = useState(players.length);
  useEffect(() => {
    if (players.length > previousPlayerCount) {
      const newPlayerCount = players.length - previousPlayerCount;
      showGameNotification(
        'Player Joined', 
        `${newPlayerCount} player${newPlayerCount > 1 ? 's' : ''} joined the game! (${players.length} total)`, 
        { duration: 3000, icon: '👋' }
      );
    } else if (players.length < previousPlayerCount) {
      const leftPlayerCount = previousPlayerCount - players.length;
      showWarning(
        'Player Left', 
        `${leftPlayerCount} player${leftPlayerCount > 1 ? 's' : ''} left the game. (${players.length} remaining)`, 
        { duration: 4000, icon: '👋' }
      );
    }
    setPreviousPlayerCount(players.length);
  }, [players.length, previousPlayerCount]);

  // Monitor for player-to-AI conversions (only during active game, not during room setup)
  const [previousAICount, setPreviousAICount] = useState(() => 
    players.filter(p => p.isAI).length
  );
  useEffect(() => {
    const currentAICount = players.filter(p => p.isAI).length;
    const isWaitingPhase = gameState?.phase === 'waiting_for_players';
    
    // Only show notification if:
    // 1. AI count increased (player converted to AI)
    // 2. We're NOT in waiting phase (where AI is intentionally added)
    // 3. Game has actually started (status is in_progress or completed)
    if (currentAICount > previousAICount && !isWaitingPhase && gameState?.status !== 'waiting') {
      const convertedCount = currentAICount - previousAICount;
      showWarning(
        'Player Disconnected', 
        `${convertedCount} player${convertedCount > 1 ? 's' : ''} disconnected and ${convertedCount > 1 ? 'were' : 'was'} replaced by AI to continue the game.`, 
        { duration: 5000, icon: '🤖' }
      );
      console.log(`🤖 ${convertedCount} player(s) converted to AI during game - Total AI: ${currentAICount}`);
    } else if (currentAICount > previousAICount && isWaitingPhase) {
      console.log(`✅ ${currentAICount - previousAICount} AI player(s) added to fill room - Total AI: ${currentAICount} (no notification shown)`);
    }
    
    setPreviousAICount(currentAICount);
  }, [players, previousAICount, gameState?.phase, gameState?.status]);

  // Fetch room scores when game ends OR when a new game starts
  useEffect(() => {
    const fetchRoomScores = async () => {
      console.log('🏆 fetchRoomScores useEffect triggered - gameState.status:', gameState?.status, 'gameState.phase:', gameState?.phase, 'gameHistory.length:', gameState?.gameHistory?.length, 'frontendRoomId:', frontendRoomId);
      
      const isGameEnded = gameState?.status === 'completed' || gameState?.phase === 'game_end';
      // New game starts when status changes back to in_progress with no game history (fresh start)
      const isNewGameStarting = gameState?.status === 'in_progress' && 
                               gameState?.phase === 'bidding' && 
                               (gameState?.gameHistory?.length === 0);
      
      if ((isGameEnded || isNewGameStarting) && frontendRoomId) {
        if (isGameEnded) {
          console.log('🏆 Game ended - fetching room scores for room:', frontendRoomId);
          dbg('roomscore', '🏆 Game ended - fetching room scores for room:', frontendRoomId);
          
          // Wait 2 seconds to ensure backend has:
          // 1) Completed trick immediately (no delay)
          // 2) Calculated final scores in completeRound()
          // 3) Recorded game completion to database
          // 4) Broadcasted final state with scores
          console.log('⏳ Waiting 2 seconds for backend to complete score recording...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        } else {
          console.log('🔄 New game starting - refreshing room scores for room:', frontendRoomId);
          dbg('roomscore', '🔄 New game starting - refreshing room scores');
          
          // Small delay to ensure backend state is settled
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        console.log('🏆 Fetching room scores...');
        const scores = await roomScoreApi.getRoomScores(frontendRoomId);
        console.log('🏆 Room scores API response:', scores);
        
        if (scores) {
          console.log('✅ Room scores fetched successfully:', scores);
          console.log('📊 Scores array length:', scores.scores?.length || 0);
          console.log('📊 Scores details:', JSON.stringify(scores.scores, null, 2));
          dbg('roomscore', '✅ Room scores fetched successfully:', scores);
          setRoomScores(scores);
        } else {
          console.warn('⚠️ No room scores available for room:', frontendRoomId);
          dbg('roomscore', '⚠️ No room scores available');
        }
      }
    };

    fetchRoomScores();
  }, [gameState?.status, gameState?.phase, gameState?.gameHistory?.length, frontendRoomId]);



  const handleCardPlay = (card: Card) => {
    console.log('🎯 Current conditions:', {
      currentPhase,
      isMyTurn: gameState?.currentRound?.playPhase.currentPlayer === currentPlayer?.id,
      currentPlayer: currentPlayer?.id,
      backendCurrentPlayer: gameState?.currentRound?.playPhase.currentPlayer
    });
    
    try {
      // Check if it's the playing phase
      if (currentPhase !== 'playing') {
        console.log('⚠️ Cannot play card during', currentPhase, 'phase');
        addNotification(`Cannot play cards during the ${currentPhase} phase`, 'warning');
        return;
      }
      
      // Check if it's the player's turn
      if (gameState?.currentRound?.playPhase.currentPlayer !== currentPlayer?.id) {
        console.log('⚠️ Not player\'s turn to play card');
        addNotification("It's not your turn to play a card!", 'turn');
        return;
      }

      // Check if the card is playable
      const playableCards = getPlayableCards();
      const isCardPlayable = playableCards.some(playableCard => playableCard.id === card.id);
      
      if (!isCardPlayable) {
        console.log('⚠️ Card is not playable:', card);
        addNotification("You must follow suit if you have cards of that suit!", 'warning');
        return;
      }
      
      // Set visual feedback
      setSelectedCard(card);
      
      // Slight delay for visual feedback before playing
      setTimeout(() => {
        try {
          playCard(card);
          setSelectedCard(null);
        } catch (error) {
          console.error('Error playing card:', error);
          addNotification('Failed to play card. Please try again.', 'error');
          setSelectedCard(null);
        }
      }, 200);

    } catch (error) {
      console.error('Error in handleCardPlay:', error);
      addNotification('An error occurred while trying to play the card', 'error');
      setSelectedCard(null);
    }
  };

  const handleMakeBid = () => {
    try {
      // Check if it's actually the player's turn to bid
      if (gameState?.currentRound?.biddingPhase.currentBidder !== currentPlayer?.id) {
        addNotification("It's not your turn to bid!", 'turn');
        return;
      }
      
      if (!selectedPartnerCards || selectedPartnerCards.length === 0) {
        const expectedCount = getPartnerCount(players.length, partnershipConfig);
        addNotification(`Please select ${expectedCount} partner card${expectedCount > 1 ? 's' : ''} before bidding!`, 'warning');
        return;
      }
      
      // Validate partner card count
      const expectedPartnerCount = getPartnerCount(players.length, partnershipConfig);
      if (selectedPartnerCards.length !== expectedPartnerCount) {
        addNotification(`Please select exactly ${expectedPartnerCount} partner card${expectedPartnerCount > 1 ? 's' : ''} for ${players.length} players!`, 'warning');
        return;
      }

      // CRITICAL: Validate play order consistency to prevent partner revelation bugs
      const playOrderValidation = (() => {
        const playOrder1Count = selectedPartnerCards.filter(pc => pc.playOrder === 1).length;
        const playOrder2Count = selectedPartnerCards.filter(pc => pc.playOrder === 2).length;
        const isDoubleDeck = players.length >= 7 || partnershipConfig === '6-2deck';
        
  dbg('multiplayer', `🎯 BID VALIDATION: ${playOrder1Count} cards for 1st play, ${playOrder2Count} cards for 2nd play (${isDoubleDeck ? 'double' : 'single'} deck)`);
        
        // In double deck games (7+ players OR 6-2deck variant), allow any combination of play orders
        // The backend handles partner revelation correctly based on actual play timing
        if (isDoubleDeck) {
          // For double deck: Any combination is allowed since multiple copies exist
          dbg('multiplayer', `✅ Double deck game - allowing any play order combination`);
          return { valid: true, message: null };
        }
        
        // For single deck games (4-6 players), still allow flexibility
        // The old validation was too restrictive - users should have freedom
  dbg('multiplayer', `✅ Single deck game - allowing user's preferred play order combination`);
        return { valid: true, message: null };
      })();
      
      if (!playOrderValidation.valid) {
        addNotification(playOrderValidation.message!, 'error');
        return;
      }

      // Validate bid amount - dynamic based on player count and variant
      const isDoubleDeck = players.length >= 7 || partnershipConfig === '6-2deck';
      const minBid = isDoubleDeck ? 250 : 125;
      const maxBid = isDoubleDeck ? 500 : 250;
      
      if (bidAmount < minBid || bidAmount > maxBid) {
        addNotification(`Bid must be between ${minBid} and ${maxBid} points!`, 'warning');
        return;
      }

      // Check if bid is higher than current bid
      const currentBidAmount = gameState?.currentRound?.biddingPhase.currentBid?.amount || 0;
      if (bidAmount <= currentBidAmount && currentBidAmount > 0) {
        addNotification(`Your bid must be higher than the current bid of ${currentBidAmount}!`, 'warning');
        return;
      }

  dbg('multiplayer', 'Making bid:', { bidAmount, selectedSuit, selectedPartnerCards });
  dbg('multiplayer', '🎯 PRE-BID DEBUG: selectedPartnerCards state before conversion:', selectedPartnerCards.map(pc => ({ 
        card: `${pc.rank}${convertSuitToDisplay(pc.suit)}`, 
        playOrder: pc.playOrder,
        id: pc.id 
      })));
      
      // Clean up partner cards for backend - convert to backend format with play order
      const cleanPartnerCards = selectedPartnerCards.map(card => {        
  dbg('multiplayer', `🎯 BID CREATION: Converting ${card.rank}${convertSuitToDisplay(card.suit)} (${card.playOrder === 1 ? '1st' : '2nd'} play) for backend`);
        return {
          suit: card.suit,
          rank: card.rank,
          id: `${card.suit}-${card.rank}`, // Clean ID format for backend
          desiredPlayOrder: card.playOrder // Use desiredPlayOrder to distinguish from deck copyNumber
        };
      });
      
  dbg('multiplayer', '🎯 BID CREATION: Final cleaned partner cards for backend:', cleanPartnerCards);
      
      // Show summary of partner selection before submitting
      const playOrder1Cards = selectedPartnerCards.filter(pc => pc.playOrder === 1);
      const playOrder2Cards = selectedPartnerCards.filter(pc => pc.playOrder === 2);
      
      if (playOrder1Cards.length > 0 && playOrder2Cards.length > 0) {
  dbg('multiplayer', `✅ Partner selection balanced: ${playOrder1Cards.length} cards for 1st play, ${playOrder2Cards.length} cards for 2nd play`);
      }
      
      makeBid(bidAmount, selectedSuit, cleanPartnerCards);
      setShowBidding(false);
      addNotification(`Bid placed: ${bidAmount} points with ${selectedPartnerCards.length} partner card${selectedPartnerCards.length > 1 ? 's' : ''}!`, 'success');

    } catch (error) {
      console.error('Error making bid:', error);
      addNotification('Failed to place bid. Please try again.', 'error');
    }
  };

  // Interface for player position
  interface PlayerPosition {
    left?: string;
    right?: string;
    top?: string;
    bottom?: string;
    transform: string;
    zIndex: number;
  }

  /**
   * Calculate optimal table dimensions based on player count
   * Fewer players = larger table for better card visibility
   * More players = compact table to fit all players comfortably
   */
  const getTableDimensions = (totalPlayers: number): { width: number; height: number; radiusX: number; radiusY: number } => {
    if (totalPlayers <= 4) {
      // 4 players: Large table with plenty of space
      return { width: 75, height: 70, radiusX: 37.5, radiusY: 35 };
    } else if (totalPlayers <= 6) {
      // 5-6 players: Moderate table size
      return { width: 70, height: 65, radiusX: 35, radiusY: 32.5 };
    } else if (totalPlayers <= 8) {
      // 7-8 players: Compact table
      return { width: 67, height: 64.5, radiusX: 33.5, radiusY: 32.25 };
    } else {
      // 9-10 players: Maximum density table
      return { width: 65, height: 62, radiusX: 32.5, radiusY: 31 };
    }
  };

  /**
   * Calculate player position on ellipse perimeter using mathematical approach
   * IMPORTANT: Position 0 is reserved for current player (at bottom, not rendered)
   * Other players are positioned clockwise from there
   * Uses ellipse parametric equations: x = centerX + radiusX * cos(θ), y = centerY + radiusY * sin(θ)
   * 
   * @param playerIndex - Visual position index (0 = current player at bottom, 1 = next clockwise, etc.)
   * @param totalPlayers - Total number of players in game
   */
  const getPlayerPosition = (playerIndex: number, totalPlayers: number = players.length): PlayerPosition => {
    const tableDims = getTableDimensions(totalPlayers);
    const centerX = 50; // Table center X in viewport %
    const centerY = 50; // Table center Y in viewport %
    
    // Calculate angle for this player position
    // Start from bottom (270° = 3π/2) and distribute players evenly clockwise
    const angleStep = (2 * Math.PI) / totalPlayers;
    const startAngle = (3 * Math.PI) / 2; // Bottom position (270°)
    
    // CLOCKWISE from bottom means DECREASING angles in standard math coords:
    // Bottom (270°) → Right (180°) → Top (90°) → Left (0°)
    // So we SUBTRACT angleStep to go clockwise
    const angle = startAngle - (playerIndex * angleStep);
    const angleDegrees = (angle * 180 / Math.PI) % 360;
    
    console.log(`🔢 Position ${playerIndex}: angle = ${angleDegrees.toFixed(1)}°`);
    
    // Calculate position on ellipse perimeter
    // CRITICAL FIX: Use actual table dimensions (not radius) for positioning
    // Table is centered at 50%, 50% with width/height from getTableDimensions()
    // We want players just outside the table border
    
    // Use a margin map for 4-10 players for optimized positioning
    // Margins reduced to bring all players closer to table proportionally
    let marginOffset = 5; // Default fallback (reduced from 8)
    
    if (totalPlayers === 4) {
      // 4-player: 1=left, 2=top, 3=right (clockwise from bottom)
      if (playerIndex === 1) marginOffset = 7; // left
      else if (playerIndex === 2) marginOffset = 12; // top - further away
      else if (playerIndex === 3) marginOffset = 0.5; // right - very close
    } 
    else if (totalPlayers === 5) {
      // 5-player: clockwise from bottom (0=bottom, 1-4 around table)
      // Reduced margins to bring players closer to table
      if (playerIndex === 1) marginOffset = 5; // left-bottom
      else if (playerIndex === 2) marginOffset = 7; // left-top
      else if (playerIndex === 3) marginOffset = 7; // right-top
      else if (playerIndex === 4) marginOffset = 5; // right-bottom
    }
    else if (totalPlayers === 6) {
      // 6-player: balanced spacing around table
      // User request: Players 2,3,6 closer to table; Players 4,5 away from table
      if (playerIndex === 1) marginOffset = 6;  // Player 2 (left-bottom) - closer to table
      else if (playerIndex === 2) marginOffset = 6;  // Player 3 (left-top) - closer to table
      else if (playerIndex === 3) marginOffset = 13; // Player 4 (top) - away from table
      else if (playerIndex === 4) marginOffset = 6; // Player 5 (right-top) - away from table
      else if (playerIndex === 5) marginOffset = 6;  // Player 6 (right-bottom) - closer to table
    }
    else if (totalPlayers === 7) {
      // 7-player: compact spacing, all closer to table
      if (playerIndex === 1) marginOffset = 6;  // position 1 (left-bottom)
      else if (playerIndex === 2) marginOffset = 8;  // position 2 (left)
      else if (playerIndex === 3) marginOffset = 7;  // position 3 (left-top)
      else if (playerIndex === 4) marginOffset = 7;  // position 4 (top)
      else if (playerIndex === 5) marginOffset = 1;  // position 5 (right-top)
      else if (playerIndex === 6) marginOffset = 6;  // position 6 (right)
    }
    else if (totalPlayers === 8) {
      // 8-player: symmetric, closer spacing
      if (playerIndex === 1) marginOffset = 6;  // left-bottom
      else if (playerIndex === 2) marginOffset = 7;  // left
      else if (playerIndex === 3) marginOffset =  6;  // left-top
      else if (playerIndex === 4) marginOffset = 12;  // top
      else if (playerIndex === 5) marginOffset = 6;  // right-top
      else if (playerIndex === 6) marginOffset = 1;  // right
      else if (playerIndex === 7) marginOffset = 6;  // right-bottom
    }
    else if (totalPlayers === 9) {
      // 9-player: very compact spacing
      if (playerIndex === 1) marginOffset = 7;  // position 1
      else if (playerIndex === 2) marginOffset = 8;  // position 2
      else if (playerIndex === 3) marginOffset = 6;  // position 3
      else if (playerIndex === 4) marginOffset = 7;  // top area
      else if (playerIndex === 5) marginOffset = 7;  // position 5
      else if (playerIndex === 6) marginOffset = 6;  // position 6
      else if (playerIndex === 7) marginOffset = 1.5;  // position 7
      else if (playerIndex === 8) marginOffset = 7;  // position 8
    }
    else if (totalPlayers === 10) {
      // 10-player: maximum compactness
      if (playerIndex === 1) marginOffset = 7;  // position 1
      else if (playerIndex === 2) marginOffset = 5;  // position 2
      else if (playerIndex === 3) marginOffset = 5;  // position 3
      else if (playerIndex === 4) marginOffset = 7;  // left-top
      else if (playerIndex === 5) marginOffset = 13;  // top
      else if (playerIndex === 6) marginOffset = 7;  // right-top
      else if (playerIndex === 7) marginOffset = 5;  // position 7
      else if (playerIndex === 8) marginOffset = 5;  // position 8
      else if (playerIndex === 9) marginOffset = 7;  // position 9
    }
    
    console.log(`📏 Position ${playerIndex} (${angleDegrees.toFixed(0)}°): marginOffset = ${marginOffset}%`);
    
    // Calculate position on table's ellipse edge first
    const tableEdgeX = centerX + (tableDims.radiusX * Math.cos(angle));
    const tableEdgeY = centerY - (tableDims.radiusY * Math.sin(angle)); // Inverted Y-axis
    
    // Then offset outward by margin amount (in direction of angle)
    const posX = tableEdgeX + (marginOffset * Math.cos(angle));
    const posY = tableEdgeY - (marginOffset * Math.sin(angle)); // Inverted Y-axis
    
    // Clamp positions to ensure players stay within viewport (with 3% margin)
    const clampedPosX = Math.max(3, Math.min(97, posX));
    const clampedPosY = Math.max(3, Math.min(97, posY));
    
    // Determine transform based on position (for proper alignment)
    // Players on sides use translateY, top/bottom use translateX, corners use translate
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);
    
    let transform = 'translate(-50%, -50%)'; // Default: center-align both axes
    
    // Optimize transform for cardinal positions (less stretching)
    if (Math.abs(cosAngle) < 0.3) {
      // Near top or bottom (vertical)
      transform = 'translateX(-50%)';
    } else if (Math.abs(sinAngle) < 0.3) {
      // Near left or right (horizontal)
      transform = 'translateY(-50%)';
    }
    
    return {
      left: `${clampedPosX.toFixed(2)}%`,
      top: `${clampedPosY.toFixed(2)}%`,
      transform,
      zIndex: 1
    };
  };

  // Detect mobile/gaming device (landscape-focused for card games)
  const isMobile = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Landscape gaming mode (modern phones/tablets in landscape up to 1400px)
    if (width <= 1400 && width > height) return true;
    
    // Portrait mobile devices (traditional mobile up to 768px)
    if (width <= 768) return true;
    
    // Touch-enabled gaming devices (high-res tablets/phones)
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches && width <= 1400) {
      return true;
    }
    
    return false;
  };

  const [isMobileDevice, setIsMobileDevice] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to get current turn player (commented out - unused)
  /* const getCurrentTurnPlayer = () => {
    if (!gameState || !players || players.length === 0) return null;
    
    if (currentPhase === 'bidding' && gameState?.currentRound?.biddingPhase) {
      const bidderId = gameState.currentRound.biddingPhase.currentBidder;
      return players.find(p => p.id === bidderId) || null;
    }
    
    if (currentPhase === 'playing' && gameState?.currentRound?.playPhase) {
      const playerId = gameState.currentRound.playPhase.currentPlayer;
      return players.find(p => p.id === playerId) || null;
    }
    
    return null;
  }; */

  // Helper function to check if a player is the bidder
  const isBidderFn = (playerId: string) => {
    if (!gameState?.currentRound?.scoring?.biddingTeam) return false;
    return gameState.currentRound.scoring.biddingTeam.bidder === playerId;
  };

  // Helper function to check if a player is current player's partner
  const isPlayerPartner = (playerId: string) => {
    if (!currentPlayer || !gameState?.currentRound?.playPhase) return false;
    
    // Get the bidder and their partner from scoring
    const biddingTeam = gameState.currentRound.scoring?.biddingTeam;
    if (!biddingTeam) return false;
    
    const bidderId = biddingTeam.bidder;
    const partnerId = biddingTeam.partner;
    
    // Check if current player and the other player are both in the bidding team
    const currentPlayerIsBidder = currentPlayer.id === bidderId;
    const currentPlayerIsPartner = currentPlayer.id === partnerId;
    const otherPlayerIsBidder = playerId === bidderId;
    const otherPlayerIsPartner = playerId === partnerId;
    
    // They're partners if they're both on the bidding team and not the same person
    if ((currentPlayerIsBidder || currentPlayerIsPartner) && 
        (otherPlayerIsBidder || otherPlayerIsPartner) && 
        playerId !== currentPlayer.id) {
      return true;
    }
    
    return false;
  };

  // Helper function to check if partner is revealed
  const isPartnerRevealed = () => {
    return (gameState?.currentRound?.playPhase?.revealedPartners?.length || 0) > 0;
  };

  // Removed unused turn indicator variables

  // Handle connection error - after all hooks are called
  if (connectionError) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
        color: 'white',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>Connection Error</h2>
          <p style={{ marginBottom: '1rem' }}>{connectionError}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={() => setShowJoinModal(true)}
              style={{
                background: '#a78bfa',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show join modal if not connected - after all hooks are called
  dbg('multiplayer', '🎮 MultiplayerGame: Modal check - skipModal:', skipModal, 'showJoinModal:', showJoinModal, 'isConnected:', isConnected, 'isConnecting:', isConnecting);
  if (!skipModal && (showJoinModal || (!isConnected && !isConnecting))) {
    dbg('multiplayer', '🎮 MultiplayerGame: Showing join modal');
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
        color: 'white',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <JoinRoomModal
          isOpen={true}
          onClose={() => window.location.href = '/'}
          onJoinRoom={handleJoinRoom}
          onCreateRoom={handleCreateRoom}
        />
      </div>
    );
  }

  // Loading state - after all hooks are called
  if (isConnecting && !showJoinModal) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
        color: 'white',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <h2>Connecting to Game...</h2>
          <p>Room ID: {roomInfo?.roomId || 'Joining...'}</p>
        </div>
      </div>
    );
  }

  // Desktop layout (original layout)
  const desktopLayout = (
    <ErrorBoundary onError={(error, errorInfo) => {
      console.error('🚨 Game component error:', error, errorInfo);
      addNotification('A game error occurred. The interface will try to recover.', 'error', 8000);
    }}>
      {/* Turn Indicator - HIDDEN: Timer already shown below player name */}
      {/* {(currentPhase === 'bidding' || currentPhase === 'playing') && (
        <TurnIndicator
          isMyTurn={isMyTurn}
          currentPlayerName={currentTurnPlayer?.name}
          timeLeft={timeLeft !== null ? timeLeft : biddingTimeLeft}
        />
      )} */}
      
      <div style={{
        width: '100%',
        minHeight: '100dvh', /* Use full dynamic viewport height for mobile */
        /* ensure the room uses full available height so the table & players can be positioned reliably */
        paddingLeft: '8px',
        paddingRight: '8px',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        position: 'relative',
        fontFamily: 'system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box',
        touchAction: 'manipulation'
      }}>
      {/* AI Engine Indicator - Separate from other elements */}
      <div style={{ position: 'fixed', top: 18, left: 18, zIndex: 100 }}>
        <AIEngineIndicator status={aiEngineStatus} />
      </div>

      {/* Game Status Pill - Positioned below AI indicator to avoid overlap */}
      <div style={{
        position: 'fixed',
        top: 75,
        left: 18,
        zIndex: 100,
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 8,
        padding: '6px 10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap'
      }}>
        {currentPhase === 'bidding' && (
          <span>
            Bidding{gameState?.currentRound?.biddingPhase?.currentBidder === currentPlayer?.id ? ' • Your turn' : ''}
          </span>
        )}
        {currentPhase === 'playing' && (
          <span>
            Trick {((gameState?.currentRound?.playPhase?.completedTricks?.length || 0) + ((gameState?.currentRound?.playPhase?.currentTrick?.cards?.length || 0) > 0 ? 1 : 0))}
          </span>
        )}
        {currentPhase !== 'bidding' && currentPhase !== 'playing' && (
          <span>{currentPhase?.toString?.() || 'Waiting'}</span>
        )}
      </div>

      {/* Auto-Play Toggle Button - Positioned to the right of player score */}
      {currentPhase !== 'waiting_for_players' && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          left: 'calc(50% + 280px)',
          zIndex: 15,
        }}>
          <button
            onClick={handleToggleAutoPlay}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: autoPlayEnabled 
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.95), rgba(16, 185, 129, 0.95))'
                : 'linear-gradient(135deg, rgba(100, 116, 139, 0.95), rgba(71, 85, 105, 0.95))',
              border: `2px solid ${autoPlayEnabled ? '#22c55e' : '#64748b'}`,
              borderRadius: '12px',
              color: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
            }}
            title={autoPlayEnabled ? 'AI is making moves for you' : 'Enable AI to make moves for you'}
          >
            <span style={{ fontSize: '18px' }}>
              {autoPlayEnabled ? '🤖' : '👤'}
            </span>
            <span>
              {autoPlayEnabled ? 'AUTO-PLAY' : 'MANUAL'}
            </span>
            {autoPlayEnabled && (
              <span style={{ fontSize: '0.65rem', fontWeight: 500, opacity: 0.9, marginLeft: '4px' }}>
                (AI)
              </span>
            )}
          </button>
        </div>
      )}
      
      {/* Top-left info panel: Bid, Trump, Partner Cards */}
      <div
        style={{
          position: 'fixed',
          top: '12px',
          left: '12px',
          zIndex: 100,
          /* hidden: redundant info already shown in-center; keep in DOM for quick re-enable */
          display: 'none',
          flexDirection: 'column',
          gap: '0.5rem',
          alignItems: 'flex-start',
          minWidth: '140px',
          maxWidth: '220px',
        }}
      >
        {/* Bid & Trump Info */}
        <div className="info-panel bid-trump-panel" style={{
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '0.6rem 1rem',
          marginBottom: '0.3rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          color: '#facc15',
          fontWeight: 600,
          fontSize: '1.05rem',
          letterSpacing: '0.01em',
        }}>
          <div>🪙 Bid: {gameState?.currentRound?.biddingPhase?.currentBid?.amount ?? '--'}</div>
          <div>
            Trump: {
              gameState?.currentRound?.biddingPhase?.currentBid?.powerHouse
                ? <span style={{ color: getSuitColor(gameState.currentRound.biddingPhase.currentBid.powerHouse) }}>{convertSuitToDisplay(gameState.currentRound.biddingPhase.currentBid.powerHouse)}</span>
                : '--'
            }
          </div>
        </div>
        {/* Partner Cards (multiple) - show from current bid during gameplay */}
        {gameState?.currentRound?.biddingPhase?.currentBid?.partnerCards && 
         gameState.currentRound.biddingPhase.currentBid.partnerCards.length > 0 && (
          <div className="info-panel partner-cards-panel" style={{
            background: 'rgba(99,102,241,0.13)',
            borderRadius: '12px',
            padding: '0.5rem 1rem',
            marginBottom: '0.3rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            color: '#a78bfa',
            fontWeight: 500,
            fontSize: '0.98rem',
            minWidth: '120px',
          }}>
            <div style={{ fontWeight: 600, marginBottom: '0.2rem', color: '#fbbf24' }}>
              Partner Card{gameState.currentRound.biddingPhase.currentBid.partnerCards.length > 1 ? 's' : ''}:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              {gameState.currentRound.biddingPhase.currentBid.partnerCards.map((card: any, idx: number) => (
                <div key={`${card.suit}-${card.rank}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.95rem' }}>
                  <span style={{ color: getSuitColor(card.suit), fontWeight: 700 }}>{convertSuitToDisplay(card.suit)}</span>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{card.rank}</span>
                  {card.desiredPlayOrder && (
                    <span style={{ color: '#818cf8', fontSize: '0.85rem', fontWeight: 500 }}>
                      ({card.desiredPlayOrder === 1 ? '1st' : '2nd'} play)
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
     
      {/* Error message display */}
      {connectionError && (
        <div style={{
          position: 'relative',
          margin: '20px auto',
          background: 'rgba(220, 53, 69, 0.95)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          border: '2px solid #dc3545',
          boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
          zIndex: 1000,
          fontWeight: 'bold',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.1rem', marginBottom: '4px' }}>⚠️ Error</div>
          <div style={{ fontSize: '0.9rem' }}>{connectionError}</div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '8px',
              background: '#ffffff',
              color: '#dc3545',
              border: 'none',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            Reload Page
          </button>
        </div>
      )}

      {/* Mobile Game Room Overlay for waiting phase */}
      {currentPhase === 'waiting_for_players' && (
        <div className="mobile-game-room-overlay">
          <div className="game-room-header">
            <span className="game-room-icon">🎮</span>
            <h2 className="game-room-title">Game Room</h2>
            <div className="game-room-id">Room: {frontendRoomId}</div>
          </div>
          
          <div className="game-room-content">
            <div className="game-room-status">
              Phase: waiting_for_players | Players: {players.length}/{gameState?.maxPlayers || 4}
            </div>
            
            <div className="game-room-share">
              <span>Share Room ID</span>
              <div className="game-room-id-box">{frontendRoomId}</div>
              <button 
                className="btn-copy-room-id" 
                onClick={() => {
                  // Use full room ID from URL parameter instead of shortened display version
                  const fullRoomId = frontendRoomId || '';
                  navigator.clipboard.writeText(fullRoomId).then(() => {
                    addNotification('Full room ID copied to clipboard! Share this with friends to join.', 'success');
                  }).catch(() => {
                    addNotification('Failed to copy room ID. Please try again.', 'error');
                  });
                }}
              >
                📋 Copy Room ID
              </button>
            </div>
            
            <div className="game-room-actions">
              <button 
                className="btn-leave-room"
                onClick={() => {
                  disconnect();
                  setTimeout(() => {
                    window.location.href = '/rooms';
                  }, 500);
                }}
              >
                🚪 Leave Room
              </button>
              <button 
                className="btn-ready-up" 
                onClick={() => setReady(true)}
              >
                ✅ Ready Up
              </button>
              {players.length < (gameState?.maxPlayers || 4) && (
                <>
                  <button 
                    className="btn-add-ai"
                    onClick={() => {
                      if (addAIPlayer) {
                        addAIPlayer();
                      }
                    }}
                  >
                    🤖 Add AI
                  </button>
                  <button 
                    className="btn-fill-room"
                    onClick={() => {
                      try {
                        if (requestAIPlayers) {
                          requestAIPlayers();
                          addNotification('Filling room with AI players...', 'success');
                        } else if (addAIPlayer) {
                          const aiToAdd = (gameState?.maxPlayers || 4) - players.length;
                          // Add AI players with delay to ensure proper state updates
                          for (let i = 0; i < aiToAdd; i++) {
                            setTimeout(() => addAIPlayer(), i * 100);
                          }
                        }
                      } catch (error) {
                        console.error('Error requesting AI players:', error);
                        addNotification('Failed to add AI players', 'error');
                      }
                    }}
                  >
                    ⚡ Fill Room ({(gameState?.maxPlayers || 4) - players.length} needed)
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Game Table - Dynamic sizing based on player count */}
      {(() => {
        const tableDims = getTableDimensions(players.length);
        return (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${tableDims.width}%`,
            height: `${tableDims.height}%`,
            background: 'radial-gradient(ellipse at center, #0d4f3c 0%, #064032 70%, #053428 100%)',
            borderRadius: '50%',
            border: '6px solid #d4af37',
            boxShadow: 'inset 0 0 50px rgba(0,0,0,0.4), 0 0 35px rgba(212,175,55,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'width 0.3s ease, height 0.3s ease' // Smooth size transitions when players join/leave
          }}>
            {/* Center Area - Modern Glass Morphism */}
            <div style={{
              color: 'white',
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3))',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              padding: '0.5rem',
              borderRadius: '24px',
              border: '2px solid rgba(212, 175, 55, 0.4)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(212, 175, 55, 0.1) inset',
              width: '55%',
              height: '82%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              overflow: 'auto',
              gap: '0.5rem'
            }}>
          {/* Room info - only show when less than 4 players or during waiting */}
          {(players.length < (gameState?.maxPlayers || 4) || currentPhase === 'waiting_for_players') && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: '1rem'
            }}>
              {/* Game Room Title */}
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, #d4af37 0%, #ffd700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 2px 8px rgba(212, 175, 55, 0.3)',
                letterSpacing: '1px'
              }}>
                🎮 Game Room
              </div>
              
              {/* Room ID Badge */}
              <div style={{ 
                fontSize: '1.1rem', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.25), rgba(255, 215, 0, 0.15))',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                padding: '0.8rem 1.2rem',
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.4)',
                boxShadow: '0 4px 16px rgba(212, 175, 55, 0.2), 0 0 0 1px rgba(255, 215, 0, 0.1) inset',
                color: '#ffd700',
                letterSpacing: '0.5px'
              }}>
                Room: {roomInfo?.roomId || 'Connecting...'}
              </div>
              
              {/* Phase and Player Count */}
              <div style={{ 
                fontSize: '0.95rem', 
                fontWeight: '600',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                padding: '0.6rem 1rem',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#e5e7eb',
                letterSpacing: '0.3px'
              }}>
                Phase: {currentPhase} | Players: {players.length}/{gameState?.maxPlayers || 4}
              </div>
              
              {/* Share Room Section */}
              {roomInfo?.roomId && (
                <div style={{ 
                  fontSize: '0.85rem', 
                  padding: '1rem 1.2rem',
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(255, 215, 0, 0.1))',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  boxShadow: '0 4px 16px rgba(212, 175, 55, 0.15), 0 0 0 1px rgba(255, 215, 0, 0.05) inset',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.7rem',
                  width: '90%',
                  maxWidth: '350px'
                }}>
                  <span style={{ 
                    color: '#ffd700', 
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    letterSpacing: '0.5px'
                  }}>
                    📤 Share Room ID
                  </span>
                  <span style={{ 
                    fontSize: '1rem', 
                    fontWeight: '700',
                    background: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    letterSpacing: '1.5px',
                    color: '#ffd700',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    fontFamily: 'monospace'
                  }}>
                    {frontendRoomId}
                  </span>
                  <button
                    onClick={() => {
                      // Use full room ID from URL parameter
                      const fullRoomId = frontendRoomId || '';
                      navigator.clipboard.writeText(fullRoomId).then(() => {
                        addNotification('Full room ID copied to clipboard! Share this with friends to join.', 'success');
                      }).catch(() => {
                        addNotification('Failed to copy room ID. Please try again.', 'error');
                      });
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #d4af37, #ffd700)',
                      color: '#1a1a2e',
                      border: 'none',
                      padding: '0.6rem 1.2rem',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
                      letterSpacing: '0.5px'
                    }}
                    onMouseOver={(e) => {
                      (e.target as HTMLElement).style.transform = 'translateY(-2px)';
                      (e.target as HTMLElement).style.boxShadow = '0 6px 16px rgba(212, 175, 55, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      (e.target as HTMLElement).style.transform = 'translateY(0)';
                      (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
                    }}
                    title="Copy Room ID"
                  >
                    📋 Copy Room ID
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Game status when 4 players present */}
          {players.length >= 4 && currentPhase !== 'waiting_for_players' && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.8rem',
              width: '100%',
              /*height: '100%',*/
              alignItems: 'center',
              padding: '0.5rem'
            }}>
              {/* Phase Header - Modern Glass Design */}
              <div style={{ 
                fontSize: '1.2rem', 
                fontWeight: '700',
                background: currentPhase === 'bidding' 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.15))'
                  : currentPhase === 'playing'
                    ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.25), rgba(67, 56, 202, 0.15))'
                    : 'linear-gradient(135deg, rgba(251, 191, 36, 0.25), rgba(245, 158, 11, 0.15))',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                padding: '0.3rem 0.2rem',
                borderRadius: '16px',
                border: currentPhase === 'bidding'
                  ? '1px solid rgba(59, 130, 246, 0.4)'
                  : currentPhase === 'playing'
                    ? '1px solid rgba(79, 70, 229, 0.4)'
                    : '1px solid rgba(251, 191, 36, 0.4)',
                boxShadow: currentPhase === 'bidding'
                  ? '0 4px 16px rgba(59, 130, 246, 0.2), 0 0 0 1px rgba(37, 99, 235, 0.1) inset'
                  : currentPhase === 'playing'
                    ? '0 4px 16px rgba(79, 70, 229, 0.2), 0 0 0 1px rgba(67, 56, 202, 0.1) inset'
                    : '0 4px 16px rgba(251, 191, 36, 0.2), 0 0 0 1px rgba(245, 158, 11, 0.1) inset',
                color: currentPhase === 'bidding'
                  ? '#60a5fa'
                  : currentPhase === 'playing'
                    ? '#a78bfa'
                    : '#fbbf24',
                width: '90%',
                flexShrink: 0,
                letterSpacing: '0.5px'
              }}>
                {currentPhase === 'bidding' ? '🎯 Bidding Phase' : 
                 currentPhase === 'playing' ? '🃏 Playing Phase' : 
                 currentPhase === 'round_end' ? '🏁 Round Complete' : 
                 '🎮 Game Active'}
              </div>
              
              {/* Cards in Play Area - Enhanced Modern Design */}
              {currentPhase === 'playing' && (
                <div style={{
                  padding: '0.4rem 0.5rem 0.3rem 0.5rem',
                  background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.25), rgba(71, 85, 105, 0.15))',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  borderRadius: '16px',
                  border: '2px solid rgba(148, 163, 184, 0.4)',
                  boxShadow: '0 6px 20px rgba(100, 116, 139, 0.25), 0 0 0 1px rgba(148, 163, 184, 0.1) inset',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  width: '95%',
                  maxHeight: '160px',
                  minHeight: '100px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Decorative corner accents */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top left, rgba(148, 163, 184, 0.15) 0%, transparent 50%), radial-gradient(circle at bottom right, rgba(100, 116, 139, 0.15) 0%, transparent 50%)',
                    pointerEvents: 'none'
                  }} />
                  
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#cbd5e1', 
                    marginBottom: '0.3rem',
                    fontWeight: '700',
                    letterSpacing: '0.8px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    zIndex: 1
                  }}>
                    <span style={{
                      background: 'linear-gradient(135deg, #ffd700, #d4af37)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontSize: '0.95rem'
                    }}>🎯</span>
                    <span>CARDS IN PLAY</span>
                  </div>
                  
                  {/* Current trick cards */}
                  {gameState?.currentRound?.playPhase?.currentTrick?.cards && gameState.currentRound.playPhase.currentTrick.cards.length > 0 ? (
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 1
                    }}>
                      {gameState.currentRound.playPhase.currentTrick.cards.map((playedCard: any, cardIndex: number) => {
                        const cardPlayer = players.find(p => p.id === playedCard.playerId);
                        const isWinningCard = gameState?.currentRound?.playPhase?.currentTrick?.winningCard?.id === playedCard.card.id;
                        const trickCompleted = gameState?.currentRound?.playPhase?.currentTrick?.cards?.length === players.length;
                        
                        return (
                          <div key={cardIndex} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            animation: trickCompleted && isWinningCard 
                              ? 'enhancedCardEntry 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), trickWinnerCelebration 2s ease-in-out 1.5s infinite'
                              : 'enhancedCardEntry 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            animationDelay: `${cardIndex * 0.1}s`,
                            animationFillMode: 'backwards',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: trickCompleted && isWinningCard ? 'scale(1.1)' : 'scale(1)',
                            filter: trickCompleted && isWinningCard 
                              ? 'drop-shadow(0 8px 20px rgba(251, 191, 36, 0.6)) brightness(1.2)'
                              : 'none'
                          }}>
                            <div style={{ 
                              position: 'relative',
                              filter: trickCompleted && isWinningCard 
                                ? 'drop-shadow(0 8px 20px rgba(251, 191, 36, 0.6)) brightness(1.2)'
                                : 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25))',
                              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                              cursor: 'pointer',
                              border: trickCompleted && isWinningCard 
                                ? '2px solid rgba(251, 191, 36, 0.8)' 
                                : 'none',
                              borderRadius: '8px',
                              background: trickCompleted && isWinningCard 
                                ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.1))'
                                : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (trickCompleted && isWinningCard) {
                                e.currentTarget.style.transform = 'translateY(-12px) scale(1.15) rotateZ(3deg)';
                                e.currentTarget.style.filter = 'drop-shadow(0 15px 30px rgba(251, 191, 36, 0.8)) brightness(1.3) saturate(1.2)';
                              } else {
                                e.currentTarget.style.transform = 'translateY(-8px) scale(1.08) rotateZ(2deg)';
                                e.currentTarget.style.filter = 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.4)) brightness(1.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (trickCompleted && isWinningCard) {
                                e.currentTarget.style.transform = 'translateY(0) scale(1.1) rotateZ(0deg)';
                                e.currentTarget.style.filter = 'drop-shadow(0 8px 20px rgba(251, 191, 36, 0.6)) brightness(1.2)';
                              } else {
                                e.currentTarget.style.transform = 'translateY(0) scale(1) rotateZ(0deg)';
                                e.currentTarget.style.filter = 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25))';
                              }
                            }}>
                              <PlayingCard
                                suit={playedCard.card.suit}
                                rank={playedCard.card.rank}
                                size="small"
                              />
                            </div>
                            <div style={{ 
                              fontSize: '0.65rem', 
                              color: trickCompleted && isWinningCard ? '#000000' : '#ffffff',
                              fontWeight: '700',
                              textShadow: trickCompleted && isWinningCard 
                                ? '0 1px 2px rgba(0,0,0,0.3)' 
                                : '0 2px 4px rgba(0,0,0,0.8)',
                              background: trickCompleted && isWinningCard
                                ? 'linear-gradient(135deg, #ffd700, #f59e0b)'
                                : 'linear-gradient(135deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3))',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              backdropFilter: 'blur(8px)',
                              WebkitBackdropFilter: 'blur(8px)',
                              border: trickCompleted && isWinningCard
                                ? '1px solid rgba(251, 191, 36, 0.8)'
                                : '1px solid rgba(255, 255, 255, 0.2)',
                              boxShadow: trickCompleted && isWinningCard
                                ? '0 4px 12px rgba(251, 191, 36, 0.6), 0 0 20px rgba(255, 215, 0, 0.4)'
                                : '0 2px 6px rgba(0, 0, 0, 0.3)',
                              animation: trickCompleted && isWinningCard
                                ? 'winnerNameGlow 1.5s ease-in-out infinite'
                                : 'none'
                            }}>
                              {trickCompleted && isWinningCard && '👑 '}{cardPlayer?.name}{trickCompleted && isWinningCard && ' 🏆'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: '#d1d5db',
                      fontStyle: 'italic',
                      fontWeight: '500',
                      padding: '0.75rem',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '10px',
                      border: '1px dashed rgba(255, 215, 0, 0.3)',
                      zIndex: 1
                    }}>
                      ⏳ Waiting for cards...
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Enhanced Bidding Phase Indicator with Turn Highlighting */}
          {currentPhase === 'bidding' && gameState?.currentRound?.biddingPhase && (
            <div style={{ 
              fontSize: '1rem', 
              marginTop: '0.5rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.25), rgba(251, 191, 36, 0.2))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: '16px',
              border: '2px solid #d4af37',
              width: '75%',
              boxShadow: '0 8px 32px rgba(212, 175, 55, 0.3), 0 0 0 1px rgba(255, 215, 0, 0.1) inset'
            }}>
              {/* Phase Header with Pulsing Animation */}
              <div style={{ 
                fontWeight: 'bold', 

                color: '#ffd700', 
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '1.1rem',
                animation: 'biddingPhasePulse 2s ease-in-out infinite',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
              }}>
                <span style={{ fontSize: '1.3rem', animation: 'iconRotate 4s linear infinite' }}>🎯</span>
                <span>BIDDING PHASE</span>
              </div>
              
              {/* Enhanced Current Bidder with Visual Hierarchy */}
              <div style={{ 
                marginBottom: '0.75rem',
                padding: '0.5rem 0.75rem',
                background: gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id 
                  ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.3), rgba(67, 56, 202, 0.3))'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.25))',
                borderRadius: '12px',
                border: `2px solid ${gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id ? 'rgba(79, 70, 229, 0.6)' : 'rgba(59, 130, 246, 0.5)'}`,
                boxShadow: gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id 
                  ? '0 0 20px rgba(79, 70, 229, 0.4), 0 4px 16px rgba(79, 70, 229, 0.3)'
                  : '0 4px 16px rgba(59, 130, 246, 0.25)',
                animation: gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id ? 'yourTurnGlow 2s ease-in-out infinite' : 'none'
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.25rem'
                }}>
                  <div style={{ 
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    color: gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id ? '#a78bfa' : '#60a5fa',
                    letterSpacing: '0.5px'
                  }}>
                    ⏰ CURRENT TURN
                  </div>
                  {gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id && (
                    <div style={{
                      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                      color: '#000',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      fontSize: '0.7rem',
                      fontWeight: '800',
                      animation: 'urgencyPulse 1.5s ease-in-out infinite',
                      boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
                    }}>
                      🔥 YOUR MOVE
                    </div>
                  )}
                </div>
                <div style={{ 
                  fontSize: '1.1rem',
                  fontWeight: '800',
                  color: gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id ? '#a78bfa' : '#ffd700',
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '1.3rem' }}>
                    {gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id ? '👤' : '🧑‍💼'}
                  </span>
                  <span>
                    {players.find(p => p.id === gameState.currentRound?.biddingPhase.currentBidder)?.name || 'Unknown'}
                  </span>
                  {gameState.currentRound?.biddingPhase.currentBidder === currentPlayer?.id && (
                    <div style={{ 
                      background: 'rgba(79, 70, 229, 0.3)',
                      color: '#a78bfa',
                      padding: '2px 6px',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      border: '1px solid rgba(79, 70, 229, 0.5)',
                      animation: 'youBadgeBounce 1s ease-in-out infinite'
                    }}>
                      YOU
                    </div>
                  )}
                </div>
                
                {/* Turn Progress Indicator */}
                <div style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#9ca3af',
                    fontWeight: '600'
                  }}>
                    Turn Progress:
                  </div>
                  <div style={{
                    flex: 1,
                    height: '4px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #a78bfa, #059669)',
                      width: `${((gameState.currentRound?.biddingPhase.biddingHistory?.length || 0) / players.length) * 100}%`,
                      borderRadius: '2px',
                      animation: 'progressGlow 2s ease-in-out infinite',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#d1d5db',
                    fontWeight: '600',
                    minWidth: '35px'
                  }}>
                    {gameState.currentRound?.biddingPhase.biddingHistory?.length || 0}/{players.length}
                  </div>
                </div>
              </div>

              {/* Current Highest Bid */}
              {gameState.currentRound?.biddingPhase.currentBid ? (
                <div style={{ marginBottom: '0.5rem' }}>
                  <div><strong>Highest Bid: </strong>{gameState.currentRound.biddingPhase.currentBid.amount} points</div>
                  <div><strong>Bidder: </strong>
                    <span style={{ color: '#a78bfa', fontWeight: 'bold' }}>
                      {players.find(p => p.id === gameState.currentRound?.biddingPhase.currentBid?.playerId)?.name || 'Unknown'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <strong>Power House: </strong>
                    <span style={{ 
                      color: getSuitColor(gameState.currentRound.biddingPhase.currentBid.powerHouse),
                      fontSize: '1.4rem',
                      fontWeight: 'bold'
                    }}>
                      {convertSuitToDisplay(gameState.currentRound.biddingPhase.currentBid.powerHouse)}
                    </span>
                    <span style={{ fontSize: '0.9rem', color: '#d1d5db' }}>
                      ({gameState.currentRound.biddingPhase.currentBid.powerHouse})
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <strong>Partner Cards: </strong>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {gameState.currentRound.biddingPhase.currentBid.partnerCards?.map((partnerCard: Card, index: number) => {
                        // For partner card display in bidding summary we no longer show per-card copy numbers.
                        // Play-order indicators are shown elsewhere when relevant (duplicate selections or desiredPlayOrder).
                        return (
                        <span key={index} style={{ 
                          color: getSuitColor(partnerCard.suit),
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          border: '1px solid #e5e7eb',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          position: 'relative'
                        }}>
                          {partnerCard.rank}
                          {convertSuitToDisplay(partnerCard.suit)}
                        </span>
                        );
                      }) || (
                        <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#9ca3af' }}>
                          No partner cards selected
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#fbbf24', fontStyle: 'italic' }}>
                  No bids yet - Minimum bid: {getMinBidAmount()} points
                </div>
              )}

              {/* Show passed players */}
              {gameState.currentRound?.biddingPhase.passedPlayers && gameState.currentRound.biddingPhase.passedPlayers.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                  <strong>Passed: </strong>
                  <span style={{ color: '#fca5a5' }}>
                    {gameState.currentRound.biddingPhase.passedPlayers.map(playerId => 
                      players.find(p => p.id === playerId)?.name || 'Unknown'
                    ).join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Show current player during playing phase */}
          {currentPhase === 'playing' && gameState?.currentRound?.playPhase && (
            <div style={{ 
              fontSize: '0.9rem', 
              marginTop: '0rem',
              padding: '0.3rem',
              background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.25), rgba(67, 56, 202, 0.15))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: '10px',
              border: '2px solid rgba(139, 92, 246, 0.4)',
              width: '75%',
              boxShadow: '0 8px 24px rgba(79, 70, 229, 0.2)',
            }}>
              {/* Header with Turn and Trick Info */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.3rem',
                gap: '0.3rem'
              }}>
                {/* PLAYING Label */}
                <div style={{ 
                  fontWeight: '700', 
                  color: '#a78bfa', 
                  fontSize: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  letterSpacing: '0.5px',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '0.85rem' }}>🃏</span>
                  <span>PLAYING</span>
                </div>

                {/* Current Turn - Compact */}
                <div style={{ 
                  padding: '0.25rem 0.4rem',
                  background: gameState.currentRound?.playPhase?.currentPlayer === currentPlayer?.id 
                    ? 'rgba(255, 215, 0, 0.3)' 
                    : 'rgba(59, 130, 246, 0.25)',
                  borderRadius: '6px',
                  border: `1px solid ${gameState.currentRound?.playPhase?.currentPlayer === currentPlayer?.id ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.4)'}`,
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: gameState.currentRound?.playPhase?.currentPlayer === currentPlayer?.id ? '#ffd700' : '#60a5fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flex: 1,
                  justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '0.8rem' }}>
                    {gameState.currentRound?.playPhase?.currentPlayer === currentPlayer?.id ? '🔥' : '👤'}
                  </span>
                  <span>
                    {players.find(p => p.id === gameState.currentRound?.playPhase.currentPlayer)?.name || 'Unknown'}
                  </span>
                  {gameState.currentRound?.playPhase.currentPlayer === currentPlayer?.id && (
                    <span style={{ 
                      fontSize: '0.65rem',
                      background: 'rgba(255, 215, 0, 0.3)',
                      padding: '2px 5px',
                      borderRadius: '4px',
                      fontWeight: '700'
                    }}>
                      YOU
                    </span>
                  )}
                </div>
              </div>

              {/* Trump Suit and Partner Card - Enhanced Side by side */}
              <div style={{ 
                display: 'flex', 
                gap: '0.3rem', 
                width: '100%'
              }}>
                {/* Trump Suit - Enhanced Design - 40% width */}
                {gameState?.currentRound?.biddingPhase?.currentBid?.powerHouse && (
                  <div style={{ 
                    fontSize: '0.9rem',
                    padding: '0.4rem',
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.4), rgba(255,193,7,0.3), rgba(212,175,55,0.25))',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderRadius: '12px',
                    border: '2px solid rgba(212, 175, 55, 0.6)',
                    textAlign: 'center',
                    width: '40%',
                    boxShadow: '0 0 20px rgba(255, 215, 0, 0.3), 0 6px 16px rgba(0, 0, 0, 0.15)',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: 'trumpPulse 3s ease-in-out infinite',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 215, 0, 0.5), 0 8px 20px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.3), 0 6px 16px rgba(0, 0, 0, 0.15)';
                  }}>
                    {/* Shimmer effect */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                      animation: 'shimmer 3s infinite'
                    }} />
                    
                    <div style={{ 
                      fontSize: '0.6rem', 
                      color: '#fbbf24', 
                      marginBottom: '2px',
                      fontWeight: '700',
                      letterSpacing: '0.5px'
                    }}>👑 TRUMP</div>
                    <div style={{ 
                      color: getSuitColor(gameState.currentRound.biddingPhase.currentBid.powerHouse), 
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      textShadow: '0 3px 10px rgba(0,0,0,0.6), 0 0 20px rgba(255,215,0,0.3)',
                      lineHeight: '1',
                      marginBottom: '4px',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                      animation: 'trumpSuitGlow 2s ease-in-out infinite alternate'
                    }}>
                      {convertSuitToDisplay(gameState.currentRound.biddingPhase.currentBid.powerHouse)}
                    </div>
                    <div style={{ 
                      fontSize: '0.55rem', 
                      color: '#d1d5db', 
                      textTransform: 'capitalize',
                      fontWeight: '600'
                    }}>
                      {gameState.currentRound.biddingPhase.currentBid.powerHouse}
                    </div>
                  </div>
                )}

                {/* Partner Cards - Enhanced Design - 60% width */}
                {gameState?.currentRound?.biddingPhase?.currentBid?.partnerCards && 
                 gameState.currentRound.biddingPhase.currentBid.partnerCards.length > 0 && (
                  <div style={{ 
                    fontSize: '0.9rem',
                    padding: '0.3rem',
                    background: gameState.currentRound.playPhase?.revealedPartners && 
                               gameState.currentRound.playPhase.revealedPartners.length > 0 ? 
                      'linear-gradient(135deg, rgba(79, 70, 229, 0.35), rgba(79, 70, 229, 0.25))' : 
                      'linear-gradient(135deg, rgba(139, 92, 246, 0.35), rgba(139, 92, 246, 0.25))',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    border: `2px solid ${gameState.currentRound.playPhase?.revealedPartners && 
                                        gameState.currentRound.playPhase.revealedPartners.length > 0 ? 
                                        'rgba(79, 70, 229, 0.5)' : 'rgba(139, 92, 246, 0.5)'}`,
                    textAlign: 'center',
                    width: '60%',
                    position: 'relative',
                    boxShadow: gameState.currentRound.playPhase?.revealedPartners && 
                               gameState.currentRound.playPhase.revealedPartners.length > 0 ? 
                      '0 4px 12px rgba(79, 70, 229, 0.2)' : 
                      '0 4px 12px rgba(139, 92, 246, 0.2)'
                  }}>
                    <div style={{ 
                      fontSize: '0.55rem', 
                      color: gameState.currentRound.playPhase?.revealedPartners && 
                             gameState.currentRound.playPhase.revealedPartners.length > 0 ? 
                        '#a78bfa' : '#a78bfa',
                      marginBottom: '2px',
                      fontWeight: '700',
                      letterSpacing: '0.3px'
                    }}>
                      {gameState.currentRound.playPhase?.revealedPartners && 
                       gameState.currentRound.playPhase.revealedPartners.length > 0 ? '✅' : '🤝'} {gameState.currentRound.playPhase?.revealedPartners && 
                               gameState.currentRound.playPhase.revealedPartners.length > 0 ? 
                               `${gameState.currentRound.playPhase.revealedPartners.length} REV` : 'HIDDEN'}
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                      {(() => {
                        const allPartnerCards = gameState?.currentRound?.biddingPhase?.currentBid?.partnerCards || [];
                        const isDoubleDeck = (gameState?.players && gameState.players.length >= 7) || partnershipConfig === '6-2deck';
                        
                        // For single deck games (4-6 players EXCEPT 6-2deck), show only unique partner cards
                        // For double deck games (7+ players OR 6-2deck), show all partner cards including duplicates
                        const cardsToDisplay = isDoubleDeck ? allPartnerCards : 
                          allPartnerCards.filter((card: Card, index: number, arr: Card[]) =>
                            index === arr.findIndex(c => c.suit === card.suit && c.rank === card.rank)
                          );
                        
                        return cardsToDisplay.map((partnerCard: Card, index: number) => {
                        // Debug: Log partner card structure
                        dbg('multiplayer', `🎯 Partner card ${index}:`, partnerCard);
                        
                        const allPartnerCards = gameState.currentRound?.biddingPhase?.currentBid?.partnerCards || [];
                        
                        // PLAYING PHASE: Use actual play order from game state
                        const actualPlayData = calculateActualPlayOrder(partnerCard, gameState);
                        const timesActuallyPlayed = actualPlayData.timesPlayed;
                        
                        // Count how many times this exact card (suit+rank) appears in partner selection
                        const sameCards = allPartnerCards.filter(
                          (pc: Card) => pc.suit === partnerCard.suit && pc.rank === partnerCard.rank
                        );
                        
                        // Calculate which occurrence this is in bidding phase (1st, 2nd, etc.)
                        const biddingOccurrenceNumber = allPartnerCards.slice(0, index + 1)
                          .filter((pc: Card) => pc.suit === partnerCard.suit && pc.rank === partnerCard.rank).length;
                        
                        // CRITICAL FIX: Always show play order for double-deck games (7+ players)
                        // In double deck, even single partner cards need play order indicators
                        // since multiple copies exist in the deck
                        const isDoubleDeck = (gameState.players && gameState.players.length >= 7) || partnershipConfig === '6-2deck';
                        // Show play order indicator if there is an explicit desiredPlayOrder
                        // or when there are duplicate partner card selections or double-deck copy numbers
                        const showPlayOrderNumber = sameCards.length > 1 || (typeof partnerCard.desiredPlayOrder === 'number') || isDoubleDeck;
                        
                        // During playing phase, show actual play status instead of bidding occurrence
                        let displayText = '';
                        let displayColor = '';
                        
                        if (timesActuallyPlayed > 0) {
                          // Card has been played - show which play order it was
                          if (timesActuallyPlayed === 1) {
                            displayText = '1st';
                            displayColor = '#a78bfa'; // Green - partner revealed
                          } else if (timesActuallyPlayed === 2) {
                            displayText = '2nd';
                            displayColor = '#3b82f6'; // Blue - second play
                          } else if (timesActuallyPlayed === 3) {
                            displayText = '3rd';
                            displayColor = '#f59e0b'; // Yellow - third play
                          } else {
                            displayText = `${timesActuallyPlayed}th`;
                            displayColor = '#ef4444'; // Red - fourth+ play
                          }
                          } else {
                          // Card hasn't been played yet - prefer explicit desiredPlayOrder (may be 1 or 2),
                          // then copyNumber for double-deck, then fall back to bidding occurrence
                          let expectedOrder: number;
                          if (typeof partnerCard.desiredPlayOrder === 'number') {
                            expectedOrder = partnerCard.desiredPlayOrder;
                          } else {
                            // Prefer explicit desiredPlayOrder when provided, otherwise fall back to bidding occurrence
                            expectedOrder = biddingOccurrenceNumber;
                          }
                          displayText = expectedOrder === 1 ? '1st' : 
                                       expectedOrder === 2 ? '2nd' : 
                                       expectedOrder === 3 ? '3rd' : 
                                       `${expectedOrder}th`;
                          displayColor = 'rgba(156, 163, 175, 0.8)'; // Gray - not played yet
                        }
                        
                        dbg('multiplayer', `🎯 Playing phase card ${index}: ${partnerCard.rank}${convertSuitToDisplay(partnerCard.suit)}, played ${timesActuallyPlayed} times, showOrder: ${showPlayOrderNumber}, display: ${displayText}${partnerCard.desiredPlayOrder ? `, desiredPlayOrder: ${partnerCard.desiredPlayOrder}` : ''}`);
                        dbg('multiplayer', `📊 Partner card debug: isDoubleDeck=${isDoubleDeck}, sameCards=${sameCards.length}, desiredPlayOrder=${partnerCard.desiredPlayOrder}, biddingOccurrence=${biddingOccurrenceNumber}`);
                        
                        return (
                        <div key={index} style={{ 
                          position: 'relative',
                          color: getSuitColor(partnerCard.suit), 
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                          border: '2px solid rgba(255,255,255,0.4)',
                          borderRadius: '8px',
                          padding: '6px 10px',
                          background: timesActuallyPlayed > 0 
                            ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.3), rgba(79, 70, 229, 0.2))'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0.15))',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: '28px',
                          minHeight: '20px',
                          boxShadow: timesActuallyPlayed > 0 
                            ? '0 2px 8px rgba(79, 70, 229, 0.3)' 
                            : '0 2px 6px rgba(0, 0, 0, 0.2)',
                          transition: 'all 0.2s ease',
                          backdropFilter: 'blur(4px)',
                          WebkitBackdropFilter: 'blur(4px)'
                        }}>
                          {partnerCard.rank}
                          {convertSuitToDisplay(partnerCard.suit)}
                          
                          {/* Show play order indicator for duplicate partner cards */}
                          {showPlayOrderNumber && (
                            <div style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: displayColor,
                              color: 'white',
                              borderRadius: '6px',
                              padding: '2px 6px',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              border: '2px solid white',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                              minWidth: '24px',
                              height: '18px'
                            }}>
                              {displayText}
                            </div>
                          )}
                        </div>
                        );
                        });
                      })()}
                    </div>
                    {gameState.currentRound.playPhase?.revealedPartners && 
                     gameState.currentRound.playPhase.revealedPartners.length > 0 && (
                      <div style={{ 
                        fontSize: '0.65rem', 
                        color: '#a78bfa', 
                        fontWeight: '700', 
                        marginTop: '5px',
                        padding: '3px 6px',
                        background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.3), rgba(79, 70, 229, 0.2))',
                        borderRadius: '5px',
                        border: '1px solid rgba(79, 70, 229, 0.4)',
                        boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3), 0 0 0 1px rgba(79, 70, 229, 0.1) inset',
                        animation: 'partnerRevealPulse 2s ease-in-out infinite alternate',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {/* Shimmer effect for revealed partners */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                          animation: 'shimmer 3s infinite'
                        }} />
                        
                        <span style={{ position: 'relative', zIndex: 1 }}>
                          ✨ Revealed: {gameState.currentRound.playPhase.revealedPartners.map((partnerId: string, index: number) => (
                            <span 
                              key={partnerId}
                              style={{
                                display: 'inline-block',
                                animation: `partnerNameFadeIn 0.5s ease-out ${index * 0.2}s both`,
                                background: 'rgba(79, 70, 229, 0.2)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                marginLeft: index > 0 ? '4px' : '0',
                                border: '1px solid rgba(79, 70, 229, 0.3)'
                              }}
                            >
                              {players.find(p => p.id === partnerId)?.name || 'Unknown'}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
        );
      })()}

      {/* Players - Reordered so current player perspective is from bottom */}
      {(() => {
        // Early return if no players or currentPlayer yet (initial loading state)
        if (!currentPlayer || !players || players.length === 0) {
          return null; // Silently wait for game state to load
        }
        
        // Enhanced debugging for player filtering
        console.log('🔍 Player Filtering Debug:', {
          currentPlayerId: currentPlayer?.id,
          currentPlayerName: currentPlayer?.name,
          allPlayers: players.map(p => ({ id: p.id, name: p.name })),
          totalPlayers: players.length
        });
        
        // Find current player's index in the players array
        const currentPlayerIndex = players.findIndex(p => p.id === currentPlayer?.id);
        
        console.log('🔍 Current Player Index:', currentPlayerIndex);
        
        if (currentPlayerIndex < 0) {
          // Current player not found, render all players normally
          console.warn('⚠️ Current player not found in players array, using fallback rendering');
          return players.map(() => {
            // Fallback - return empty for now  
            return null; // Placeholder
          });
        }

        // Create reordered array: current player would be at position 0 (bottom),
        // followed by other players in clockwise order
        // If current player is at index 2 in [P0, P1, P2, P3, P4, P5]
        // Reordered: [P2, P3, P4, P5, P0, P1] - so P3 is next clockwise from P2
        const reorderedPlayers = [
          ...players.slice(currentPlayerIndex),
          ...players.slice(0, currentPlayerIndex)
        ];

        console.log('🔍 Reordered Players:', reorderedPlayers.map(p => ({ id: p.id, name: p.name })));

        // Filter out current player (index 0) - they don't get a player box
        const playersToRender = reorderedPlayers.slice(1);
        
        console.log('🔍 Players to Render (after removing current player):', playersToRender.map(p => ({ id: p.id, name: p.name })));

        return playersToRender.map((player, arrayIndex) => {
        // Position players clockwise starting from the position AFTER current player (who is at bottom/position 0)
        // Current player is at position 0 (bottom, not rendered)
        // Other players are at positions 1, 2, 3... (clockwise from current player)
        const positionIndex = arrayIndex + 1; // Start from position 1 (next clockwise from bottom)
        const position = getPlayerPosition(positionIndex, players.length); // Use TOTAL player count
        
        // Debug logging for player positioning
        console.log(`🎯 Player positioning:`, {
          playerName: player.name,
          arrayIndex,
          positionIndex,
          totalPlayers: players.length,
          position: position,
          actualValues: {
            left: position.left,
            top: position.top,
            right: position.right,
            transform: position.transform
          }
        });
        
        const isCurrentUser = player.id === currentPlayer?.id;
        
        // Get room leaderboard data for this player
        const hasRoomScores = roomScores?.scores && roomScores.scores.length > 0;
        
        // Find player's score entry in room leaderboard (try both id and name matching)
        const playerScoreEntry = hasRoomScores 
          ? roomScores.scores.find((s: any) => 
              s.playerId === player.id || 
              s.playerName === player.name ||
              s.userId === player.id
            )
          : null;
        
        // Calculate ranking (1-indexed) - sort all players and find this player's rank
        const sortedScores = hasRoomScores 
          ? [...roomScores.scores].sort((a: any, b: any) => b.cumulativeScore - a.cumulativeScore)
          : [];
        
        let playerRanking = 1; // Default rank
        let roomCumulativeScore = 0; // Default score
        
        if (hasRoomScores) {
          if (playerScoreEntry) {
            // Found exact match - use that data
            roomCumulativeScore = playerScoreEntry.cumulativeScore || 0;
            playerRanking = sortedScores.findIndex((s: any) => 
              s.playerId === playerScoreEntry.playerId || 
              s.playerName === playerScoreEntry.playerName
            ) + 1;
          } else {
            // No match found - player might be new, rank them by their total
            // All players with 0 score are tied at last place
            const playersWithScores = sortedScores.filter((s: any) => s.cumulativeScore > 0);
            playerRanking = playersWithScores.length === 0 ? 1 : sortedScores.length + 1;
          }
        }
        
        // Rank badge (medals for top 3, number for others)
        const rankBadge = playerRanking === 1 ? '🥇' 
          : playerRanking === 2 ? '🥈' 
          : playerRanking === 3 ? '🥉' 
          : `${playerRanking}.`;
          
        // Enhanced rank styling
        const getRankStyles = () => {
          switch (playerRanking) {
            case 1:
              return {
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.4), rgba(255, 193, 7, 0.4))',
                border: '1px solid rgba(255, 215, 0, 0.6)',
                color: '#ffd700',
                boxShadow: '0 0 20px rgba(255, 215, 0, 0.4), 0 4px 16px rgba(255, 215, 0, 0.3)',
                animation: 'goldShimmer 3s ease-in-out infinite'
              };
            case 2:
              return {
                background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.4), rgba(169, 169, 169, 0.4))',
                border: '1px solid rgba(192, 192, 192, 0.6)',
                color: '#c0c0c0',
                boxShadow: '0 0 15px rgba(192, 192, 192, 0.3), 0 4px 16px rgba(192, 192, 192, 0.25)',
                animation: 'silverGlow 3s ease-in-out infinite'
              };
            case 3:
              return {
                background: 'linear-gradient(135deg, rgba(205, 127, 50, 0.4), rgba(184, 115, 51, 0.4))',
                border: '1px solid rgba(205, 127, 50, 0.6)',
                color: '#cd7f32',
                boxShadow: '0 0 15px rgba(205, 127, 50, 0.3), 0 4px 16px rgba(205, 127, 50, 0.25)',
                animation: 'bronzeWarm 3s ease-in-out infinite'
              };
            default:
              return {
                background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.3), rgba(75, 85, 99, 0.3))',
                border: '1px solid rgba(107, 114, 128, 0.5)',
                color: '#9ca3af',
                boxShadow: '0 4px 16px rgba(107, 114, 128, 0.25)'
              };
          }
        };
        
        // Current game score (for corner bubble)
        const currentGameScore = gameState?.currentRound?.scoring?.individualScores?.[player.id] || 0;
        const hasCurrentGameScore = gameState?.currentRound?.scoring?.individualScores?.[player.id] !== undefined;
        
        // Determine if player is on left or right side based on position
        const isLeftSide = position.left !== undefined && parseFloat(position.left) < 50;
        const isRightSide = position.right !== undefined || (position.left !== undefined && parseFloat(position.left) > 50);
        
        return (
          <div
            key={player.id}
            style={{
              position: 'absolute',
              ...position,
              background: isCurrentUser 
                ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.25), rgba(67, 56, 202, 0.25))'
                : 'linear-gradient(135deg, rgba(55, 65, 81, 0.25), rgba(75, 85, 99, 0.25))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              color: 'white',
              padding: players.length >= 6 ? '0.5rem 0.6rem' : '0.6rem 0.8rem', // Smaller padding for 6+ players
              borderRadius: players.length >= 6 ? '12px' : '16px', // Slightly smaller border radius for 6+ players
              border: isCurrentUser 
                ? '2px solid rgba(212, 175, 55, 0.6)' 
                : '2px solid rgba(255, 255, 255, 0.15)',
              boxShadow: isCurrentUser 
                ? '0 8px 32px rgba(212, 175, 55, 0.2), 0 0 0 1px rgba(79, 70, 229, 0.1) inset' 
                : '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
              textAlign: 'center',
              minWidth: players.length >= 6 ? '100px' : '120px', // Smaller boxes for 6+ players
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: players.length >= 6 ? '0.3rem' : '0.4rem', // Tighter spacing for 6+ players
              transform: isCurrentUser ? position.transform + ' scale(1.08)' : position.transform,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {/* Card Display - Modern glass morphism style */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              padding: players.length >= 6 ? '0.2rem 0.4rem' : '0.3rem 0.5rem',
              minWidth: players.length >= 6 ? '80px' : '95px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <span style={{ fontSize: '1rem', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}>🃏</span>
              <span style={{ 
                fontSize: '0.75rem', 
                fontWeight: '600',
                color: '#ffffff',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
              }}>
                {player.hand?.length || 0} cards
              </span>
            </div>

            {/* Player Name - Modern typography */}
            <div style={{ 
              fontWeight: '700', 
              fontSize: isCurrentUser ? '0.85rem' : '0.8rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              lineHeight: '1.2',
              letterSpacing: '0.3px'
            }}>
              {getAIPlayerDisplayName(player.name, player.isAI || false, (player as any).aiEngineStatus)}
              {/* Bid Winner Indicator */}
              {gameState?.currentRound?.biddingPhase?.currentBid?.playerId === player.id && (
                <div style={{ 
                  fontSize: '0.55rem', 
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(37, 99, 235, 0.4))',
                  color: '#3b82f6',
                  fontWeight: '700',
                  marginTop: '2px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  letterSpacing: '0.3px',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}>
                  🎯 BID WINNER
                </div>
              )}
              {isCurrentUser && (
                <div style={{ 
                  fontSize: '0.6rem', 
                  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(245, 158, 11, 0.3))',
                  color: '#fbbf24',
                  fontWeight: '700',
                  marginTop: '3px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  border: '1px solid rgba(251, 191, 36, 0.4)',
                  letterSpacing: '0.5px',
                  animation: 'youIndicatorGlow 2s ease-in-out infinite',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}>
                  YOU
                </div>
              )}
            </div>
            
            {/* Ready status - ONLY show during waiting phase, hidden during gameplay */}
            {player.isReady && currentPhase === 'waiting_for_players' && (
              <div style={{ 
                fontSize: '0.65rem', 
                background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.3), rgba(67, 56, 202, 0.3))',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                color: '#a78bfa',
                padding: '0.2rem 0.5rem',
                borderRadius: '6px',
                fontWeight: '600',
                border: '1px solid rgba(79, 70, 229, 0.3)',
                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)',
                letterSpacing: '0.5px'
              }}>
                ✅ READY
              </div>
            )}

            {/* Enhanced Bidding Turn Indicator - Show during bidding phase */}
            {currentPhase === 'bidding' && gameState?.currentRound?.biddingPhase?.currentBidder && (
              <div style={{ 
                fontSize: '0.65rem', 
                background: gameState.currentRound.biddingPhase.currentBidder === player.id
                  ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.4), rgba(245, 158, 11, 0.4))'
                  : gameState.currentRound.biddingPhase.passedPlayers?.includes(player.id)
                    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.25))',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                color: gameState.currentRound.biddingPhase.currentBidder === player.id
                  ? '#fbbf24'
                  : gameState.currentRound.biddingPhase.passedPlayers?.includes(player.id)
                    ? '#ef4444'
                    : '#60a5fa',
                padding: '0.2rem 0.5rem',
                borderRadius: '6px',
                fontWeight: '700',
                border: `1px solid ${gameState.currentRound.biddingPhase.currentBidder === player.id
                  ? 'rgba(251, 191, 36, 0.6)'
                  : gameState.currentRound.biddingPhase.passedPlayers?.includes(player.id)
                    ? 'rgba(239, 68, 68, 0.5)'
                    : 'rgba(59, 130, 246, 0.4)'
                }`,
                boxShadow: gameState.currentRound.biddingPhase.currentBidder === player.id
                  ? '0 0 15px rgba(251, 191, 36, 0.4), 0 2px 8px rgba(251, 191, 36, 0.3)'
                  : gameState.currentRound.biddingPhase.passedPlayers?.includes(player.id)
                    ? '0 2px 8px rgba(239, 68, 68, 0.2)'
                    : '0 2px 8px rgba(59, 130, 246, 0.2)',
                animation: gameState.currentRound.biddingPhase.currentBidder === player.id 
                  ? 'urgencyPulse 1.5s ease-in-out infinite'
                  : 'none',
                letterSpacing: '0.5px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
              }}>
                {gameState.currentRound.biddingPhase.currentBidder === player.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>🎯 BIDDING</span>
                    {timerActive && (
                      <span style={{
                        fontSize: '0.55rem',
                        background: pressureLevel === 'high' 
                          ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                          : pressureLevel === 'medium'
                          ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                          : 'linear-gradient(135deg, #a78bfa, #059669)',
                        color: 'white',
                        padding: '1px 4px',
                        borderRadius: '4px',
                        fontWeight: '700',
                        minWidth: '20px',
                        textAlign: 'center',
                        animation: pressureLevel === 'high' ? 'youBadgeBounce 0.5s infinite' : 'none'
                      }}>
                        {biddingTimeLeft}s
                      </span>
                    )}
                  </div>
                ) : gameState.currentRound.biddingPhase.passedPlayers?.includes(player.id) ? (
                  <span>🚫 PASSED</span>
                ) : (
                  <span>⏳ WAITING</span>
                )}
              </div>
            )}
            
            {/* Enhanced Turn indicator with pulsing animation */}
            {currentPhase === 'playing' && 
             gameState?.currentRound?.playPhase?.currentPlayer === player.id && (
              <div style={{ 
                fontSize: '0.65rem', 
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.4), rgba(245, 158, 11, 0.4))',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                color: '#fbbf24',
                padding: '0.2rem 0.5rem',
                borderRadius: '6px',
                fontWeight: '600',
                border: '1px solid rgba(251, 191, 36, 0.4)',
                boxShadow: '0 0 20px rgba(251, 191, 36, 0.4), 0 2px 8px rgba(251, 191, 36, 0.3)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                🎯 YOUR TURN
                {timeLeft !== null && timeLeft >= 0 && (
                  <span style={{ 
                    marginLeft: '4px',
                    fontWeight: '700',
                    fontSize: '0.7rem',
                    color: timeLeft <= 3 ? '#ef4444' : timeLeft <= 5 ? '#f97316' : '#fbbf24',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                  }}>
                    ({timeLeft}s)
                  </span>
                )}
              </div>
            )}
            
            {/* Connection Status Indicator - Bright Neon Online/Offline Dot */}
            <div style={{
              position: 'absolute',
              right: isLeftSide || isCurrentUser || (!isLeftSide && !isRightSide) ? '-10px' : undefined,
              left: isRightSide && !isCurrentUser ? '-10px' : undefined,
              top: '6px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#00ff41', // Bright neon green for online
              border: '2px solid rgba(0, 255, 65, 0.3)',
              boxShadow: '0 0 12px rgba(0, 255, 65, 0.8), 0 0 24px rgba(0, 255, 65, 0.4), inset 0 0 8px rgba(0, 255, 65, 0.6)',
              animation: 'neonPulse 2s ease-in-out infinite',
              zIndex: 15
            }} title={`${player.name} is online`} />
            
            {/* Progressive Ranking Badge - Dynamic size based on actual ranking */}
            <div style={{
              position: 'absolute',
              left: isLeftSide || (!isLeftSide && !isRightSide) ? '-35px' : undefined,
              right: isRightSide ? '-35px' : undefined,
              top: '-20px',
              fontSize: rankBadge === '🥇' ? '1.5rem' : rankBadge === '🥈' ? '1.3rem' : rankBadge === '🥉' ? '1rem' : '0.8rem',
              ...getRankStyles(),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              padding: rankBadge === '🥇' ? '0.45rem 0.65rem' : rankBadge === '🥈' ? '0.4rem 0.6rem' : rankBadge === '🥉' ? '0.35rem 0.55rem' : '0.25rem 0.45rem',
              borderRadius: rankBadge === '🥇' ? '12px' : rankBadge === '🥈' ? '10px' : rankBadge === '🥉' ? '9px' : '7px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: rankBadge === '🥇' ? '50px' : rankBadge === '🥈' ? '46px' : rankBadge === '🥉' ? '42px' : '32px',
              maxWidth: rankBadge === '🥇' ? '54px' : rankBadge === '🥈' ? '50px' : rankBadge === '🥉' ? '46px' : '36px',
              height: rankBadge === '🥇' ? '46px' : rankBadge === '🥈' ? '42px' : rankBadge === '🥉' ? '38px' : '28px',
              zIndex: 10,
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.8)',
              transform: rankBadge === '🥇' ? 'scale(1.05)' : rankBadge === '🥈' ? 'scale(1.02)' : 'scale(1)'
            }}>
              {rankBadge}
            </div>
            
            {/* Compact Room Score - Smaller and positioned better */}
            <div style={{
              position: 'absolute',
              left: isLeftSide || (!isLeftSide && !isRightSide) ? '-35px' : undefined,
              right: isRightSide ? '-35px' : undefined,
              bottom: '-14px',
              fontSize: '0.65rem',
              background: roomCumulativeScore > 0 
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(79, 70, 229, 0.4))'
                : roomCumulativeScore < 0 
                  ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.4), rgba(220, 38, 38, 0.4))'
                  : 'linear-gradient(135deg, rgba(107, 114, 128, 0.4), rgba(75, 85, 99, 0.4))',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: `1px solid ${roomCumulativeScore > 0 ? 'rgba(139, 92, 246, 0.6)' : roomCumulativeScore < 0 ? 'rgba(239, 68, 68, 0.6)' : 'rgba(107, 114, 128, 0.6)'}`,
              color: roomCumulativeScore > 0 ? '#a78bfa' : roomCumulativeScore < 0 ? '#ef4444' : '#9ca3af',
              padding: '0.2rem 0.35rem',
              borderRadius: '6px',
              fontWeight: '700',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: roomCumulativeScore > 0 
                ? '0 4px 16px rgba(139, 92, 246, 0.3), 0 0 0 1px rgba(79, 70, 229, 0.1) inset'
                : roomCumulativeScore < 0
                  ? '0 4px 16px rgba(239, 68, 68, 0.3), 0 0 0 1px rgba(220, 38, 38, 0.1) inset'
                  : '0 4px 16px rgba(107, 114, 128, 0.25), 0 0 0 1px rgba(75, 85, 99, 0.1) inset',
              minWidth: '52px',
              zIndex: 10,
              lineHeight: '1.1',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
            }}>
              <div style={{ fontSize: '0.55rem', opacity: 0.7, marginBottom: '2px', fontWeight: '600', letterSpacing: '0.5px' }}>TOTAL</div>
              <div>{roomCumulativeScore > 0 ? '+' : ''}{roomCumulativeScore}</div>
            </div>
            
            {/* Compact Current Game Score */}
            {hasCurrentGameScore && (
              <div style={{
                position: 'absolute',
                left: isLeftSide || (!isLeftSide && !isRightSide) ? undefined : '-35px',
                right: isRightSide ? undefined : '-35px',
                bottom: '-14px',
                fontSize: '0.65rem',
                background: currentGameScore > 0 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(37, 99, 235, 0.4))' 
                  : currentGameScore < 0 
                    ? 'linear-gradient(135deg, rgba(251, 146, 60, 0.4), rgba(249, 115, 22, 0.4))' 
                    : 'linear-gradient(135deg, rgba(156, 163, 175, 0.4), rgba(107, 114, 128, 0.4))',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: `1px solid ${currentGameScore > 0 ? 'rgba(59, 130, 246, 0.6)' : currentGameScore < 0 ? 'rgba(251, 146, 60, 0.6)' : 'rgba(156, 163, 175, 0.6)'}`,
                color: currentGameScore > 0 ? '#3b82f6' : currentGameScore < 0 ? '#fb923c' : '#9ca3af',
                padding: '0.2rem 0.35rem',
                borderRadius: '6px',
                fontWeight: '700',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: currentGameScore > 0
                  ? '0 4px 16px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(37, 99, 235, 0.1) inset'
                  : currentGameScore < 0
                    ? '0 4px 16px rgba(251, 146, 60, 0.3), 0 0 0 1px rgba(249, 115, 22, 0.1) inset'
                    : '0 4px 16px rgba(156, 163, 175, 0.25), 0 0 0 1px rgba(107, 114, 128, 0.1) inset',
                minWidth: '52px',
                zIndex: 10,
                lineHeight: '1.1',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
              }}>
                <div style={{ fontSize: '0.55rem', opacity: 0.7, marginBottom: '2px', fontWeight: '600', letterSpacing: '0.5px' }}>GAME</div>
                <div>{currentGameScore > 0 ? '+' : ''}{currentGameScore}</div>
              </div>
            )}
          </div>
        );
      });
      })()}

      {/* Current Player Stats - Display below cards */}
      {currentPlayer && (() => {
        // Get current player's stats
        const hasRoomScores = roomScores?.scores && roomScores.scores.length > 0;
        const playerScoreEntry = hasRoomScores 
          ? roomScores.scores.find((s: any) => 
              s.playerId === currentPlayer.id || 
              s.playerName === currentPlayer.name ||
              s.userId === currentPlayer.id
            )
          : null;
        
        const sortedScores = hasRoomScores 
          ? [...roomScores.scores].sort((a: any, b: any) => b.cumulativeScore - a.cumulativeScore)
          : [];
        
        let playerRanking = 1;
        let roomCumulativeScore = 0;
        
        if (hasRoomScores && playerScoreEntry) {
          roomCumulativeScore = playerScoreEntry.cumulativeScore || 0;
          playerRanking = sortedScores.findIndex((s: any) => 
            s.playerId === playerScoreEntry.playerId || 
            s.playerName === playerScoreEntry.playerName
          ) + 1;
        }
        
        const rankBadge = playerRanking === 1 ? '🥇' 
          : playerRanking === 2 ? '🥈' 
          : playerRanking === 3 ? '🥉' 
          : `${playerRanking}.`;
        
        const currentGameScore = gameState?.currentRound?.scoring?.individualScores?.[currentPlayer.id] || 0;
        const hasCurrentGameScore = gameState?.currentRound?.scoring?.individualScores?.[currentPlayer.id] !== undefined;
        
        return (
          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            zIndex: 5
          }}>
            {/* Your Turn Indicator */}
            {gameState?.currentRound?.playPhase?.currentPlayer === currentPlayer.id && currentPhase === 'playing' && (
              <div style={{
                fontSize: '0.8rem',
                padding: '0.4rem 0.8rem',
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(245, 158, 11, 0.3))',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(251, 191, 36, 0.5)',
                borderRadius: '12px',
                color: '#fbbf24',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 0 20px rgba(251, 191, 36, 0.3), 0 4px 12px rgba(0, 0, 0, 0.1)',
                animation: 'yourTurnPulse 2s ease-in-out infinite',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                letterSpacing: '0.5px',
                minWidth: '120px',
                justifyContent: 'center'
              }}>
                🎯 <span>YOUR TURN</span>
                {timeLeft !== null && timeLeft >= 0 && (
                  <span style={{ 
                    marginLeft: '4px',
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    color: timeLeft <= 3 ? '#ef4444' : timeLeft <= 5 ? '#f97316' : '#fbbf24',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                  }}>
                    ({timeLeft}s)
                  </span>
                )}
              </div>
            )}
            
            {/* Ranking Badge - Enhanced with same prominence as other players */}
            <div style={{
              fontSize: rankBadge === '🥇' ? '1.5rem' : rankBadge === '🥈' ? '1.3rem' : rankBadge === '🥉' ? '1rem' : '0.8rem',
              background: playerRanking === 1 
                ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.4), rgba(255, 193, 7, 0.4))'
                : playerRanking === 2
                  ? 'linear-gradient(135deg, rgba(192, 192, 192, 0.4), rgba(169, 169, 169, 0.4))'
                  : playerRanking === 3
                    ? 'linear-gradient(135deg, rgba(205, 127, 50, 0.4), rgba(184, 115, 51, 0.4))'
                    : 'linear-gradient(135deg, rgba(107, 114, 128, 0.3), rgba(75, 85, 99, 0.3))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid ${playerRanking === 1 ? 'rgba(255, 215, 0, 0.6)' : playerRanking === 2 ? 'rgba(192, 192, 192, 0.6)' : playerRanking === 3 ? 'rgba(205, 127, 50, 0.6)' : 'rgba(107, 114, 128, 0.5)'}`,
              color: playerRanking === 1 ? '#ffd700' : playerRanking === 2 ? '#c0c0c0' : playerRanking === 3 ? '#cd7f32' : '#9ca3af',
              padding: rankBadge === '🥇' ? '0.45rem 0.65rem' : rankBadge === '🥈' ? '0.4rem 0.6rem' : rankBadge === '🥉' ? '0.35rem 0.55rem' : '0.3rem 0.5rem',
              borderRadius: rankBadge === '🥇' ? '12px' : rankBadge === '🥈' ? '10px' : rankBadge === '🥉' ? '9px' : '8px',
              fontWeight: '700',
              boxShadow: playerRanking === 1 
                ? '0 0 20px rgba(255, 215, 0, 0.4), 0 4px 16px rgba(255, 215, 0, 0.3)' 
                : playerRanking === 2
                  ? '0 0 15px rgba(192, 192, 192, 0.3), 0 4px 16px rgba(192, 192, 192, 0.25)'
                  : playerRanking === 3
                    ? '0 0 15px rgba(205, 127, 50, 0.3), 0 4px 16px rgba(205, 127, 50, 0.25)'
                    : '0 4px 16px rgba(107, 114, 128, 0.25)',
              animation: playerRanking === 1 ? 'goldShimmer 3s ease-in-out infinite' : playerRanking === 2 ? 'silverGlow 3s ease-in-out infinite' : playerRanking === 3 ? 'bronzeWarm 3s ease-in-out infinite' : 'none',
              minWidth: rankBadge === '🥇' ? '50px' : rankBadge === '🥈' ? '46px' : rankBadge === '🥉' ? '42px' : '38px',
              textAlign: 'center',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.8)',
              transform: rankBadge === '🥇' ? 'scale(1.05)' : rankBadge === '🥈' ? 'scale(1.02)' : 'scale(1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              {rankBadge}
            </div>
            
            {/* Room Cumulative Score */}
            <div style={{
              fontSize: '0.85rem',
              background: roomCumulativeScore > 0 
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(79, 70, 229, 0.3))'
                : roomCumulativeScore < 0 
                  ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))'
                  : 'linear-gradient(135deg, rgba(107, 114, 128, 0.3), rgba(75, 85, 99, 0.3))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid ${roomCumulativeScore > 0 ? 'rgba(139, 92, 246, 0.5)' : roomCumulativeScore < 0 ? 'rgba(239, 68, 68, 0.5)' : 'rgba(107, 114, 128, 0.5)'}`,
              color: roomCumulativeScore > 0 ? '#a78bfa' : roomCumulativeScore < 0 ? '#ef4444' : '#9ca3af',
              padding: '0.4rem 0.6rem',
              borderRadius: '10px',
              fontWeight: '700',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: roomCumulativeScore > 0 
                ? '0 4px 16px rgba(139, 92, 246, 0.3), 0 0 0 1px rgba(79, 70, 229, 0.1) inset'
                : roomCumulativeScore < 0
                  ? '0 4px 16px rgba(239, 68, 68, 0.3), 0 0 0 1px rgba(220, 38, 38, 0.1) inset'
                  : '0 4px 16px rgba(107, 114, 128, 0.25), 0 0 0 1px rgba(75, 85, 99, 0.1) inset',
              minWidth: '55px',
              lineHeight: '1.1',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
            }}>
              <div style={{ fontSize: '0.6rem', opacity: 0.7, marginBottom: '2px', fontWeight: '600', letterSpacing: '0.5px' }}>TOTAL</div>
              <div>{roomCumulativeScore > 0 ? '+' : ''}{roomCumulativeScore}</div>
            </div>
            
            {/* Current Game Score */}
            {hasCurrentGameScore && (
              <div style={{
                fontSize: '0.85rem',
                background: currentGameScore > 0 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.3))' 
                  : currentGameScore < 0 
                    ? 'linear-gradient(135deg, rgba(251, 146, 60, 0.3), rgba(249, 115, 22, 0.3))' 
                    : 'linear-gradient(135deg, rgba(156, 163, 175, 0.3), rgba(107, 114, 128, 0.3))',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: `1px solid ${currentGameScore > 0 ? 'rgba(59, 130, 246, 0.5)' : currentGameScore < 0 ? 'rgba(251, 146, 60, 0.5)' : 'rgba(156, 163, 175, 0.5)'}`,
                color: currentGameScore > 0 ? '#3b82f6' : currentGameScore < 0 ? '#fb923c' : '#9ca3af',
                padding: '0.4rem 0.6rem',
                borderRadius: '10px',
                fontWeight: '700',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: currentGameScore > 0
                  ? '0 4px 16px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(37, 99, 235, 0.1) inset'
                  : currentGameScore < 0
                    ? '0 4px 16px rgba(251, 146, 60, 0.3), 0 0 0 1px rgba(249, 115, 22, 0.1) inset'
                    : '0 4px 16px rgba(156, 163, 175, 0.25), 0 0 0 1px rgba(107, 114, 128, 0.1) inset',
                minWidth: '55px',
                lineHeight: '1.1',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
              }}>
                <div style={{ fontSize: '0.6rem', opacity: 0.7, marginBottom: '2px', fontWeight: '600', letterSpacing: '0.5px' }}>GAME</div>
                <div>{currentGameScore > 0 ? '+' : ''}{currentGameScore}</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Team Scores Display - Bottom Left Corner (when teams are revealed) */}
      {gameState?.currentRound?.playPhase?.partnerRevealed && gameState?.currentRound?.scoring && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '80px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          zIndex: 500
        }}>
          {/* Bidding Team Score */}
          <div style={{
            fontSize: '0.75rem',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(37, 99, 235, 0.4))',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '2px solid rgba(59, 130, 246, 0.6)',
            color: '#3b82f6',
            padding: '6px 10px',
            borderRadius: '10px',
            fontWeight: '700',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)',
            minWidth: '85px',
            textAlign: 'center',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
            animation: 'pulse 3s ease-in-out infinite'
          }}>
            <div style={{ 
              fontSize: '0.65rem', 
              opacity: 0.8, 
              marginBottom: '3px', 
              fontWeight: '600', 
              letterSpacing: '0.5px' 
            }}>
              🎯 BIDDING
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>
              {gameState.currentRound.scoring.biddingTeam.pointsWon || 0}
            </div>
          </div>

          {/* Opposing Team Score */}
          <div style={{
            fontSize: '0.8rem',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.4), rgba(220, 38, 38, 0.4))',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '2px solid rgba(239, 68, 68, 0.6)',
            color: '#ef4444',
            padding: '8px 12px',
            borderRadius: '12px',
            fontWeight: '700',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)',
            minWidth: '85px',
            textAlign: 'center',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
            animation: 'pulse 3s ease-in-out infinite 0.5s'
          }}>
            <div style={{ 
              fontSize: '0.65rem', 
              opacity: 0.8, 
              marginBottom: '3px', 
              fontWeight: '600', 
              letterSpacing: '0.5px' 
            }}>
              🛡️ OPPOSING
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: '800' }}>
              {gameState.currentRound.scoring.opposingTeam.pointsWon || 0}
            </div>
          </div>
        </div>
      )}

      {/* Current Player's Hand - Properly centered using full viewport width */}
      {playerCards.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '60px',
          left: 0,
          right: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none', // Allow clicks to pass through container
          zIndex: 10
        }}>
          <div style={{
            maxWidth: '98vw',
            pointerEvents: 'auto' // Re-enable pointer events on cards
          }}>
            <PlayerHand
              cards={playerCards}
              isInteractive={true}
              onCardClick={handleCardPlay}
              selectedCard={selectedCard}
              playableCards={getPlayableCards()}
              currentPhase={currentPhase}
              isMyTurn={gameState?.currentRound?.playPhase.currentPlayer === currentPlayer?.id}
            />
          </div>
        </div>
      )}

      {/* Game Controls - Top Right Corner */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        zIndex: 1000,
        width: 'fit-content'
      }}>
        {/* Main Action Buttons Row - All buttons in single row */}
        {/* Single Row - All Action Buttons with Leave Room always at the right */}
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'nowrap', // Keep all buttons in single row
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}>
          {/* Make Bid Button - Only show during bidding phase when it's player's turn */}
          {currentPhase === 'bidding' && gameState?.currentRound?.biddingPhase.currentBidder === currentPlayer?.id && (
          <button
            onClick={() => {
              try {
                // Check if it's actually the player's turn to bid
                if (gameState?.currentRound?.biddingPhase.currentBidder !== currentPlayer?.id) {
                  addNotification("It's not your turn to bid!", 'turn');
                  return;
                }
                setShowBidding(true);
              } catch (error) {
                console.error('Error opening bid modal:', error);
                addNotification('Failed to open bidding interface', 'error');
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              padding: '12px 16px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
          >
            🎯 Make Bid
          </button>
        )}

        {/* Pass Button - Only show during bidding phase when it's player's turn */}
        {currentPhase === 'bidding' && gameState?.currentRound?.biddingPhase.currentBidder === currentPlayer?.id && (
          <button
            onClick={() => {
              try {
                // Check if it's actually the player's turn to bid
                if (gameState?.currentRound?.biddingPhase.currentBidder !== currentPlayer?.id) {
                  addNotification("It's not your turn to bid!", 'turn');
                  return;
                }
                
                // Check if player has already passed
                if (gameState?.currentRound?.biddingPhase.passedPlayers?.includes(currentPlayer?.id || '')) {
                  addNotification("You have already passed this round!", 'warning');
                  return;
                }
                
                dbg('multiplayer', '🚫 Pass button clicked by:', currentPlayer?.name);
                passMove();
                addNotification('You passed this round', 'info');
              } catch (error) {
                console.error('Error passing bid:', error);
                addNotification('Failed to pass. Please try again.', 'error');
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              padding: '12px 16px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
            }}
          >
            🚫 Pass
          </button>
        )}

          {/* Ready Up Button - Show if current player is not ready and in waiting phase */}
          {(currentPhase === 'waiting_for_players' || currentPhase === 'round_end') && !currentPlayer?.isReady && (
            <button
              onClick={() => {
                try {
                  setReady(true);
                } catch (error) {
                  console.error('Error setting ready state:', error);
                  addNotification('Failed to update ready status', 'error');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #a78bfa, #059669)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                padding: '12px 16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(79, 70, 229, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.3)';
              }}
            >
              ✅ Ready Up
            </button>
          )}

          {/* Remove AI Button - Only show in waiting phase if there are AI players */}
          {currentPhase === 'waiting_for_players' && (() => {
            const aiPlayers = players.filter(p => p.name?.includes('AI Player') || p.isAI);
            return aiPlayers.length > 0;
          })() && (
            <button
              onClick={() => {
                try {
                  removeAIPlayer();
                  addNotification('Removing AI player...', 'success');
                } catch (error) {
                  console.error('Error removing AI player:', error);
                  addNotification('Failed to remove AI player', 'error');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                padding: '12px 16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
              }}
            >
              ➖ Remove AI
            </button>
          )}

          {/* Start Game Now Button - Show in waiting phase when players >= 4 */}
          {currentPhase === 'waiting_for_players' && players.length >= 4 && (
            <button
              onClick={() => {
                try {
                  if (!currentPlayer) {
                    addNotification('You must be in the game to start it', 'error');
                    return;
                  }
                  const otherHumanPlayers = players.filter(p => p.id !== currentPlayer.id && !(p as any).isAI);
                  const othersReady = otherHumanPlayers.every(p => p.isReady);

                  if (!othersReady) {
                    if (!currentPlayer.isReady) {
                      setReady(true);
                      addNotification('Marked you ready. Waiting for other players...', 'info');
                    } else {
                      addNotification('Not all players are ready yet.', 'warning');
                    }
                    return;
                  }

                  if (!currentPlayer.isReady) {
                    setReady(true);
                    addNotification('You are now ready. Click Start again to launch the game.', 'info');
                    return;
                  }

                  console.log('🚀 Initiating start/restart (all humans ready)');
                  restartGame();
                  addNotification('Starting game...', 'success');
                } catch (error) {
                  console.error('Error starting game:', error);
                  addNotification('Failed to start game', 'error');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                padding: '12px 16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }}
            >
              🚀 Start Game Now
            </button>
          )}

          {/* Add AI Player Button - Only show in waiting phase if room is not full */}
          {currentPhase === 'waiting_for_players' && players.length < (gameState?.maxPlayers || 4) && (
            <button
              onClick={() => {
                try {
                  addAIPlayer();
                  addNotification('Adding AI player...', 'success');
                } catch (error) {
                  console.error('Error adding AI player:', error);
                  addNotification('Failed to add AI player', 'error');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                padding: '12px 16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
              }}
            >
              ➕ Add AI
            </button>
          )}
          
          {/* Fill with AI Button - Only show in waiting phase if room needs more than 1 player */}
          {currentPhase === 'waiting_for_players' && players.length < (gameState?.maxPlayers || 4) - 1 && (
            <button
              onClick={() => {
                try {
                  requestAIPlayers();
                  addNotification('Filling room with AI players...', 'success');
                } catch (error) {
                  console.error('Error requesting AI players:', error);
                  addNotification('Failed to add AI players', 'error');
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                padding: '12px 16px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.3)';
              }}
            >
              🤖 Fill Room
            </button>
          )}

          {/* Spacer to push Leave Room button to the right */}
          <div style={{ flex: 1 }} />

          {/* Leave Room Button - Always visible, always at the right */}
          <button
            onClick={() => {
              try {
                disconnect();
                setTimeout(() => {
                  window.location.href = '/rooms';
                }, 500);
              } catch (error) {
                console.error('Error leaving room:', error);
                addNotification('Failed to leave room cleanly', 'warning');
                window.location.href = '/rooms';
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              padding: '12px 16px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
            }}
          >
            🚪 Leave Room
          </button>
        </div>
      </div>

      {/* Bidding Modal - Desktop Only */}
      {showBidding && !isMobileDevice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
            borderRadius: '16px',
            maxWidth: '850px',
            width: '95%',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 40px rgba(59, 130, 246, 0.2)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            overflow: 'hidden'
          }}>
            {/* Fixed Header */}
            <div style={{ padding: '1rem 1rem 0 1rem' }}>
              <h3 style={{ 
                marginBottom: '0.75rem',
                color: '#1e293b',
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>🎯 Make Your Bid</h3>
            </div>

            {/* Scrollable Content Area */}
            <div style={{ 
              flex: '1 1 auto', 
              overflowY: 'auto', 
              padding: '0 1rem',
              maxHeight: 'calc(92vh - 180px)' // Reserve space for header and buttons with new modal height
            }}>

            {/* Timer and Pressure Indicators */}
            {timerActive && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                borderRadius: '12px',
                background: pressureLevel === 'high' 
                  ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1))'
                  : pressureLevel === 'medium'
                  ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.1))'
                  : 'linear-gradient(135deg, rgba(79, 70, 229, 0.15), rgba(67, 56, 202, 0.1))',
                border: '2px solid',
                borderColor: pressureLevel === 'high' 
                  ? 'rgba(239, 68, 68, 0.3)'
                  : pressureLevel === 'medium'
                  ? 'rgba(245, 158, 11, 0.3)'
                  : 'rgba(79, 70, 229, 0.3)',
                boxShadow: pressureLevel === 'high'
                  ? '0 0 20px rgba(239, 68, 68, 0.3)'
                  : pressureLevel === 'medium'
                  ? '0 0 15px rgba(245, 158, 11, 0.2)'
                  : '0 0 10px rgba(79, 70, 229, 0.2)',
                animation: pressureLevel === 'high'
                  ? 'urgencyPulse 0.5s infinite'
                  : pressureLevel === 'medium'
                  ? 'biddingPhasePulse 1s infinite'
                  : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: pressureLevel === 'high' ? '#dc2626' : pressureLevel === 'medium' ? '#d97706' : '#059669',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      animation: pressureLevel === 'high' ? 'iconRotate 1s infinite' : 'none'
                    }}>
                      {pressureLevel === 'high' ? '⏰' : pressureLevel === 'medium' ? '⏱️' : '🕐'}
                    </span>
                    <span>Time Remaining</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <span style={{
                      background: pressureLevel === 'high' 
                        ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                        : pressureLevel === 'medium'
                        ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                        : 'linear-gradient(135deg, #a78bfa, #059669)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '700',
                      minWidth: '48px',
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      animation: pressureLevel === 'high' ? 'youBadgeBounce 0.5s infinite' : 'none'
                    }}>
                      {biddingTimeLeft}s
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(226, 232, 240, 0.8)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${(biddingTimeLeft / (currentPhase === 'playing' ? 15 : 30)) * 100}%`,
                    background: pressureLevel === 'high' 
                      ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                      : pressureLevel === 'medium'
                      ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                      : 'linear-gradient(90deg, #a78bfa, #059669)',
                    borderRadius: '6px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: pressureLevel === 'high' ? 'progressGlow 1s infinite' : 'none'
                  }} />
                </div>

                {/* Pressure Message */}
                <div style={{
                  fontSize: '10px',
                  color: pressureLevel === 'high' ? '#dc2626' : pressureLevel === 'medium' ? '#d97706' : '#64748b',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  {pressureLevel === 'high' 
                    ? '🔥 Hurry up! Time is almost up!' 
                    : pressureLevel === 'medium' 
                    ? '⚡ Consider your options quickly'
                    : '💭 Take your time to think strategically'}
                </div>
              </div>
            )}
            
            {/* Bid Amount Section */}
            <div style={{ 
              marginBottom: '0.75rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.05))',
              borderRadius: '12px',
              border: '2px solid rgba(59, 130, 246, 0.2)'
            }}>
              <label style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
                color: '#1e293b',
                fontWeight: '600',
                fontSize: '12px'
              }}>
                <span>💰 Bid Amount</span>
                <span style={{
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                }}>{bidAmount}</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="range"
                  min={players.length >= 7 || partnershipConfig === '6-2deck' ? "250" : "125"}
                  max={players.length >= 7 || partnershipConfig === '6-2deck' ? "500" : "250"}
                  step="5"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(parseInt(e.target.value))}
                  style={{ 
                    width: '100%',
                    height: '6px',
                    borderRadius: '6px',
                    outline: 'none',
                    background: `linear-gradient(to right, 
                      #3b82f6 0%, 
                      #3b82f6 ${((bidAmount - (players.length >= 7 || partnershipConfig === '6-2deck' ? 250 : 125)) / ((players.length >= 7 || partnershipConfig === '6-2deck' ? 250 : 125))) * 100}%, 
                      #e2e8f0 ${((bidAmount - (players.length >= 7 || partnershipConfig === '6-2deck' ? 250 : 125)) / ((players.length >= 7 || partnershipConfig === '6-2deck' ? 250 : 125))) * 100}%, 
                      #e2e8f0 100%)`,
                    WebkitAppearance: 'none',
                    cursor: 'pointer'
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '4px',
                  fontSize: '10px',
                  color: '#64748b',
                  fontWeight: '500'
                }}>
                  <span>{players.length >= 7 || partnershipConfig === '6-2deck' ? "250" : "125"}</span>
                  <span>{players.length >= 7 || partnershipConfig === '6-2deck' ? "500" : "250"}</span>
                </div>
              </div>
            </div>

            {/* Bid Suggestion System for New Players */}
            {(() => {
              const isDoubleDeck = players.length >= 7 || partnershipConfig === '6-2deck';
              const minBid = isDoubleDeck ? 250 : 125;
              const maxBid = isDoubleDeck ? 500 : 250;
              const currentBidAmount = gameState?.currentRound?.biddingPhase.currentBid?.amount || 0;
              const nextMinBid = Math.max(minBid, currentBidAmount + 5);
              
              // Analyze player's hand strength (if available)
              const handAnalysis = (() => {
                const totalCards = playerCards.length;
                const highCards = playerCards.filter(c => ['K', 'A'].includes(c.rank)).length;
                const powerCards = playerCards.filter(c => c.suit === selectedSuit).length;
                
                return {
                  strength: totalCards > 0 ? (highCards / totalCards) * 100 : 50, // Percentage of high cards
                  powerSuitCount: powerCards,
                  totalCards: totalCards
                };
              })();

              // Generate intelligent suggestions
              const suggestions = [];

              // Safe bid suggestion - ensure it's always at least the minimum required
              const safeBid = Math.max(nextMinBid, Math.min(nextMinBid + 10, Math.floor((minBid + maxBid) * 0.3)));
              suggestions.push({
                type: 'safe',
                amount: safeBid,
                icon: '🛡️',
                title: 'Safe Bid',
                description: 'Conservative approach, lower risk',
                reason: 'Good for learning or with weak cards'
              });

              // Balanced bid suggestion
              const balancedBid = Math.max(nextMinBid, Math.min(nextMinBid + 25, Math.floor((minBid + maxBid) * 0.5)));
              if (balancedBid !== safeBid) {
                suggestions.push({
                  type: 'balanced',
                  amount: balancedBid,
                  icon: '⚖️',
                  title: 'Balanced Bid',
                  description: 'Moderate risk, decent reward',
                  reason: 'Solid choice for most situations'
                });
              }

              // Aggressive bid suggestion (only if hand looks strong)
              if (handAnalysis.strength > 40 || handAnalysis.powerSuitCount >= 3) {
                const aggressiveBid = Math.max(nextMinBid, Math.min(nextMinBid + 45, Math.floor((minBid + maxBid) * 0.8)));
                if (aggressiveBid !== balancedBid && aggressiveBid <= maxBid) {
                  suggestions.push({
                    type: 'aggressive',
                    amount: aggressiveBid,
                    icon: '🚀',
                    title: 'Aggressive Bid',
                    description: 'High risk, high reward',
                    reason: handAnalysis.powerSuitCount >= 3 
                      ? `Strong ${selectedSuit} suit (${handAnalysis.powerSuitCount} cards)`
                      : `Good hand strength (${Math.floor(handAnalysis.strength)}% high cards)`
                  });
                }
              }

              // Current bid context
              const currentBidContext = currentBidAmount > 0 
                ? `Current bid: ${currentBidAmount} - you need ${nextMinBid}+` 
                : 'First bid of the round - set the pace';

              return (
                <div style={{
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(245, 158, 11, 0.05))',
                  borderRadius: '12px',
                  border: '2px solid rgba(245, 158, 11, 0.2)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <label style={{ 
                      color: '#1e293b',
                      fontWeight: '600',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      💡 Smart Suggestions
                    </label>
                    <div style={{
                      fontSize: '9px',
                      color: '#64748b',
                      fontStyle: 'italic'
                    }}>
                      {currentBidContext}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '0.375rem',
                    flexWrap: 'wrap'
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div 
                        key={index}
                        onClick={() => setBidAmount(suggestion.amount)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.375rem',
                          borderRadius: '8px',
                          minWidth: '80px',
                          flex: '1 1 auto',
                          border: '1px solid',
                          borderColor: {
                            safe: 'rgba(79, 70, 229, 0.3)',
                            balanced: 'rgba(59, 130, 246, 0.3)',
                            aggressive: 'rgba(239, 68, 68, 0.3)'
                          }[suggestion.type],
                          background: {
                            safe: 'linear-gradient(135deg, rgba(209, 250, 229, 0.8), rgba(167, 243, 208, 0.6))',
                            balanced: 'linear-gradient(135deg, rgba(219, 234, 254, 0.8), rgba(147, 197, 253, 0.6))',
                            aggressive: 'linear-gradient(135deg, rgba(254, 226, 226, 0.8), rgba(252, 165, 165, 0.6))'
                          }[suggestion.type],
                          cursor: 'pointer',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: bidAmount === suggestion.amount ? 'scale(1.02)' : 'scale(1)',
                          boxShadow: bidAmount === suggestion.amount 
                            ? '0 4px 12px rgba(245, 158, 11, 0.3)' 
                            : '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          if (bidAmount !== suggestion.amount) {
                            e.currentTarget.style.transform = 'scale(1.01)';
                            e.currentTarget.style.boxShadow = '0 3px 8px rgba(245, 158, 11, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (bidAmount !== suggestion.amount) {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                          }
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.25rem',
                          marginBottom: '0.25rem' 
                        }}>
                          <span style={{ fontSize: '10px' }}>{suggestion.icon}</span>
                          <div style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            color: {
                              safe: '#059669',
                              balanced: '#2563eb',
                              aggressive: '#dc2626'
                            }[suggestion.type],
                            textAlign: 'center'
                          }}>
                            {suggestion.title}
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: '0.25rem' 
                        }}>
                          <span style={{
                            background: {
                              safe: 'linear-gradient(135deg, #a78bfa, #059669)',
                              balanced: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                              aggressive: 'linear-gradient(135deg, #ef4444, #dc2626)'
                            }[suggestion.type],
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '700',
                            minWidth: '36px',
                            textAlign: 'center'
                          }}>
                            {suggestion.amount}
                          </span>
                          {bidAmount === suggestion.amount && (
                            <span style={{
                              color: '#f59e0b',
                              fontSize: '10px'
                            }}>
                              ✓
                            </span>
                          )}
                        </div>
                        
                        <div style={{
                          fontSize: '7px',
                          color: '#64748b',
                          textAlign: 'center',
                          marginTop: '0.25rem',
                          lineHeight: '1.1',
                          maxWidth: '70px'
                        }}>
                          {suggestion.description}
                        </div>
                      </div>
                    ))}

                    {/* Strategic tip based on analysis */}
                    {handAnalysis.totalCards > 0 && (
                      <div style={{
                        marginTop: '0.25rem',
                        padding: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.6)',
                        borderRadius: '6px',
                        fontSize: '8px',
                        color: '#64748b',
                        fontStyle: 'italic',
                        lineHeight: '1.3'
                      }}>
                        💭 Analysis: {handAnalysis.strength > 60 ? 'Strong hand' : handAnalysis.strength > 30 ? 'Average hand' : 'Weak hand'} 
                        {handAnalysis.powerSuitCount > 0 && ` • ${handAnalysis.powerSuitCount} ${selectedSuit} cards`}
                        {suggestions.find(s => s.reason) && ` • ${suggestions.find(s => s.type === 'aggressive')?.reason || suggestions.find(s => s.reason)?.reason}`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Power House Suit Section */}
            <div style={{ 
              marginBottom: '0.75rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(139, 92, 246, 0.05))',
              borderRadius: '12px',
              border: '2px solid rgba(139, 92, 246, 0.2)'
            }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                color: '#1e293b',
                fontWeight: '600',
                fontSize: '12px'
              }}>
                🏠 Power House Suit
              </label>
              <select
                value={selectedSuit}
                onChange={(e) => setSelectedSuit(e.target.value as SuitType)}
                style={{ 
                  width: '100%', 
                  padding: '10px 12px',
                  borderRadius: '10px',
                  border: '2px solid #cbd5e1',
                  background: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  outline: 'none',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#8b5cf6';
                  e.currentTarget.style.boxShadow = '0 3px 10px rgba(139, 92, 246, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#8b5cf6';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
                }}
              >
                <option value="spades">♠️ Spades</option>
                <option value="hearts">♥️ Hearts</option>
                <option value="diamonds">♦️ Diamonds</option>
                <option value="clubs">♣️ Clubs</option>
              </select>
            </div>

            {/* Partner Cards Section */}
            <div style={{ 
              marginBottom: '0.75rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.08), rgba(79, 70, 229, 0.05))',
              borderRadius: '12px',
              border: '2px solid rgba(79, 70, 229, 0.2)'
            }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                color: '#1e293b',
                fontWeight: '600',
                fontSize: '11px',
                lineHeight: '1.4'
              }}>
                {(() => {
                  const maxPlayers = gameState?.maxPlayers || players.length;
                  const isSingleDeck = maxPlayers <= 6 && partnershipConfig !== '6-2deck';
                  const partnerCount = getPartnerCount(players.length, partnershipConfig);
                  
                  if (isSingleDeck) {
                    return `🤝 Select ${partnerCount} Partner Cards (Single deck - no play order needed)`;
                  } else {
                    return `🤝 Select ${partnerCount} Partner Cards (Choose 1st or 2nd to play each)`;
                  }
                })()}
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(13, 1fr)', // Fixed 13 columns for 13 cards per row (4 suits = 4 rows)
                gap: '6px', 
                padding: '8px',
                background: 'white',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.05)'
              }}>
                {/* Generate all possible cards - properly filter out cards player has */}
                {(['spades', 'hearts', 'diamonds', 'clubs'] as SuitType[]).flatMap(suit => 
                  (['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'] as RankType[]).flatMap(rank => {
                    const isDoubleDeck = players.length >= 7 || partnershipConfig === '6-2deck';
                    const hasCard = playerCards.some(c => c.suit === suit && c.rank === rank);
                    const cardSelectionCount = getCardSelectionCount(suit, rank);
                    const copiesInHand = playerCards.filter(pc => pc.suit === suit && pc.rank === rank).length;
                    
                    dbg('multiplayer', `🎯 Analyzing ${rank}${convertSuitToDisplay(suit)}: isDoubleDeck=${isDoubleDeck}, copiesInHand=${copiesInHand}/4, hasCard=${hasCard}`);
                    
                    // In both single deck AND double deck: don't show cards where player has ALL copies
                    // Single deck: if player has the card, don't show it (can't be partner with yourself)
                    // Double deck: if player has 4 copies (all of them), don't show it
                    
                    if (!isDoubleDeck && hasCard) {
                      dbg('multiplayer', `❌ Filtering out ${rank}${convertSuitToDisplay(suit)} - player has this card in single deck`);
                      return [];
                    }
                    
                    if (isDoubleDeck && copiesInHand >= 4) {
                      dbg('multiplayer', `❌ Filtering out ${rank}${convertSuitToDisplay(suit)} - player has all 4 copies in double deck`);
                      return [];
                    }
                    
                    // For single deck games, only show cards NOT in player's hand
                    if (!isDoubleDeck) {
                      const card = { suit, rank, id: `${suit}-${rank}` };
                      dbg('multiplayer', `✅ Showing ${rank}${convertSuitToDisplay(suit)} in single deck - not in player's hand`);
                      return [(
                        <div
                          key={card.id}
                          onClick={() => handlePartnerCardClick({ suit, rank, id: `${suit}-${rank}` })}
                          style={{
                            width: '100%', // Use 100% width to fill grid cell
                            height: '54px', // Slightly taller for better visibility
                            minWidth: '40px', // Minimum width for mobile
                            background: cardSelectionCount > 0 
                              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                              : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                            color: cardSelectionCount > 0 ? 'white' : getSuitColor(suit),
                            border: cardSelectionCount > 0 
                              ? '1.5px solid #60a5fa' 
                              : '1.5px solid #e2e8f0',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            position: 'relative',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: cardSelectionCount > 0 
                              ? '0 3px 8px rgba(59, 130, 246, 0.3)' 
                              : '0 1px 3px rgba(0, 0, 0, 0.05)'
                          }}
                          onMouseEnter={(e) => {
                            if (cardSelectionCount === 0) {
                              e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
                              e.currentTarget.style.borderColor = '#3b82f6';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            if (cardSelectionCount === 0) {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                            }
                          }}
                          title='Select as partner card'
                        >
                          <div style={{ fontSize: '14px' }}>{convertSuitToDisplay(suit)}</div>
                          <div style={{ fontSize: '11px', marginTop: '1px' }}>{rank}</div>
                          
                          {/* Show selection status */}
                          {cardSelectionCount > 0 && (
                            <div style={{ 
                              position: 'absolute',
                              top: '-5px',
                              right: '-5px',
                              background: 'linear-gradient(135deg, #a78bfa, #059669)',
                              color: 'white',
                              borderRadius: '50%',
                              width: '16px',
                              height: '16px',
                              fontSize: '9px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              border: '1.5px solid white',
                              boxShadow: '0 2px 6px rgba(79, 70, 229, 0.4)'
                            }}>
                              ✓
                            </div>
                          )}
                        </div>
                      )];
                    }
                    
                    // NEW SYSTEM: Show only single cards, allow up to 2 selections per card
                    dbg('multiplayer', `✅ Showing ${rank}${convertSuitToDisplay(suit)} - ${copiesInHand} in hand`);
                    const selectionCount = getCardSelectionCount(suit, rank);
                    
                    return (
                      <div
                        key={`${suit}-${rank}`}
                        onClick={() => handlePartnerCardClick({ suit, rank, id: `${suit}-${rank}` })}
                        style={{
                          width: '100%', // Use 100% width to fill grid cell
                          height: '54px', // Slightly taller for better visibility
                          minWidth: '40px', // Minimum width for mobile
                          background: selectionCount > 0 
                            ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                            : 'linear-gradient(135deg, #ffffff, #f8fafc)',
                          color: selectionCount > 0 ? 'white' : getSuitColor(suit),
                          border: hasCard 
                            ? '1.5px solid #fbbf24' 
                            : selectionCount > 0 
                              ? '1.5px solid #60a5fa' 
                              : '1.5px solid #e2e8f0',
                          borderRadius: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          position: 'relative',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: selectionCount > 0 
                            ? '0 3px 8px rgba(59, 130, 246, 0.3)' 
                            : '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          if (selectionCount === 0) {
                            e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
                            e.currentTarget.style.borderColor = '#3b82f6';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          if (selectionCount === 0) {
                            e.currentTarget.style.borderColor = hasCard ? '#fbbf24' : '#e2e8f0';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                          }
                        }}
                        title={
                          `${rank}${convertSuitToDisplay(suit)} - Selected ${selectionCount}/2 times\n` +
                          `${selectionCount === 0 ? 'Click to add (defaults to 2nd play)' : 
                            selectionCount === 1 ? 'Click again to add 1st play option' : 
                            'Maximum 2 selections reached'}\n` +
                          `Partnership determined by actual play order during game`
                        }
                      >
                        <div style={{ fontSize: '14px' }}>{convertSuitToDisplay(suit)}</div>
                        <div style={{ fontSize: '11px', marginTop: '1px' }}>{rank}</div>
                        
                        {/* Show selection count */}
                        {selectionCount > 0 && (
                          <div style={{ 
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            background: selectionCount === 1 
                              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                              : 'linear-gradient(135deg, #a78bfa, #059669)',
                            color: 'white',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            fontSize: '9px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            border: '1.5px solid white',
                            boxShadow: selectionCount === 1 
                              ? '0 2px 6px rgba(59, 130, 246, 0.4)' 
                              : '0 2px 6px rgba(79, 70, 229, 0.4)'
                          }}>
                            {selectionCount}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Selected Partner Cards Display */}
            {selectedPartnerCards.length > 0 && (
              <div style={{ 
                marginTop: '0.75rem',
                marginBottom: '0.75rem', 
                padding: '0.75rem', 
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.08))', 
                borderRadius: '12px',
                border: '2px solid rgba(59, 130, 246, 0.3)',
                boxShadow: '0 3px 8px rgba(59, 130, 246, 0.1)'
              }}>
                <h4 style={{ 
                  margin: '0 0 0.5rem 0', 
                  color: '#1d4ed8', 
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>✅</span>
                  <span>Selected ({selectedPartnerCards.length}/{getPartnerCount(players.length, partnershipConfig)})</span>
                </h4>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}>
                  {selectedPartnerCards.map((card) => {
                    const playOrderText = card.playOrder === 1 ? '1st' : '2nd';
                    const displayText = `${card.rank}${convertSuitToDisplay(card.suit)}`;
                    const maxPlayers = gameState?.maxPlayers || players.length;
                    // partnershipConfig is available from useGameContext() hook scope
                    const isSingleDeck = maxPlayers <= 6 && partnershipConfig !== '6-2deck'; // Single deck EXCEPT 6-2deck variant
                    
                    console.log('🎴 RENDERING SELECTED CARD:', {
                      card: displayText,
                      maxPlayers,
                      partnershipConfig,
                      isSingleDeck,
                      willShowToggle: !isSingleDeck
                    });
                    
                    return (
                      <div
                        key={card.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
                          border: '1.5px solid #3b82f6',
                          borderRadius: '8px',
                          padding: '6px',
                          minWidth: '54px',
                          position: 'relative',
                          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
                        }}
                      >
                        {/* Card Display */}
                        <div style={{
                          color: getSuitColor(card.suit),
                          fontSize: '16px',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                        }}>
                          {displayText}
                        </div>
                        
                        {/* Play Order Button - Only show for double deck games */}
                        {!isSingleDeck && (
                          <div 
                            onClick={() => togglePlayOrder(card)}
                            style={{
                              background: card.playOrder === 1 
                                ? 'linear-gradient(135deg, #a78bfa, #059669)' 
                                : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                              color: 'white',
                              borderRadius: '6px',
                              padding: '3px 8px',
                              fontSize: '9px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              marginBottom: '3px',
                              boxShadow: card.playOrder === 1 
                                ? '0 1px 6px rgba(79, 70, 229, 0.3)' 
                                : '0 1px 6px rgba(59, 130, 246, 0.3)',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title={`Currently: ${playOrderText} to play becomes partner. Click to toggle to ${card.playOrder === 1 ? '2nd' : '1st'} play.`}
                          >
                            {playOrderText} play
                          </div>
                        )}
                        
                        {/* Single deck indicator */}
                        {isSingleDeck && (
                          <div style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            borderRadius: '6px',
                            padding: '3px 8px',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            marginBottom: '3px',
                            boxShadow: '0 1px 6px rgba(16, 185, 129, 0.3)',
                          }}>
                            Partner
                          </div>
                        )}
                        
                        {/* Remove Button */}
                        <button 
                          onClick={() => removePartnerCard(card)}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            color: 'white',
                            border: '1.5px solid white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(239, 68, 68, 0.4)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
                            e.currentTarget.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.4)';
                          }}
                          title="Remove this partner card"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '9px', 
                  color: '#64748b', 
                  fontStyle: 'italic',
                  fontWeight: '500',
                  lineHeight: '1.4'
                }}>
                  💡 Partnership by play order during game
                </div>
              </div>
            )}

            {/* Bid History & Analytics Section */}
            <div style={{ 
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08), rgba(251, 191, 36, 0.05))',
              borderRadius: '12px',
              border: '2px solid rgba(251, 191, 36, 0.2)',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <h4 style={{ 
                  color: '#1e293b',
                  fontWeight: '600',
                  fontSize: '12px',
                  margin: 0
                }}>
                  📜 Bidding History & Patterns
                </h4>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  Round {(gameState?.gameHistory?.length || 0) + 1}
                </div>
              </div>

              {/* Current Round Bidding */}
              {gameState?.currentRound?.biddingPhase?.biddingHistory && gameState.currentRound.biddingPhase.biddingHistory.length > 0 && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#059669', 
                    marginBottom: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <span>🔥</span>
                    <span>Current Round Bids</span>
                    <div style={{
                      background: '#a78bfa',
                      color: 'white',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      fontSize: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold'
                    }}>
                      {gameState.currentRound.biddingPhase.biddingHistory.length}
                    </div>
                  </div>
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    maxHeight: '80px',
                    overflowY: 'auto'
                  }}>
                    {gameState?.currentRound?.biddingPhase?.biddingHistory?.map((entry: any, index: number) => {
                      const player = players.find(p => p.id === entry.playerId);
                      const playerName = getAIPlayerDisplayName(
                        player?.name || 'Unknown',
                        player?.isAI || false,
                        (player as any)?.aiEngineStatus
                      );
                      const isHighestBid = entry.amount > 0 && gameState?.currentRound?.biddingPhase?.biddingHistory && 
                        entry.amount === Math.max(...gameState.currentRound.biddingPhase.biddingHistory.filter((b: any) => b.amount > 0).map((b: any) => b.amount));
                      
                      return (
                        <div key={index} style={{ 
                          fontSize: '10px', 
                          marginBottom: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.25rem',
                          background: entry.amount === 0 ? 'rgba(239, 68, 68, 0.1)' : isHighestBid ? 'rgba(79, 70, 229, 0.15)' : 'rgba(59, 130, 246, 0.1)',
                          borderRadius: '6px',
                          border: `1px solid ${entry.amount === 0 ? '#fca5a5' : isHighestBid ? '#6ee7b7' : '#93c5fd'}`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ fontWeight: 'bold', color: '#1e293b' }}>
                              {playerName}
                            </span>
                            {entry.amount === 0 ? (
                              <span style={{ color: '#dc2626', fontSize: '8px' }}>🚫 PASS</span>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <span style={{ color: '#059669', fontWeight: 'bold' }}>
                                  {entry.amount}pts
                                </span>
                                {entry.powerHouseSuit && (
                                  <span style={{ fontSize: '8px' }}>
                                    {entry.powerHouseSuit === 'spades' ? '♠️' : 
                                     entry.powerHouseSuit === 'hearts' ? '♥️' :
                                     entry.powerHouseSuit === 'diamonds' ? '♦️' : '♣️'}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {isHighestBid && entry.amount > 0 && (
                            <div style={{
                              background: '#ffd700',
                              color: '#1e293b',
                              borderRadius: '50%',
                              width: '12px',
                              height: '12px',
                              fontSize: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold'
                            }}>
                              👑
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Previous Rounds Summary */}
              {gameState?.gameHistory && gameState.gameHistory.length > 0 && (
                <div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#7c3aed', 
                    marginBottom: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <span>📊</span>
                    <span>Previous Rounds</span>
                    <div style={{
                      background: '#8b5cf6',
                      color: 'white',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      fontSize: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold'
                    }}>
                      {gameState.gameHistory.length}
                    </div>
                  </div>
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    maxHeight: '80px',
                    overflowY: 'auto'
                  }}>
                    {gameState.gameHistory.slice(-3).reverse().map((round: any, roundIndex: number) => {
                      const actualRoundNumber = gameState.gameHistory.length - roundIndex;
                      const winningBid = round.biddingPhase?.currentBid;
                      const bidSuccessful = round.scoring?.roundResult === 'bid_successful';
                      
                      if (!winningBid) return null;
                      
                      const bidder = players.find(p => p.id === winningBid.playerId)?.name || 'Unknown';
                      
                      return (
                        <div key={actualRoundNumber} style={{ 
                          fontSize: '10px', 
                          marginBottom: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.25rem',
                          background: bidSuccessful ? 'rgba(139, 92, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '6px',
                          border: `1px solid ${bidSuccessful ? '#86efac' : '#fca5a5'}`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ 
                              background: '#6b7280',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '1px 4px',
                              fontSize: '8px',
                              fontWeight: 'bold'
                            }}>
                              R{actualRoundNumber}
                            </span>
                            <span style={{ fontWeight: 'bold', color: '#1e293b' }}>
                              {bidder}
                            </span>
                            <span style={{ color: '#059669', fontWeight: 'bold' }}>
                              {winningBid.amount}pts
                            </span>
                            <span style={{ fontSize: '8px' }}>
                              {winningBid.powerHouseSuit === 'spades' ? '♠️' : 
                               winningBid.powerHouseSuit === 'hearts' ? '♥️' :
                               winningBid.powerHouseSuit === 'diamonds' ? '♦️' : '♣️'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ 
                              fontSize: '8px',
                              color: bidSuccessful ? '#059669' : '#dc2626',
                              fontWeight: 'bold'
                            }}>
                              {bidSuccessful ? '✅ MADE' : '❌ FAILED'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No History Message */}
              {(!gameState?.currentRound?.biddingPhase?.biddingHistory || gameState.currentRound.biddingPhase.biddingHistory.length === 0) && 
               (!gameState?.gameHistory || gameState.gameHistory.length === 0) && (
                <div style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '11px',
                  fontStyle: 'italic',
                  padding: '1rem'
                }}>
                  🎯 First bid of the game - make it count!
                </div>
              )}
            </div>

            {/* Enhanced Bidding Statistics & Player Analysis */}
            {(gameState?.gameHistory?.length || 0) > 0 && (
              <div style={{ 
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(139, 92, 246, 0.05))',
                borderRadius: '12px',
                border: '2px solid rgba(139, 92, 246, 0.2)',
                maxHeight: '220px',
                overflowY: 'auto'
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem'
                }}>
                  <h4 style={{ 
                    color: '#1e293b',
                    fontWeight: '600',
                    fontSize: '12px',
                    margin: 0
                  }}>
                    📊 Player Analytics & Risk Assessment
                  </h4>
                  <div style={{
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    Strategic Intel
                  </div>
                </div>

                {(() => {
                  // Calculate comprehensive player statistics
                  const playerStats = players.map(player => {
                    const playerBids = gameState?.gameHistory?.flatMap((round: any) => 
                      round.biddingPhase?.biddingHistory?.filter((bid: any) => 
                        bid.playerId === player.id && bid.amount > 0
                      ) || []
                    ) || [];
                    
                    const winningBids = gameState?.gameHistory?.filter((round: any) => 
                      round.biddingPhase?.currentBid?.playerId === player.id
                    ) || [];
                    
                    const successfulBids = winningBids.filter((round: any) => 
                      round.scoring?.roundResult === 'bid_successful'
                    );
                    
                    // Suit preferences
                    const suitCounts = { spades: 0, hearts: 0, diamonds: 0, clubs: 0 };
                    winningBids.forEach((round: any) => {
                      const suit = round.biddingPhase?.currentBid?.powerHouse;
                      if (suit) suitCounts[suit as keyof typeof suitCounts]++;
                    });
                    
                    const preferredSuit = Object.entries(suitCounts).reduce((a, b) => 
                      suitCounts[a[0] as keyof typeof suitCounts] > suitCounts[b[0] as keyof typeof suitCounts] ? a : b
                    )[0];
                    
                    // Calculate metrics
                    const totalBids = playerBids.length;
                    const avgBidAmount = totalBids > 0 ? Math.round(playerBids.reduce((sum, bid) => sum + bid.amount, 0) / totalBids) : 0;
                    const successRate = winningBids.length > 0 ? Math.round((successfulBids.length / winningBids.length) * 100) : 0;
                    const aggressiveness = totalBids > 0 ? Math.min(100, Math.round((playerBids.filter(bid => bid.amount > 170).length / totalBids) * 100)) : 0;
                    
                    // Risk assessment
                    let riskLevel = 'Safe';
                    let riskColor = '#a78bfa';
                    if (aggressiveness > 60 || (successRate < 40 && winningBids.length > 2)) {
                      riskLevel = 'High Risk';
                      riskColor = '#ef4444';
                    } else if (aggressiveness > 30 || avgBidAmount > 180) {
                      riskLevel = 'Moderate';
                      riskColor = '#f59e0b';
                    }
                    
                    return {
                      player,
                      totalBids,
                      avgBidAmount,
                      successRate,
                      aggressiveness,
                      preferredSuit,
                      winningBids: winningBids.length,
                      successfulBids: successfulBids.length,
                      riskLevel,
                      riskColor
                    };
                  }).filter(stat => stat.totalBids > 0 || stat.winningBids > 0);

                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}>
                      {playerStats.length > 0 ? playerStats.map((stat) => (
                        <div key={stat.player.id} style={{
                          padding: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '8px',
                          border: '1px solid rgba(139, 92, 246, 0.2)'
                        }}>
                          {/* Player Header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '0.25rem'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <span style={{ 
                                fontWeight: 'bold', 
                                color: '#1e293b',
                                fontSize: '10px'
                              }}>
                                {stat.player.name}
                                {stat.player.id === currentPlayer?.id && ' (You)'}
                              </span>
                              {stat.preferredSuit && (
                                <span style={{ fontSize: '10px' }}>
                                  {stat.preferredSuit === 'spades' ? '♠️' : 
                                   stat.preferredSuit === 'hearts' ? '♥️' :
                                   stat.preferredSuit === 'diamonds' ? '♦️' : '♣️'}
                                </span>
                              )}
                            </div>
                            <div style={{
                              background: stat.riskColor,
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '8px',
                              fontWeight: 'bold'
                            }}>
                              {stat.riskLevel}
                            </div>
                          </div>
                          
                          {/* Statistics Grid */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.25rem',
                            fontSize: '9px'
                          }}>
                            <div style={{
                              textAlign: 'center',
                              padding: '3px',
                              background: 'rgba(59, 130, 246, 0.1)',
                              borderRadius: '4px'
                            }}>
                              <div style={{ fontWeight: 'bold', color: '#3b82f6' }}>
                                {stat.successRate}%
                              </div>
                              <div style={{ color: '#6b7280' }}>Success</div>
                            </div>
                            
                            <div style={{
                              textAlign: 'center',
                              padding: '3px',
                              background: 'rgba(79, 70, 229, 0.1)',
                              borderRadius: '4px'
                            }}>
                              <div style={{ fontWeight: 'bold', color: '#a78bfa' }}>
                                {stat.avgBidAmount || 'N/A'}
                              </div>
                              <div style={{ color: '#6b7280' }}>Avg Bid</div>
                            </div>
                            
                            <div style={{
                              textAlign: 'center',
                              padding: '3px',
                              background: 'rgba(251, 146, 60, 0.1)',
                              borderRadius: '4px'
                            }}>
                              <div style={{ fontWeight: 'bold', color: '#fb923c' }}>
                                {stat.aggressiveness}%
                              </div>
                              <div style={{ color: '#6b7280' }}>Aggression</div>
                            </div>
                          </div>
                          
                          {/* Performance Summary */}
                          <div style={{
                            marginTop: '0.25rem',
                            fontSize: '8px',
                            color: '#6b7280',
                            textAlign: 'center'
                          }}>
                            {stat.winningBids} winning bids • {stat.successfulBids} successful • {stat.totalBids} total attempts
                          </div>
                        </div>
                      )) : (
                        <div style={{
                          textAlign: 'center',
                          color: '#6b7280',
                          fontSize: '10px',
                          fontStyle: 'italic',
                          padding: '1rem'
                        }}>
                          📈 Statistics will appear as players make bids
                        </div>
                      )}
                      
                      {/* Strategic Insights */}
                      {playerStats.length > 1 && (
                        <div style={{
                          marginTop: '0.5rem',
                          padding: '0.5rem',
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.1))',
                          borderRadius: '6px',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}>
                          <div style={{
                            fontSize: '9px',
                            fontWeight: 'bold',
                            color: '#3b82f6',
                            marginBottom: '0.25rem'
                          }}>
                            💡 Strategic Insights
                          </div>
                          {(() => {
                            const mostAggressive = playerStats.reduce((prev, current) => 
                              prev.aggressiveness > current.aggressiveness ? prev : current
                            );
                            const mostSuccessful = playerStats.reduce((prev, current) => 
                              prev.successRate > current.successRate ? prev : current
                            );
                            const highestAvgBidder = playerStats.reduce((prev, current) => 
                              prev.avgBidAmount > current.avgBidAmount ? prev : current
                            );
                            
                            return (
                              <div style={{ fontSize: '8px', color: '#6b7280', lineHeight: '1.3' }}>
                                <div>🎯 Most successful: <strong>{mostSuccessful.player.name}</strong> ({mostSuccessful.successRate}%)</div>
                                <div>⚡ Most aggressive: <strong>{mostAggressive.player.name}</strong> ({mostAggressive.aggressiveness}%)</div>
                                <div>💰 Highest avg bid: <strong>{highestAvgBidder.player.name}</strong> ({highestAvgBidder.avgBidAmount} pts)</div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Advanced Bid Validation Feedback */}
            {(() => {
              const isDoubleDeck = players.length >= 7 || partnershipConfig === '6-2deck';
              const minBid = isDoubleDeck ? 250 : 125;
              const maxBid = isDoubleDeck ? 500 : 250;
              const currentBidAmount = gameState?.currentRound?.biddingPhase.currentBid?.amount || 0;
              const expectedPartnerCount = getPartnerCount(players.length, partnershipConfig);
              
              const validationResults = [];

              // Check bid amount range
              if (bidAmount < minBid) {
                validationResults.push({
                  type: 'error' as const,
                  icon: '⚠️',
                  message: `Bid too low! Minimum is ${minBid} points`,
                  suggestion: `Consider starting at ${minBid} for a safer bid`
                });
              } else if (bidAmount > maxBid) {
                validationResults.push({
                  type: 'error' as const,
                  icon: '⚠️',
                  message: `Bid too high! Maximum is ${maxBid} points`,
                  suggestion: `Try ${maxBid} for the highest possible bid`
                });
              }

              // Check against current bid
              if (currentBidAmount > 0 && bidAmount <= currentBidAmount) {
                validationResults.push({
                  type: 'error' as const,
                  icon: '📈',
                  message: `Must exceed current bid of ${currentBidAmount}`,
                  suggestion: `Try ${currentBidAmount + 5} or higher to outbid`
                });
              }

              // Check partner card selection
              if (selectedPartnerCards.length === 0) {
                validationResults.push({
                  type: 'warning' as const,
                  icon: '🤝',
                  message: `Select ${expectedPartnerCount} partner card${expectedPartnerCount > 1 ? 's' : ''} to continue`,
                  suggestion: 'Choose cards you\'re likely to have or play early'
                });
              } else if (selectedPartnerCards.length < expectedPartnerCount) {
                const remaining = expectedPartnerCount - selectedPartnerCards.length;
                validationResults.push({
                  type: 'warning' as const,
                  icon: '🤝',
                  message: `Need ${remaining} more partner card${remaining > 1 ? 's' : ''}`,
                  suggestion: 'Select cards that complement your strategy'
                });
              } else if (selectedPartnerCards.length > expectedPartnerCount) {
                const extra = selectedPartnerCards.length - expectedPartnerCount;
                validationResults.push({
                  type: 'warning' as const,
                  icon: '🤝',
                  message: `Too many cards selected! Remove ${extra}`,
                  suggestion: 'Keep only your strongest partner choices'
                });
              }

              // Strategic suggestions based on bid amount
              if (validationResults.filter(r => r.type === 'error').length === 0) {
                if (bidAmount <= minBid + 10) {
                  validationResults.push({
                    type: 'info' as const,
                    icon: '🛡️',
                    message: 'Conservative bid - lower risk, lower reward',
                    suggestion: 'Good for beginners or when you have weak cards'
                  });
                } else if (bidAmount >= maxBid - 20) {
                  validationResults.push({
                    type: 'info' as const,
                    icon: '🚀',
                    message: 'Aggressive bid - high risk, high reward',
                    suggestion: 'Make sure you have strong cards and good partners'
                  });
                } else {
                  validationResults.push({
                    type: 'success' as const,
                    icon: '✅',
                    message: 'Balanced bid - good risk/reward ratio',
                    suggestion: 'Solid choice for most game situations'
                  });
                }
              }

              // Play order balance check
              if (selectedPartnerCards.length === expectedPartnerCount) {
                const playOrder1Count = selectedPartnerCards.filter(pc => pc.playOrder === 1).length;
                const playOrder2Count = selectedPartnerCards.filter(pc => pc.playOrder === 2).length;
                
                if (playOrder1Count === 0) {
                  validationResults.push({
                    type: 'tip' as const,
                    icon: '🎯',
                    message: 'All cards set for 2nd play',
                    suggestion: 'Consider mixing 1st and 2nd play cards for flexibility'
                  });
                } else if (playOrder2Count === 0) {
                  validationResults.push({
                    type: 'tip' as const,
                    icon: '🎯',
                    message: 'All cards set for 1st play',
                    suggestion: 'This reveals partners early - high transparency strategy'
                  });
                } else {
                  validationResults.push({
                    type: 'success' as const,
                    icon: '⚖️',
                    message: 'Balanced play order distribution',
                    suggestion: 'Good mix of early and late partner reveals'
                  });
                }
              }

              if (validationResults.length === 0) return null;

              return (
                <div style={{
                  marginTop: '0.75rem',
                  marginBottom: '0.75rem',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '12px',
                  padding: '0.75rem'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#475569',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    🔍 Bid Analysis
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {validationResults.map((result, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                        padding: '0.5rem',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: {
                          error: 'rgba(239, 68, 68, 0.3)',
                          warning: 'rgba(245, 158, 11, 0.3)',
                          info: 'rgba(59, 130, 246, 0.3)',
                          success: 'rgba(79, 70, 229, 0.3)',
                          tip: 'rgba(139, 92, 246, 0.3)'
                        }[result.type],
                        background: {
                          error: 'linear-gradient(135deg, rgba(254, 226, 226, 0.8), rgba(252, 165, 165, 0.6))',
                          warning: 'linear-gradient(135deg, rgba(255, 251, 235, 0.8), rgba(252, 211, 77, 0.6))',
                          info: 'linear-gradient(135deg, rgba(219, 234, 254, 0.8), rgba(147, 197, 253, 0.6))',
                          success: 'linear-gradient(135deg, rgba(209, 250, 229, 0.8), rgba(110, 231, 183, 0.6))',
                          tip: 'linear-gradient(135deg, rgba(237, 233, 254, 0.8), rgba(196, 181, 253, 0.6))'
                        }[result.type]
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          fontSize: '10px',
                          fontWeight: '600',
                          color: {
                            error: '#dc2626',
                            warning: '#d97706',
                            info: '#2563eb',
                            success: '#059669',
                            tip: '#7c3aed'
                          }[result.type]
                        }}>
                          <span>{result.icon}</span>
                          <span>{result.message}</span>
                        </div>
                        {result.suggestion && (
                          <div style={{
                            fontSize: '9px',
                            color: '#64748b',
                            fontStyle: 'italic',
                            paddingLeft: '1rem'
                          }}>
                            💡 {result.suggestion}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            </div>

            {/* Fixed Button Area */}
            <div style={{ 
              padding: '1rem',
              borderTop: '1px solid rgba(226, 232, 240, 0.8)',
              background: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(4px)'
            }}>
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleMakeBid}
                disabled={selectedPartnerCards.length !== getPartnerCount(players.length, partnershipConfig)}
                style={{
                  background: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) 
                    ? 'linear-gradient(135deg, #a78bfa, #059669)' 
                    : 'linear-gradient(135deg, #9ca3af, #6b7280)',
                  color: 'white',
                  border: '1.5px solid rgba(255, 255, 255, 0.2)',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) ? 'pointer' : 'not-allowed',
                  fontWeight: '700',
                  fontSize: '12px',
                  flex: 1,
                  boxShadow: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) 
                    ? '0 3px 10px rgba(79, 70, 229, 0.3)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig)) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  if (selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig)) {
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(79, 70, 229, 0.3)';
                  }
                }}
              >
                🎯 Bid {bidAmount}
              </button>
              <button
                onClick={() => {
                  try {
                    // Check if it's actually the player's turn to bid
                    if (gameState?.currentRound?.biddingPhase.currentBidder !== currentPlayer?.id) {
                      addNotification("It's not your turn to bid!", 'turn');
                      return;
                    }
                    
                    // Check if player has already passed
                    if (gameState?.currentRound?.biddingPhase.passedPlayers?.includes(currentPlayer?.id || '')) {
                      addNotification("You have already passed this round!", 'warning');
                      return;
                    }
                    
                    dbg('multiplayer', '🚫 Pass button clicked by:', currentPlayer?.name);
                    passMove();
                    setShowBidding(false); // Close bidding modal after passing
                    addNotification('You passed this round', 'info');
                  } catch (error) {
                    console.error('Error passing bid:', error);
                    addNotification('Failed to pass. Please try again.', 'error');
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'white',
                  border: '1.5px solid rgba(255, 255, 255, 0.2)',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '12px',
                  flex: 1,
                  boxShadow: '0 3px 10px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 3px 10px rgba(239, 68, 68, 0.3)';
                }}
              >
                🚫 Pass
              </button>
              <button
                onClick={() => setShowBidding(false)}
                style={{
                  background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                  color: 'white',
                  border: '1.5px solid rgba(255, 255, 255, 0.2)',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '12px',
                  minWidth: '80px',
                  boxShadow: '0 3px 10px rgba(107, 114, 128, 0.3)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 3px 10px rgba(107, 114, 128, 0.3)';
                }}
              >
                ❌ Cancel
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Join Room Modal */}
      <JoinRoomModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onJoinRoom={handleJoinRoom}
        onCreateRoom={handleCreateRoom}
      />

      {/* Victory Overlay */}
      {(() => {
        const isGameComplete = (gameState as any)?.cumulativeTeamScores?.isGameComplete;
        const isGameEnd = gameState?.phase === 'game_end';
        const isCompleted = gameState?.status === 'completed';
        const isVisible = Boolean(isGameComplete || isGameEnd || isCompleted);
        
        console.log('🏆🏆🏆 VICTORY BANNER VISIBILITY CHECK:', {
          isGameComplete,
          isGameEnd,
          isCompleted,
          isVisible,
          gameStatus: gameState?.status,
          gamePhase: gameState?.phase,
          hasCumulativeScores: !!(gameState as any)?.cumulativeTeamScores
        });
        
        return null;
      })()}
      {/* Only render VictoryOverlay if both players and roomScores are present and valid */}
      {/* Hide victory banner when auto-play is enabled to allow seamless game flow */}
      {(players && players.length > 0 && roomScores && Object.keys(roomScores).length > 0) ? (
        <VictoryOverlay
          isVisible={Boolean(
            !autoPlayEnabled && // Hide when auto-play is enabled
            ((gameState as any)?.cumulativeTeamScores?.isGameComplete || gameState?.phase === 'game_end' || gameState?.status === 'completed')
          )}
          winnerTeam={
            ((gameState as any)?.cumulativeTeamScores?.winningTeam as 'bidding' | 'opposing') ||
            (gameState?.gameHistory?.[0]?.scoring?.roundResult === 'bid_successful' ? 'bidding' : 'opposing')
          }
          scores={{
            biddingTeam: (gameState as any)?.cumulativeTeamScores?.biddingTeam ?? (gameState?.currentRound?.scoring?.biddingTeam?.pointsWon || 0),
            opposingTeam: (gameState as any)?.cumulativeTeamScores?.opposingTeam ?? (gameState?.currentRound?.scoring?.opposingTeam?.pointsWon || 0)
          }}
          roomScores={roomScores as unknown as Record<string, any>}
          players={players.map(p => {
            // Determine player's team from game state
            let playerTeam: 'bidding' | 'opposing' | undefined = undefined;
            if (gameState?.currentRound?.scoring) {
              const bidder = gameState.currentRound.scoring.biddingTeam?.bidder;
              const partners = gameState.currentRound.scoring.biddingTeam?.partners || [];
              const biddingTeamPlayers = bidder ? [bidder, ...partners] : partners;
              const opposingTeamPlayers = gameState.currentRound.scoring.opposingTeam?.players || [];
              if (biddingTeamPlayers.includes(p.id)) {
                playerTeam = 'bidding';
              } else if (opposingTeamPlayers.includes(p.id)) {
                playerTeam = 'opposing';
              }
            }
            return {
              id: p.id,
              username: p.name || 'Unknown',
              team: playerTeam,
              isReady: p.isReady || false,
              isAI: p.isAI || false
            };
          })}
          currentPlayerId={currentPlayer?.id || null}
          bidAmount={lastBidAmount}
          bidderId={lastBidderId}
          onToggleReady={() => {
            const newReadyState = !currentPlayer?.isReady;
            setReady(newReadyState);
            if (!newReadyState && onNavigateToLobby) {
              dbg('multiplayer', '🏠 Player not ready - navigating to lobby');
              onNavigateToLobby();
            }
          }}
          onStartNewGame={() => {
            dbg('multiplayer', '🚀 Starting new game directly from victory screen...');
            restartGame();
          }}
          onReturnToLobby={onReturnToLobby}
        />
      ) : null}

      {/* Enhanced Game Interface - Score Display and Game Log */}
      <GameInterfaceEnhancement
        roomId={frontendRoomId}
        frontendRoomId={frontendRoomId}
        roomInfo={roomInfo ? { roomId: roomInfo.roomId } : undefined}
        maxPlayers={players.length || 4}
        isVisible={true}
        position="bottom-right"
      />

      {/* Add spinning animation and pulse effect */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.05);
            opacity: 0.8;
          }
        }
        
        @keyframes partnerRevealPulse {
          0% { 
            box-shadow: 0 2px 8px rgba(79, 70, 229, 0.3), 0 0 0 1px rgba(79, 70, 229, 0.1) inset;
          }
          100% { 
            box-shadow: 0 4px 16px rgba(79, 70, 229, 0.5), 0 0 0 2px rgba(79, 70, 229, 0.2) inset;
          }
        }
        
        @keyframes partnerNameFadeIn {
          0% { 
            opacity: 0;
            transform: translateY(-10px) scale(0.8);
          }
          100% { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
        
        @keyframes connectionPulse {
          0%, 100% { 
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 8px rgba(79, 70, 229, 0.4), 0 0 0 2px rgba(79, 70, 229, 0.1);
          }
          50% { 
            opacity: 0.7;
            transform: scale(1.2);
            box-shadow: 0 0 12px rgba(79, 70, 229, 0.6), 0 0 0 3px rgba(79, 70, 229, 0.2);
          }
        }
        
        @keyframes goldShimmer {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), 0 4px 16px rgba(255, 215, 0, 0.3);
          }
          50% { 
            box-shadow: 0 0 30px rgba(255, 215, 0, 0.6), 0 4px 20px rgba(255, 215, 0, 0.4);
            transform: scale(1.05);
          }
        }
        
        @keyframes silverGlow {
          0%, 100% { 
            box-shadow: 0 0 15px rgba(192, 192, 192, 0.3), 0 4px 16px rgba(192, 192, 192, 0.25);
          }
          50% { 
            box-shadow: 0 0 25px rgba(192, 192, 192, 0.5), 0 4px 18px rgba(192, 192, 192, 0.35);
          }
        }
        
        @keyframes bronzeWarm {
          0%, 100% { 
            box-shadow: 0 0 15px rgba(205, 127, 50, 0.3), 0 4px 16px rgba(205, 127, 50, 0.25);
          }
          50% { 
            box-shadow: 0 0 25px rgba(205, 127, 50, 0.5), 0 4px 18px rgba(205, 127, 50, 0.35);
          }
        }
        
        @keyframes youIndicatorGlow {
          0%, 100% { 
            box-shadow: 0 0 8px rgba(251, 191, 36, 0.3);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 12px rgba(251, 191, 36, 0.5);
            transform: scale(1.02);
          }
        }
        
        @keyframes yourTurnPulse {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1);
            transform: scale(1);
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.15));
          }
          50% { 
            box-shadow: 0 0 30px rgba(251, 191, 36, 0.4), 0 6px 16px rgba(0, 0, 0, 0.15);
            transform: scale(1.02);
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.25), rgba(245, 158, 11, 0.25));
          }
        }
        
        @keyframes enhancedCardEntry {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.8) rotateY(-15deg);
            filter: blur(4px);
          }
          60% {
            opacity: 0.8;
            transform: translateY(-5px) scale(1.05) rotateY(2deg);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1) rotateY(0deg);
            filter: blur(0px);
          }
        }
        
        @keyframes trickWinnerCelebration {
          0%, 100% { 
            transform: scale(1.1) rotateZ(0deg);
            filter: drop-shadow(0 8px 20px rgba(251, 191, 36, 0.6)) brightness(1.2);
          }
          25% { 
            transform: scale(1.15) rotateZ(2deg);
            filter: drop-shadow(0 12px 25px rgba(251, 191, 36, 0.8)) brightness(1.3) saturate(1.2);
          }
          50% { 
            transform: scale(1.2) rotateZ(-1deg);
            filter: drop-shadow(0 15px 30px rgba(251, 191, 36, 1.0)) brightness(1.4) saturate(1.3);
          }
          75% { 
            transform: scale(1.15) rotateZ(1deg);
            filter: drop-shadow(0 12px 25px rgba(251, 191, 36, 0.8)) brightness(1.3) saturate(1.2);
          }
        }
        
        @keyframes winnerNameGlow {
          0%, 100% { 
            box-shadow: 0 4px 12px rgba(251, 191, 36, 0.6), 0 0 20px rgba(255, 215, 0, 0.4);
          }
          50% { 
            box-shadow: 0 6px 20px rgba(251, 191, 36, 0.8), 0 0 30px rgba(255, 215, 0, 0.6);
            transform: scale(1.05);
          }
        }
        
        @keyframes trumpPulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(255, 215, 0, 0.3), 0 6px 16px rgba(0, 0, 0, 0.15);
          }
          50% {
            box-shadow: 0 0 30px rgba(255, 215, 0, 0.5), 0 8px 20px rgba(0, 0, 0, 0.2);
          }
        }
        
        @keyframes trumpSuitGlow {
          0% {
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
            text-shadow: 0 3px 10px rgba(0,0,0,0.6), 0 0 20px rgba(255,215,0,0.3);
          }
          100% {
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.4));
            text-shadow: 0 4px 12px rgba(0,0,0,0.7), 0 0 30px rgba(255,215,0,0.5);
          }
        }
        
        @keyframes biddingPhasePulse {
          0%, 100% {
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 0 15px rgba(255, 215, 0, 0.4);
            transform: scale(1);
          }
          50% {
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 0 25px rgba(255, 215, 0, 0.6);
            transform: scale(1.02);
          }
        }
        
        @keyframes iconRotate {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(-5deg); }
          75% { transform: rotate(5deg); }
          100% { transform: rotate(0deg); }
        }
        
        @keyframes yourTurnGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(79, 70, 229, 0.4), 0 4px 16px rgba(79, 70, 229, 0.3);
            border-color: rgba(79, 70, 229, 0.6);
          }
          50% {
            box-shadow: 0 0 30px rgba(79, 70, 229, 0.6), 0 6px 20px rgba(79, 70, 229, 0.4);
            border-color: rgba(79, 70, 229, 0.8);
          }
        }
        
        @keyframes urgencyPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 2px 8px rgba(251, 191, 36, 0.4);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(251, 191, 36, 0.6);
          }
        }
        
        @keyframes youBadgeBounce {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-2px);
          }
        }
        
        @keyframes progressGlow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(79, 70, 229, 0.5);
          }
          50% {
            box-shadow: 0 0 15px rgba(79, 70, 229, 0.8);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: translateX(-50%) scale(1);
          }
          50% {
            opacity: 0.7;
            transform: translateX(-50%) scale(1.2);
          }
        }
        
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        /* Smooth transitions for all interactive elements */
        button {
          transition: all 0.2s ease !important;
        }
        
        /* Enhanced hover effects for cards */
        .playing-card:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 8px 25px rgba(0,0,0,0.3) !important;
        }
      `}</style>


      
      </div>
    </ErrorBoundary>
  );

  // Mobile Redesigned Game Interface - Complete Overhaul
  if (isMobileDevice && isConnected) {
    
    // Removed unused getCircularPosition function

    return (
      <ErrorBoundary onError={(error, errorInfo) => {
        console.error('🚨 Game component error:', error, errorInfo);
        addNotification('A game error occurred. The interface will try to recover.', 'error', 8000);
      }}>
        {/* Mobile optimized game table with larger center panel */}
        <div className="mobile-game-table">
          
          {/* TOP RIGHT Corner - Action Buttons (fixed to viewport, always visible) */}
          <div style={{
            position: 'fixed',
            top: '12px',
            right: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4rem',
            zIndex: 1000
          }}>
            {/* Ready button - only during waiting */}
            {(players.length < (gameState?.maxPlayers || 4) || !gameState?.currentRound?.biddingPhase) && (
              <button 
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2))',
                  border: '2px solid rgba(34, 197, 94, 0.5)',
                  backdropFilter: 'blur(12px)',
                  color: '#22c55e',
                  padding: '0.5rem 0.7rem',
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => setReady(true)}
              >
                <span>✅</span>
                <span>Ready</span>
              </button>
            )}
            
            {/* Fill Room button - only during waiting */}
            {(players.length < (gameState?.maxPlayers || 4) || !gameState?.currentRound?.biddingPhase) && (
              <button 
                style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(59, 130, 246, 0.2))',
                  border: '2px solid rgba(59, 130, 246, 0.5)',
                  backdropFilter: 'blur(12px)',
                  color: '#3b82f6',
                  padding: '0.5rem 0.7rem',
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => {
                  const aiSlots = (gameState?.maxPlayers || 4) - players.length;
                  if (aiSlots > 0) {
                    for (let i = 0; i < aiSlots; i++) {
                      setTimeout(() => requestAIPlayers(), i * 100);
                    }
                  }
                }}
                disabled={players.length >= (gameState?.maxPlayers || 4)}
              >
                <span>🤖</span>
                <span>Fill</span>
              </button>
            )}
            
            {/* Leave button - ALWAYS VISIBLE */}
            <button 
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(239, 68, 68, 0.2))',
                border: '2px solid rgba(239, 68, 68, 0.5)',
                backdropFilter: 'blur(12px)',
                color: '#ef4444',
                padding: '0.5rem 0.7rem',
                borderRadius: '10px',
                fontSize: '0.7rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap'
              }}
              onClick={() => window.history.back()}
            >
              <span>🚪</span>
              <span>Leave</span>
            </button>
          </div>

          {/* TOP LEFT CORNER - Bid, Trump, Partner Cards (fixed to viewport) */}
          {gameState?.currentRound?.biddingPhase?.currentBid && (
            <div style={{
              position: 'fixed',
              top: '12px',
              left: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              zIndex: 1000
            }}>
              {/* Bid & Trump */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(251, 191, 36, 0.2))',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(251, 191, 36, 0.6)',
                borderRadius: '10px',
                padding: '0.5rem 0.7rem',
                color: '#fbbf24',
                fontSize: '0.75rem',
                fontWeight: '700',
                boxShadow: '0 4px 16px rgba(251, 191, 36, 0.4)'
              }}>
                <div>💰 Bid: {gameState.currentRound.biddingPhase.currentBid.amount}</div>
                {gameState.currentRound.biddingPhase.currentBid.powerHouse && (
                  <div style={{ marginTop: '0.3rem' }}>
                    Trump: {convertSuitToDisplay(gameState.currentRound.biddingPhase.currentBid.powerHouse)}
                  </div>
                )}
              </div>

              {/* Partner Cards (multiple) */}
              {gameState.currentRound.biddingPhase.currentBid.partnerCards && 
               gameState.currentRound.biddingPhase.currentBid.partnerCards.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.3), rgba(147, 51, 234, 0.2))',
                  backdropFilter: 'blur(12px)',
                  border: '2px solid rgba(147, 51, 234, 0.6)',
                  borderRadius: '10px',
                  padding: '0.5rem 0.7rem',
                  color: '#9333ea',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4)'
                }}>
                  <div style={{ marginBottom: '0.3rem' }}>
                    🤝 Partner Card{gameState.currentRound.biddingPhase.currentBid.partnerCards.length > 1 ? 's' : ''}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    {gameState.currentRound.biddingPhase.currentBid.partnerCards.map((card: any, idx: number) => (
                      <div key={`${card.suit}-${card.rank}-${idx}`} style={{ fontSize: '0.9rem' }}>
                        {card.rank}{convertSuitToDisplay(card.suit)}
                        {card.desiredPlayOrder && (
                          <span style={{ fontSize: '0.7rem', marginLeft: '0.3rem', opacity: 0.8 }}>
                            ({card.desiredPlayOrder === 1 ? '1st' : '2nd'})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* BOTTOM LEFT CORNER - Team Scores (fixed to viewport) */}
          {gameState?.currentRound?.scoring && (
            <div style={{
              position: 'fixed',
              bottom: '100px',
              left: '80px', // More margin from left edge to account for UI elements
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem',
              zIndex: 500,
              maxWidth: '150px', // Constrain width to prevent overflow
            }}>
              {/* Bidding Team */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2))',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(34, 197, 94, 0.6)',
                borderRadius: '8px',
                padding: '0.4rem 0.6rem',
                color: '#22c55e',
                fontSize: '0.7rem',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)',
                whiteSpace: 'nowrap', // Prevent text wrapping
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Bidding: {gameState.currentRound.scoring.biddingTeam?.pointsWon || 0}
              </div>
              
              {/* Opposing Team */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(239, 68, 68, 0.2))',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(239, 68, 68, 0.6)',
                borderRadius: '8px',
                padding: '0.4rem 0.6rem',
                color: '#ef4444',
                fontSize: '0.7rem',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                whiteSpace: 'nowrap', // Prevent text wrapping
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Opposing: {gameState.currentRound.scoring.opposingTeam?.pointsWon || 0}
              </div>
            </div>
          )}

          {/* CENTER RIGHT - Current Player Stats (fixed to viewport) */}
          {currentPlayer && (
            <div style={{
              position: 'fixed',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              zIndex: 1000
            }}>
              {(() => {
                const hasRoomScores = roomScores?.scores && roomScores.scores.length > 0;
                const playerScoreEntry = hasRoomScores 
                  ? roomScores.scores.find((s: any) => 
                      s.playerId === currentPlayer.id || 
                      s.playerName === currentPlayer.name ||
                      s.userId === currentPlayer.id
                    )
                  : null;
                
                const sortedScores = hasRoomScores 
                  ? [...roomScores.scores].sort((a: any, b: any) => b.cumulativeScore - a.cumulativeScore)
                  : [];
                
                let playerRanking = 1;
                let roomCumulativeScore = 0;
                
                if (hasRoomScores && playerScoreEntry) {
                  roomCumulativeScore = playerScoreEntry.cumulativeScore || 0;
                  playerRanking = sortedScores.findIndex((s: any) => 
                    s.playerId === playerScoreEntry.playerId || 
                    s.playerName === playerScoreEntry.playerName
                  ) + 1;
                }
                
                const rankBadge = playerRanking === 1 ? '🥇' 
                  : playerRanking === 2 ? '🥈' 
                  : playerRanking === 3 ? '🥉' 
                  : `${playerRanking}.`;
                
                const currentGameScore = gameState?.currentRound?.scoring?.individualScores?.[currentPlayer.id] || 0;
                const hasCurrentGameScore = gameState?.currentRound?.scoring?.individualScores?.[currentPlayer.id] !== undefined;
                
                return (
                  <>
                    {/* Rank */}
                    <div style={{
                      background: playerRanking === 1 ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 193, 7, 0.3))' :
                                  playerRanking === 2 ? 'linear-gradient(135deg, rgba(192, 192, 192, 0.3), rgba(169, 169, 169, 0.3))' :
                                  playerRanking === 3 ? 'linear-gradient(135deg, rgba(205, 127, 50, 0.3), rgba(184, 115, 51, 0.3))' :
                                  'linear-gradient(135deg, rgba(107, 114, 128, 0.3), rgba(75, 85, 99, 0.3))',
                      backdropFilter: 'blur(12px)',
                      border: `2px solid ${playerRanking === 1 ? 'rgba(255, 215, 0, 0.6)' :
                                          playerRanking === 2 ? 'rgba(192, 192, 192, 0.6)' :
                                          playerRanking === 3 ? 'rgba(205, 127, 50, 0.6)' :
                                          'rgba(107, 114, 128, 0.5)'}`,
                      borderRadius: '10px',
                      padding: '0.5rem 0.7rem',
                      color: playerRanking === 1 ? '#ffd700' :
                             playerRanking === 2 ? '#c0c0c0' :
                             playerRanking === 3 ? '#cd7f32' :
                             '#9ca3af',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      boxShadow: playerRanking <= 3 ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}>
                      Rank: {rankBadge}
                    </div>
                    
                    {/* Current Game Score */}
                    {hasCurrentGameScore && (
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2))',
                        backdropFilter: 'blur(12px)',
                        border: '2px solid rgba(34, 197, 94, 0.6)',
                        borderRadius: '10px',
                        padding: '0.5rem 0.7rem',
                        color: '#22c55e',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)'
                      }}>
                        Game: +{currentGameScore}
                      </div>
                    )}
                    
                    {/* Room Cumulative Score */}
                    {hasRoomScores && roomCumulativeScore > 0 && (
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(59, 130, 246, 0.2))',
                        backdropFilter: 'blur(12px)',
                        border: '2px solid rgba(59, 130, 246, 0.6)',
                        borderRadius: '10px',
                        padding: '0.5rem 0.7rem',
                        color: '#3b82f6',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                      }}>
                        Room: 🏆 {roomCumulativeScore}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          
          {/* Game Table - Dynamic dimensions based on player count */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${getTableDimensions(players.length).width}%`,
            height: `${getTableDimensions(players.length).height}%`,
            background: 'radial-gradient(ellipse at center, #0d4f3c 0%, #064032 70%, #053428 100%)',
            borderRadius: '50%',
            border: '6px solid #d4af37',
            boxShadow: 'inset 0 0 50px rgba(0,0,0,0.4), 0 0 35px rgba(212,175,55,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Center Area - Minimized to show only essential info */}
            <div style={{
              color: 'white',
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3))',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              padding: '0.4rem',
              borderRadius: '20px',
              border: '2px solid rgba(212, 175, 55, 0.4)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(212, 175, 55, 0.1) inset',
              width: '40%',
              height: '60%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              gap: '0.4rem'
            }}>

              {/* Render mobile-optimized content based on game state */}
              {(players.length < (gameState?.maxPlayers || 4) || !gameState?.currentRound?.biddingPhase) ? (
                // Waiting for players - Minimal display
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.5rem',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  padding: '0.5rem'
                }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#ffd700' }}>
                    Waiting...
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#e5e7eb' }}>
                    {players.length}/{gameState?.maxPlayers || 4}
                  </div>
                </div>
              ) : (
                // Active game content - MINIMIZED: Only trick cards and bidding buttons
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.4rem',
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0.3rem'
                }}>
                  {/* Dynamic content based on phase */}
                  {currentPhase === 'bidding' && (
                    <div style={{ textAlign: 'center', color: '#e5e7eb', fontSize: '0.75rem', width: '100%' }}>
                      {gameState?.currentRound?.biddingPhase?.currentBidder === currentPlayer?.id ? (
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                          <button 
                            style={{
                              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2))',
                              border: '2px solid rgba(34, 197, 94, 0.5)',
                              color: '#22c55e',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                            }}
                            onClick={() => setShowBidding(true)}
                          >
                            🎯 Bid
                          </button>
                          <button 
                            style={{
                              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(239, 68, 68, 0.2))',
                              border: '2px solid rgba(239, 68, 68, 0.5)',
                              color: '#ef4444',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                            }}
                            onClick={passMove}
                          >
                            ❌ Pass
                          </button>
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                          Waiting...
                        </div>
                      )}
                    </div>
                  )}

                  {currentPhase === 'playing' && (
                    <div style={{ textAlign: 'center', color: '#e5e7eb', width: '100%', fontSize: '0.75rem' }}>
                      {/* Current Trick Cards - Compact display */}
                      {gameState?.currentRound?.playPhase?.currentTrick?.cards && 
                       gameState.currentRound.playPhase.currentTrick.cards.length > 0 && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.3rem', 
                          justifyContent: 'center', 
                          flexWrap: 'wrap' 
                        }}>
                          {gameState.currentRound.playPhase.currentTrick.cards.map((playedCard: any, cardIndex: number) => {
                            const isWinning = gameState?.currentRound?.playPhase?.currentTrick?.winningCard?.id === playedCard.card.id;
                            const suitColor = getSuitColor(playedCard.card.suit);
                            return (
                              <div 
                                key={`trick-${cardIndex}`} 
                                style={{
                                  padding: '0.3rem 0.4rem',
                                  background: isWinning ? 
                                    'linear-gradient(135deg, rgba(144, 238, 144, 0.95), rgba(152, 251, 152, 0.95))' : 
                                    'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(240, 240, 240, 0.95))',
                                  border: isWinning ? 
                                    '2px solid rgba(34, 197, 94, 0.9)' : 
                                    '1px solid rgba(0, 0, 0, 0.25)',
                                  borderRadius: '6px',
                                  fontSize: '0.7rem',
                                  fontWeight: '700',
                                  color: suitColor,
                                  boxShadow: isWinning ?
                                    '0 0 15px rgba(34, 197, 94, 0.5)' :
                                    '0 2px 6px rgba(0, 0, 0, 0.2)',
                                  minWidth: '2rem',
                                  textAlign: 'center',
                                  position: 'relative'
                                }}
                              >
                                {isWinning && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '-0.4rem',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: '0.7rem'
                                  }}>
                                    👑
                                  </div>
                                )}
                                <div>{playedCard.card.rank}</div>
                                <div style={{ fontSize: '0.85rem' }}>{convertSuitToDisplay(playedCard.card.suit)}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {currentPhase === 'round_end' && (
                    <button 
                      style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(59, 130, 246, 0.2))',
                        border: '1px solid rgba(59, 130, 246, 0.5)',
                        color: '#3b82f6',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                      onClick={() => restartGame()}
                    >
                      🔄 Next Round
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Players positioned around the table */}
            {/* Player boxes positioned TOUCHING the outer edge of table */}
            {players.map((player, index) => {
              console.log(`🔵 SECTION 2 - Rendering player: ${player.name} (index: ${index})`);
              const totalPlayers = players.length;
              
              // Reorder so current player is always at bottom (index 0)
              const currentPlayerIndex = players.findIndex(p => p.id === currentPlayer?.id);
              let displayIndex = index - currentPlayerIndex;
              if (displayIndex < 0) displayIndex += totalPlayers;
              
              console.log(`🔵 SECTION 2 - Player ${player.name}: displayIndex=${displayIndex}, currentPlayerIndex=${currentPlayerIndex}`);
              
              // SKIP RENDERING CURRENT PLAYER - they are shown at bottom with their hand, not as a player box
              if (displayIndex === 0 || player.id === currentPlayer?.id) {
                console.log(`🚫 SECTION 2 - Skipping render for current player: ${player.name} (displayIndex: ${displayIndex})`);
                return null;
              }
              
              console.log(`✅ SECTION 2 - Rendering player ${player.name} at displayIndex ${displayIndex}`);
              
              // Use same elliptical positioning logic as desktop for consistency
              const angleStep = 360 / totalPlayers;
              const angle = 90 + (displayIndex * angleStep); // Start from bottom (90°), go clockwise
              const angleRad = (angle * Math.PI) / 180;
              
              // Compute radii from actual table size so avatars sit just outside the ellipse edge
              // Match desktop radiusX/radiusY values for proper elliptical positioning
              // Slightly outside the table edge; fine-tuned for 6 players on the north arc
              let radiusX = 46;
              let radiusY = 44;

              if (totalPlayers === 4) {
                radiusX = 55;  // further out
                radiusY = 53;
              } else if (totalPlayers === 5) {
                radiusX = 55;
                radiusY = 53;
              } else if (totalPlayers === 6) {
                radiusX = 56;  // a touch more for 6 players
                radiusY = 54;
              } else if (totalPlayers === 7) {
                radiusX = 57;
                radiusY = 55;
              } else if (totalPlayers >= 8) {
                radiusX = 58;
                radiusY = 56;
              }

              // Base position from ellipse
              let x = 50 + radiusX * Math.cos(angleRad);
              let y = 50 + radiusY * Math.sin(angleRad);

              // North-arc correction: nudge upward slightly (toward ellipse edge)
              const normalizedAngle = ((angle % 360) + 360) % 360; // 0..359
              if (normalizedAngle > 220 && normalizedAngle < 320) {
                y -= 1.0;
              }
              
              const isCurrentTurn = (currentPhase === 'bidding' && gameState?.currentRound?.biddingPhase.currentBidder === player.id) ||
                                  (currentPhase === 'playing' && gameState?.currentRound?.playPhase?.currentPlayer === player.id);
              const cardCount = playerCards.length > 0 && player.id === currentPlayer?.id ? playerCards.length : 13;
              const isAI = player.isAI || player.name.startsWith('AI_');
              
              // Get player scores and ranking (same as desktop)
              const hasRoomScores = roomScores?.scores && roomScores.scores.length > 0;
              const playerScoreEntry = hasRoomScores 
                ? roomScores.scores.find((s: any) => 
                    s.playerId === player.id || 
                    s.playerName === player.name ||
                    s.userId === player.id
                  )
                : null;
              
              const sortedScores = hasRoomScores 
                ? [...roomScores.scores].sort((a: any, b: any) => b.cumulativeScore - a.cumulativeScore)
                : [];
              
              let playerRanking = 1;
              let roomCumulativeScore = 0;
              
              if (hasRoomScores) {
                if (playerScoreEntry) {
                  roomCumulativeScore = playerScoreEntry.cumulativeScore || 0;
                  playerRanking = sortedScores.findIndex((s: any) => 
                    s.playerId === playerScoreEntry.playerId || 
                    s.playerName === playerScoreEntry.playerName
                  ) + 1;
                } else {
                  const playersWithScores = sortedScores.filter((s: any) => s.cumulativeScore > 0);
                  playerRanking = playersWithScores.length === 0 ? 1 : sortedScores.length + 1;
                }
              }
              
              // const rankBadge = playerRanking === 1 ? '🥇' 
              //   : playerRanking === 2 ? '🥈' 
              //   : playerRanking === 3 ? '🥉' 
              //   : `${playerRanking}.`;
              
              const currentGameScore = gameState?.currentRound?.scoring?.individualScores?.[player.id] || 0;
              const hasCurrentGameScore = gameState?.currentRound?.scoring?.individualScores?.[player.id] !== undefined;
              
              // Check if player is bidder or partner
              const playerIsBidder = isBidderFn(player.id);
              const playerIsPartner = isPlayerPartner(player.id);
              const partnerRevealed = isPartnerRevealed();
              
              // Determine player's team from scoring data
              let playerTeam: 'bidding' | 'opposing' | undefined;
              if (gameState?.currentRound?.scoring) {
                const biddingTeam = gameState.currentRound.scoring.biddingTeam;
                const opposingTeam = gameState.currentRound.scoring.opposingTeam;
                
                console.log(`🎯 [CleanPlayerCard] Player ${player.name} (${player.id}):`, {
                  bidderId: biddingTeam?.bidder,
                  partnerId: biddingTeam?.partner,
                  opposingPlayers: opposingTeam?.players,
                  partnerRevealed
                });
                
                // Check if player is on bidding team (bidder OR partner)
                const isOnBiddingTeam = biddingTeam && (
                  player.id === biddingTeam.bidder || 
                  (biddingTeam.partner && player.id === biddingTeam.partner)
                );
                
                if (isOnBiddingTeam) {
                  playerTeam = 'bidding';
                  console.log(`  ✅ ${player.name} is on BIDDING team`);
                } else if (opposingTeam && opposingTeam.players.includes(player.id)) {
                  playerTeam = 'opposing';
                  console.log(`  ❌ ${player.name} is on OPPOSING team`);
                }
              }
              
              return (
                <div 
                  key={player.id} 
                  style={{
                    position: 'absolute',
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    zIndex: 10
                  }}
                >
                  {/* CleanPlayerCard with stats positioned around circle */}
                  <CleanPlayerCard
                    playerName={getAIPlayerDisplayName(player.name, isAI, aiEngineStatus)}
                    playerId={player.id}
                    currentGameScore={hasCurrentGameScore ? currentGameScore : undefined}
                    roomScore={hasRoomScores && roomCumulativeScore > 0 ? roomCumulativeScore : undefined}
                    rank={playerRanking}
                    team={playerTeam}
                    isBidder={playerIsBidder}
                    isPartner={playerIsPartner}
                    isPartnerRevealed={partnerRevealed}
                    isCurrentTurn={isCurrentTurn}
                  >
                    {/* Player Avatar - Larger and more visible */}
                    <div style={{
                    width: '3.5rem',
                    height: '3.5rem',
                    borderRadius: '50%',
                    background: isCurrentTurn ? 
                      'linear-gradient(135deg, rgba(34, 197, 94, 0.4), rgba(34, 197, 94, 0.3))' :
                      isAI ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.4), rgba(147, 51, 234, 0.3))' :
                      'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(59, 130, 246, 0.3))',
                    border: `3px solid ${isCurrentTurn ? '#22c55e' : isAI ? '#9333ea' : '#3b82f6'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    position: 'relative',
                    boxShadow: isCurrentTurn ? 
                      '0 0 20px rgba(34, 197, 94, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)' :
                      '0 4px 12px rgba(0, 0, 0, 0.4)'
                  }}>
                    {isAI ? '🤖' : getAIPlayerDisplayName(player.name, isAI, aiEngineStatus)[0].toUpperCase()}
                    
                    {/* Card count badge - More prominent */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-0.3rem',
                      right: '-0.3rem',
                      background: isCurrentTurn ? '#22c55e' : '#1f2937',
                      color: '#fbbf24',
                      borderRadius: '50%',
                      width: '1.4rem',
                      height: '1.4rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      border: '2px solid #374151',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)'
                    }}>
                      {cardCount}
                    </div>
                    
                    {/* Active turn indicator animation */}
                    {isCurrentTurn && (
                      <div style={{
                        position: 'absolute',
                        top: '-0.4rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '1rem',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }}>
                        ✨
                      </div>
                    )}
                  </div>
                  </CleanPlayerCard>
                </div>
              );
            })}
          </div>
          
          {/* Current Player's Hand - Fixed at bottom outside table - CENTERED */}
          {playerCards.length > 0 && (
            <div style={{
              position: 'fixed',
              bottom: '0.25rem',
              left: '0',
              right: '0',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 100,
              pointerEvents: 'none' // Allow clicks to pass through container
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.8))',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                padding: '0.4rem 0.6rem',
                borderRadius: '14px',
                border: '2px solid rgba(212, 175, 55, 0.6)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(212, 175, 55, 0.4) inset',
                maxWidth: '98vw',
                overflow: 'hidden',
                pointerEvents: 'auto' // Re-enable clicks on cards
              }}>
                <div style={{
                  display: 'flex',
                  gap: '0.25rem',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  padding: '0.15rem',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  justifyContent: 'center' // Center cards within the container
                }}>
                  {playerCards.map((card) => {
                    const playableCards = getPlayableCards();
                    const isPlayable = playableCards.some(playableCard => playableCard.id === card.id);
                    const suitColor = getSuitColor(card.suit);
                    return (
                      <div
                        key={card.id}
                        style={{
                          padding: '0.5rem 0.6rem',
                          background: isPlayable ? 
                            'linear-gradient(135deg, rgba(144, 238, 144, 0.95), rgba(152, 251, 152, 0.95))' : 
                            'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(240, 240, 240, 0.95))',
                          border: isPlayable ? 
                            '3px solid rgba(34, 197, 94, 0.9)' : 
                            '2px solid rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          color: suitColor,
                          cursor: isPlayable ? 'pointer' : 'not-allowed',
                          opacity: isPlayable ? 1 : 0.6,
                          transition: 'all 0.2s ease',
                          boxShadow: isPlayable ? 
                            '0 4px 16px rgba(34, 197, 94, 0.5), 0 0 0 2px rgba(34, 197, 94, 0.3) inset' :
                            '0 2px 10px rgba(0, 0, 0, 0.3)',
                          minWidth: '2.7rem',
                          textAlign: 'center',
                          flexShrink: 0,
                          transform: isPlayable ? 'scale(1)' : 'scale(0.95)'
                        }}
                        onClick={() => {
                          if (isPlayable && (currentPhase === 'playing' && gameState?.currentRound?.playPhase?.currentPlayer === currentPlayer?.id)) {
                            handleCardPlay(card);
                          }
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          gap: '0.1rem'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: '#1f2937', fontWeight: '700' }}>{card.rank}</span>
                          <span style={{ fontSize: '1.1rem' }}>{convertSuitToDisplay(card.suit)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* Floating Info Button - Bottom right */}
          <button
            onClick={() => setShowMobileInfo(!showMobileInfo)}
            style={{
              position: 'fixed',
              bottom: '1rem',
              right: '1rem',
              width: '3.5rem',
              height: '3.5rem',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              fontSize: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.5), 0 0 0 4px rgba(102, 126, 234, 0.2)',
              zIndex: 1000,
              transition: 'all 0.3s ease'
            }}
          >
            📊
          </button>

          {/* Mobile Info Panel - Slide from right */}
          {showMobileInfo && (
            <>
              {/* Backdrop */}
              <div 
                onClick={() => setShowMobileInfo(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(4px)',
                  zIndex: 1001,
                  animation: 'fadeIn 0.2s ease-out'
                }}
              />
              
              {/* Info Panel */}
              <div style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                width: '85%',
                maxWidth: '400px',
                background: 'linear-gradient(180deg, rgba(26, 29, 58, 0.98) 0%, rgba(15, 15, 30, 0.98) 100%)',
                backdropFilter: 'blur(20px)',
                boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.5)',
                zIndex: 1002,
                overflowY: 'auto',
                animation: 'slideInRight 0.3s ease-out',
                padding: '1.5rem'
              }}>
                {/* Close button */}
                <button
                  onClick={() => setShowMobileInfo(false)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '2px solid rgba(239, 68, 68, 0.5)',
                    borderRadius: '50%',
                    width: '2.5rem',
                    height: '2.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ef4444',
                    fontSize: '1.2rem',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>

                <h2 style={{
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  paddingRight: '3rem'
                }}>
                  📊 Game Info
                </h2>

                {/* Current Scores */}
                {gameState?.currentRound?.scoring && (
                  <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: 'rgba(102, 126, 234, 0.15)',
                    border: '2px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{ margin: '0 0 0.75rem 0', color: '#fbbf24', fontSize: '1.1rem', fontWeight: '700' }}>
                      🎯 Current Round
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        background: 'rgba(34, 197, 94, 0.15)',
                        borderRadius: '8px',
                        border: '1px solid rgba(34, 197, 94, 0.3)'
                      }}>
                        <span style={{ color: '#22c55e', fontWeight: '600' }}>Bidding Team:</span>
                        <span style={{ color: 'white', fontWeight: '700' }}>{gameState.currentRound.scoring.biddingTeam?.pointsWon || 0}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        background: 'rgba(239, 68, 68, 0.15)',
                        borderRadius: '8px',
                        border: '1px solid rgba(239, 68, 68, 0.3)'
                      }}>
                        <span style={{ color: '#ef4444', fontWeight: '600' }}>Opposing Team:</span>
                        <span style={{ color: 'white', fontWeight: '700' }}>{gameState.currentRound.scoring.opposingTeam?.pointsWon || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Player Rankings */}
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: 'rgba(147, 51, 234, 0.15)',
                  border: '2px solid rgba(147, 51, 234, 0.3)',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ margin: '0 0 0.75rem 0', color: '#a78bfa', fontSize: '1.1rem', fontWeight: '700' }}>
                    🏆 Rankings
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {players.map((player, index) => (
                      <div 
                        key={player.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          background: index === 0 ? 'rgba(251, 191, 36, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          border: `1px solid ${index === 0 ? 'rgba(251, 191, 36, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`
                        }}
                      >
                        <span style={{ 
                          fontSize: '1.2rem',
                          width: '2rem',
                          textAlign: 'center'
                        }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                        </span>
                        <span style={{ 
                          flex: 1,
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '0.95rem'
                        }}>
                          {getAIPlayerDisplayName(player.name, player.name.startsWith('AI_'), aiEngineStatus)}
                        </span>
                        <span style={{ 
                          color: '#fbbf24',
                          fontWeight: '700',
                          fontSize: '1rem'
                        }}>
                          {/* TODO: Add actual scores */}
                          0
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Trump Suit */}
                {gameState?.currentRound?.biddingPhase?.currentBid?.powerHouse && (
                  <div style={{
                    padding: '1rem',
                    background: 'rgba(251, 191, 36, 0.15)',
                    border: '2px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#fbbf24', fontWeight: '700', marginBottom: '0.5rem' }}>
                      Trump Suit
                    </div>
                    <div style={{ fontSize: '2.5rem' }}>
                      {convertSuitToDisplay(gameState.currentRound.biddingPhase.currentBid.powerHouse)}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Bidding Modal for Mobile */}
          {showBidding && isMobileDevice && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              animation: 'fadeIn 0.2s ease-out'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
                borderRadius: '16px',
                maxWidth: '420px',
                width: '95%',
                border: '2px solid rgba(59, 130, 246, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 40px rgba(59, 130, 246, 0.2)',
                maxHeight: '80%',
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}>
                {/* Fixed Header */}
                <div style={{ padding: '1rem 1rem 0 1rem' }}>
                  <h3 style={{ 
                    marginBottom: '0.75rem',
                    color: '#1e293b',
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>🎯 Make Your Bid</h3>
                </div>

                {/* Modal Body - scrollable content */}
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '0 1rem'
                }}>
                  {/* Bid Amount Selector */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#374151',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      🎯 Bid Amount (Points):
                    </label>
                    <input
                      type="number"
                      min={getMinBidAmount()}
                      max={players.length >= 7 || partnershipConfig === '6-2deck' ? 500 : 250}
                      step="5"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(parseInt(e.target.value) || getMinBidAmount())}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '16px',
                        fontWeight: '600',
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                      }}
                    />
                  </div>

                  {/* Power House Selector */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#374151',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      🏠 Power House (Trump Suit):
                    </label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '8px'
                    }}>
                      {(['spades', 'hearts', 'diamonds', 'clubs'] as const).map(suit => (
                        <button
                          key={suit}
                          onClick={() => setSelectedSuit(suit)}
                          style={{
                            padding: '12px 8px',
                            borderRadius: '8px',
                            border: selectedSuit === suit ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                            background: selectedSuit === suit ? 
                              'linear-gradient(135deg, #dbeafe, #bfdbfe)' : 
                              'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: getSuitColor(suit),
                            fontWeight: '700',
                            transition: 'all 0.2s',
                            boxShadow: selectedSuit === suit ? 
                              '0 4px 12px rgba(59, 130, 246, 0.3)' : 
                              '0 2px 4px rgba(0, 0, 0, 0.05)'
                          }}
                        >
                          {convertSuitToDisplay(suit)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Partner Cards Selection */}
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#374151',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      👥 Partner Cards (Select {getPartnerCount(players.length, partnershipConfig)}):
                    </label>
                    
                    {/* Selected Partner Cards Display */}
                    {selectedPartnerCards.length > 0 && (
                      <div style={{ 
                        marginBottom: '12px',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                        borderRadius: '8px',
                        border: '1px solid #10b981'
                      }}>
                        <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '8px', fontWeight: '600' }}>
                          Selected Partner Cards:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {selectedPartnerCards.map(card => (
                            <div key={card.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              background: 'rgba(255, 255, 255, 0.8)',
                              borderRadius: '6px',
                              border: '1px solid #10b981',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              <span style={{ color: getSuitColor(card.suit) }}>
                                {card.rank}{convertSuitToDisplay(card.suit)}
                              </span>
                              <span style={{ color: '#6b7280', fontSize: '10px' }}>
                                ({card.playOrder === 1 ? '1st' : '2nd'})
                              </span>
                              <button 
                                onClick={() => removePartnerCard(card)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#dc2626',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '0',
                                  marginLeft: '2px'
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Card Selection Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '6px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      padding: '8px',
                      background: '#f8fafc',
                      borderRadius: '8px'
                    }}>
                      {(['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'] as RankType[]).map(rank => 
                        (['spades', 'hearts', 'diamonds', 'clubs'] as SuitType[]).map(suit => {
                          const selectionCount = getCardSelectionCount(suit, rank);
                          const maxPlayers = gameState?.maxPlayers || players.length;
                          const isSingleDeck = maxPlayers <= 6;
                          const maxSelections = isSingleDeck ? 1 : 2;
                          
                          return (
                            <button
                              key={`${suit}-${rank}`}
                              onClick={() => handlePartnerCardClick({ suit, rank, id: `${suit}-${rank}` })}
                              style={{
                                padding: '6px 4px',
                                borderRadius: '6px',
                                border: selectionCount > 0 ? '2px solid #10b981' : '1px solid #d1d5db',
                                background: selectionCount > 0 ? 
                                  'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 
                                  'linear-gradient(135deg, #ffffff, #f9fafb)',
                                cursor: 'pointer',
                                fontSize: '11px',
                                color: getSuitColor(suit),
                                fontWeight: '600',
                                transition: 'all 0.2s',
                                position: 'relative',
                                opacity: selectionCount >= maxSelections ? 0.7 : 1
                              }}
                            >
                              {rank}{convertSuitToDisplay(suit)}
                              {selectionCount > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  top: '-2px',
                                  right: '-2px',
                                  background: '#10b981',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '12px',
                                  height: '12px',
                                  fontSize: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700'
                                }}>
                                  {selectionCount}
                                </div>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                {/* Fixed Footer - Action buttons */}
                <div style={{
                  padding: '1rem',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => setShowBidding(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                      color: '#374151',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMakeBid}
                    disabled={selectedPartnerCards.length !== getPartnerCount(players.length, partnershipConfig)}
                    style={{
                      flex: 2,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) ?
                        'linear-gradient(135deg, #3b82f6, #1d4ed8)' : '#9ca3af',
                      color: 'white',
                      fontWeight: '700',
                      cursor: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) ? 
                        'pointer' : 'not-allowed',
                      fontSize: '14px',
                      boxShadow: selectedPartnerCards.length === getPartnerCount(players.length, partnershipConfig) ? 
                        '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none'
                    }}
                  >
                    🎯 Place Bid
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Victory Overlay for Mobile */}
        <VictoryOverlay
          isVisible={Boolean(
            (players && players.length > 0 || roomScores) &&
            ((gameState as any)?.cumulativeTeamScores?.isGameComplete || gameState?.phase === 'game_end' || gameState?.status === 'completed')
          )}
          winnerTeam={
            ((gameState as any)?.cumulativeTeamScores?.winningTeam as 'bidding' | 'opposing') ||
            (gameState?.gameHistory?.[0]?.scoring?.roundResult === 'bid_successful' ? 'bidding' : 'opposing')
          }
          scores={{
            biddingTeam: (gameState as any)?.cumulativeTeamScores?.biddingTeam ?? (gameState?.currentRound?.scoring?.biddingTeam?.pointsWon || 0),
            opposingTeam: (gameState as any)?.cumulativeTeamScores?.opposingTeam ?? (gameState?.currentRound?.scoring?.opposingTeam?.pointsWon || 0)
          }}
          roomScores={roomScores as unknown as Record<string, any>}
          players={players && players.length > 0 ? players.map(p => {
            // Determine player's team from game state
            let playerTeam: 'bidding' | 'opposing' | undefined = undefined;
            
            if (!playerTeam && gameState?.currentRound?.scoring) {
              // Bidding team = bidder + partners
              const bidder = gameState.currentRound.scoring.biddingTeam?.bidder;
              const partners = gameState.currentRound.scoring.biddingTeam?.partners || [];
              const biddingTeamPlayers = bidder ? [bidder, ...partners] : partners;
              
              // Opposing team is explicitly listed
              const opposingTeamPlayers = gameState.currentRound.scoring.opposingTeam?.players || [];
              
              if (biddingTeamPlayers.includes(p.id)) {
                playerTeam = 'bidding';
              } else if (opposingTeamPlayers.includes(p.id)) {
                playerTeam = 'opposing';
                console.log(`✅ [Mobile VictoryOverlay] ${p.name} assigned to OPPOSING team`);
              } else {
                console.log(`❌ [Mobile VictoryOverlay] ${p.name} NOT FOUND in either team!`);
              }
            }
            
            const mappedPlayer = {
              id: p.id,
              username: p.name || 'Unknown',
              team: playerTeam,
              isReady: p.isReady || false,
              isAI: p.isAI || false
            };
            console.log(`📤 [Mobile VictoryOverlay] Returning player:`, mappedPlayer);
            return mappedPlayer;
          }) : []}
          currentPlayerId={currentPlayer?.id || null}
          bidAmount={lastBidAmount}
          bidderId={lastBidderId}
          onToggleReady={() => {
            const newReadyState = !currentPlayer?.isReady;
            setReady(newReadyState);
            
            // If player clicks "Not Ready", navigate to lobby
            if (!newReadyState && onNavigateToLobby) {
              dbg('multiplayer', '🏠 Player not ready - navigating to lobby');
              onNavigateToLobby();
            }
          }}
          onStartNewGame={() => {
            dbg('multiplayer', '🚀 Starting new game directly from victory screen...');
            // Reset all players to ready and start new game immediately
            restartGame();
          }}
          onReturnToLobby={() => {
            dbg('multiplayer', '🏠 Returning to game lobby...');
            // Use callback to return to lobby via GameRoom component
            if (onReturnToLobby) {
              onReturnToLobby();
            } else {
              // Fallback to old method if callback not provided
              disconnect();
              setTimeout(() => {
                if (frontendRoomId) {
                  connectToGame(user?.displayName || user?.username || 'Anonymous Player', frontendRoomId, false, user?.id);
                }
              }, 100);
            }
          }}
        />
      </ErrorBoundary>
    );
  }

  // Return desktop layout as default
  return desktopLayout;
};

export default MultiplayerGameComponent;
